import { carryIntoWorkout } from "../lib/reviewWrite";
import type { SetFieldWrite } from "../lib/setFieldWrite";
import { Icon } from "../components/Icon";
import { COACH_RULE_TABS, COACH_SEES_ENGINE } from "../lib/coachEngine";
import { RowRuleLine, useWorkoutEngine } from "../components/WorkoutEngine";
import { useRowSteps, type RowStepsInput } from "../components/RuleTabs";
import { VideoThumb } from "../components/VideoThumb";
import { ExerciseShelf, ShelfViewSwitch, useShelfView } from "../components/ExerciseShelf";
import { shelfMatches } from "../lib/exerciseShelf";
import { myId } from "../lib/me";
import { DiscardWindow } from "../components/DiscardWindow";
import { RemoveWorkoutWindow } from "../components/RemoveWorkoutConfirm";
import { Fragment, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { DndContext, DragOverlay, useDroppable, type DragEndEvent, type DragMoveEvent, type DragStartEvent } from "@dnd-kit/core";
import { SortableContext, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  AUTOSCROLL, DRAG_INSTRUCTIONS, DROP_MS, SLIDE_MS, SortableRun, alwaysAnimate, gapStyle, GapHeight, LayoutFrozen, useStableIds, dropAnimation, midpointCollision, releasedOnControl,
  snapshotOf, targetOf, useBuilderSensors, useGrip, useNoPositionEndings, useReducedMotion, type DropData,
} from "./builderDrag";
import { NEW_KEY, arrangementOf, dropInsert, dropMove, followPartMove, locate, moveKey, parseRunKey, withoutKey } from "../lib/dragModel";
import { supabase } from "../lib/supabase";
import { duplicateWorkout } from "../lib/duplicateWorkout";
import { duplicateDeps, existingTemplateNames, readWorkoutUsage, removeDeps } from "../lib/workoutActionDeps";
import { removeWorkout, type WorkoutUsage } from "../lib/archiveWorkout";
import { ChipGroup } from "../components/ChipGroup";
import { matchesFacets } from "../lib/exerciseTable";
import { CATEGORY_LABEL, CATEGORY_ORDER } from "../lib/coachExercise";
import { backdropRoute, href, hrefKeepingQuery, type Route } from "../lib/route";
import { UNDO_BURST_MS, pushHistory, redoStep, ruleTag, titleHeight, undoStep, usedOnLine, CLIENT_DAY_COPY_LINE, NEXT_WEEK_COPY_LINE, clientDayEyebrow, workoutCaption, type History } from "../lib/builderHead";
import { tellTheClient } from "../lib/assignPush";
import { saveStatus } from "../lib/exerciseAutosave";
import { draftTree } from "../lib/draftTree";
import { distanceChange, distanceText, distanceUnitOf, type DistanceUnit } from "../lib/distanceCell";
import { PhoneWindow } from "../components/PhoneWindow";
import { AddSetSplit } from "../components/AddSetSplit";
import { EQUIPMENT_AMBIENT } from "../lib/equipment";
import { vocabularyLabel } from "../lib/vocabulary";
import { WORKOUT_NAME_MAX, WORKOUT_NAME_REFUSAL, clampWorkoutName, workoutNameLength } from "../lib/workoutNameCap";
import { wireNumber } from "../lib/wireNumber";
import { missingRuleCount, ruleMap } from "../lib/progressionRule";
import { accepts, valueOf, timeText, type CellKind } from "../lib/typedCell";
import { accepts as tempoAccepts, typing as tempoTyping, deleting as tempoDeleting, stored as tempoStored } from "../lib/tempoEntry";
import { useTopLayerMenu } from "../components/useTopLayerMenu";
import { readPref, writePref } from "../lib/viewPrefs";
import {
  emptySet, expandUniform, columnsFor, clock, parseSeconds, setsFromRows, rowsFromSets,
  insertExerciseAt, moveExerciseTo, dropLooseAt, tidyImplicit, newImplicitSection, looseTarget, landedAt,
  validate, blocking, planSave, supersetRuns, allowsSupersets, libraryName,
  OFFERED_FORMATS, FORMAT_CAPTION, SET_TYPE_LABEL, SET_TYPE_LETTER, formatHeadline,
  type WorkoutDraft, type SectionDraft, type ExerciseDraft, type SetDraft, type SetType,
  type SectionFormat, type SectionType, type TrackingField, type Problem,
  formatFields, readFormatConfig, rehomeFormatConfig,
  moveExercise, duplicateExercise,
  moveSection,
  canMove,
  addTargetIndex,
  describeAddTarget,
  moveTargets,
  runAt,
  LIBRARY_PAGE,
  closeAttempt,
  closeOutcome, discardOutcome, exerciseCount, sameDraft, type CloseStake,
  workoutEquipment, exerciseCountWords, arrangeItemCount,
  setLabel, allowedSetTypes, SET_ORDER_RULE, rowMedia, type RowMedia, FORMAT_LABEL, sectionName, sectionReference, sentenceStart, ordinalWord
         } from "../lib/builder";

/* The workout builder.
 *
 * The rules are in `lib/builder.ts` with 87 executed tests; what lives here is the screen and the
 * writes. The measurement that shaped it is in that file's header and it is worth repeating in
 * one line, because it is the reason this looks nothing like §8b:
 *
 *   **306 of 323 real exercises — 94.7% — have every set identical**, in both the hand-made and
 *   the seed-written population independently. So the default row is ONE LINE, and the per-set
 *   table Everfit shows always is one deliberate step away.
 *
 * That is §2b's three parts at once: full functionality (the table is there), minimal surface
 * (it is not there until it is needed), and nothing whose meaning needs explaining.
 *
 * ══════════════════════════════════════════════════════════════════════════════════════════
 * 🔴 SESSION 10 — IT IS A WINDOW NOW, AND IT STILL HAS ITS ADDRESS (2026-08-30)
 *
 * The owner, on Everfit's editors: *"you see they're just windows which make the rest of the
 * background screen darker? very clean."* So the builder is a modal `<dialog>` over the library
 * it belongs to — and the half that makes that honest rather than merely pretty is that
 * `#/workouts/<id>` still MEANS the same thing. `backdropRoute` in `lib/route.ts` is where the
 * window/backdrop relationship is declared; `App.tsx` draws the backdrop screen and this window
 * together, and closing is a NAVIGATION to the backdrop's own address. A modal opened by a click
 * would have no address at all, and a shared workout link would stop opening the right thing.
 *
 * ⚠️ IT IS THE NATIVE `<dialog>` WITH `showModal()`, AND THAT IS NOT A CONVENIENCE. Focus
 * trapping, Escape, the inertness of everything behind it and the `::backdrop` pseudo-element
 * all arrive with the element. Every one of those hand-rolled is a chance to ship a control that
 * looks right and cannot be driven from a keyboard, which is exactly what `EVERFIT-MAP.md` §12
 * measured in their product — ZERO focusable elements on their primary work surface. Reproducing
 * their look by reproducing their defect would be the worst possible trade.
 *
 * ⚠️ AND ESCAPE MAY NOT DISCARD A DRAFT. `cancel` is cancelable precisely so this decision can be
 * taken; `closeAttempt` in `lib/builder.ts` takes it and is executed by `builder.test.ts`.
 *
 * ⚠️ THE BACKDROP DOES NOT CLOSE THE WINDOW, AND THAT IS A DECISION RATHER THAN AN OMISSION.
 * `<dialog>` gives no backdrop-click for free; wiring one means comparing mousedown and mouseup
 * targets, or a drag that ends outside a text field closes an editor holding unsaved work. A
 * stray click beside a form is not a decision, so the two ways out are Escape and a labelled
 * Close — both always visible, both keyboard-operable, neither ambiguous.
 * ══════════════════════════════════════════════════════════════════════════════════════════
 */

/**
 * WHAT A PRESS ON THE SAVE BAR MEANT — his word of 2026-09-19 gave the footer two verbs, and
 * `EVERFIT-MAP` §12/§18-quater read the same two on theirs: `Save` and `Save & Close`.
 *
 * ⚠️ IT IS THE INTENT, NEVER THE OUTCOME. `"close"` says the coach asked to leave once the write
 * reports; whether the window actually closes is `onSave`'s decision after the database answers,
 * and on a refusal or an error it does not. A name like `closing` would read as a state the
 * window is in and invite a second reader to believe it.
 */
type SaveIntent = "stay" | "close";

/** 🔴 W88 — where the Arrange outline's open/closed choice is remembered, per viewer. */
const OUTLINE_KEY = "builderOutline"; // W140: a `viewPrefs` key (the flat `ht.builder.outline` is its read-only fallback)

type LibraryExercise = {
  id: string; name: string; trackingFields: TrackingField[];
  /** NULL on most rows and that is a real state, not a gap — a coach recognises a movement
   *  by picture, and a row without one shows the plate tile rather than an empty box. */
  thumbnailUrl: string | null;
  demoVideoUrl: string | null;
  isHold: boolean; equipment: string[] | null;
  /* 🔴 W32(3) — READ ONLY BECAUSE THE FILTER POPOVER ASKS FOR IT. `coach_exercises.category` is
     one of five words or NULL, and NULL is an answer ("never categorised"), never a gap to fill
     in. Matched through `matchesFacets`, the one function the exercises page uses for the same
     chip, so the two screens cannot disagree about what `Strength` selects. */
  category: string | null;
  /* 🔴 W15's "DEFAULT NOTE", AND IT NEEDED NO MIGRATION — `coach_exercises.notes` HAS EXISTED
     ALL ALONG and the builder simply never read it. Everfit carry the same field under Advanced
     Settings; ours was a column with a writer (the exercise editor) and NO READER at the one place
     it is for. A cue a coach writes once on the movement is a cue they then retype into every
     workout, or more likely stop writing at all. */
  note: string | null;
  /* 🔴 F11 LESSON 27. `progression_config` decides whether a row will EVER progress, and until
     now no coach screen said a word about it. MEASURED live 2026-09-19, grouped by coach so the
     denominator is one person's library: **111 of his 142 rows carry no rule**; the other
     coach's 42 of 42 carry none. Read here, never written — `progressionRule.ts` says why a
     machine must not guess one. */
  progressionConfig: unknown;
  /** W72 step 5 — the ONE search also matches muscles (`shelfMatches`), so the pane reads them. */
  muscles: string[];
  /** W72 step 5 — `coach_exercises.hidden_at`. A hidden row is off the PANE and still resolves every row of a
   *  workout that already prescribes it — which is why the read keeps it and only the pane's list skips it. */
  hiddenAt: string | null;
};

const newSection = (): SectionDraft => ({
  name: null, format: "straight_sets", sectionType: "workout", description: null,
  // A section a coach added with "Add section" is theirs, so never implicit.
  formatConfig: {}, isImplicit: false, exercises: [],
});

/** William's item 4 (2026-10-15) — the builder opened over a CLIENT's week: whose day, and which. `day` is null only while
 *  the page behind has not yet read which day a pencil's copy is on. */
export type ClientTarget = { clientId: string; clientName: string; day: string | null };

export function WorkoutBuilder({ workoutId, route, onSaved, carry = [], client }: {
  workoutId: string | null;
  /** This window's own address — the one `backdropRoute` reads to know where closing GOES. */
  route: Route;
  /** A save landed, so the library behind this window is stale. See `App.tsx`. */
  onSaved: () => void;
  /** 2026-10-07 — opened by the review's `Edit next week`: the review's drafted Next-week numbers, written onto THEIR sets
   *  of the copy as it loads (`carryIntoWorkout`), so the window shows what the coach typed and Save writes it. Read ONCE,
   *  at the load; the draft at open stays the saved copy, so the close question sees the carried numbers as a change. */
  carry?: readonly SetFieldWrite[];
  /** William's item 4 — present on `clientWorkoutNew` / `clientWorkout`: the first Save of a new one creates the client's
   *  copy AND its day (`create_client_workout`), never a template; the head names who and when and offers `Save to library`. */
  client?: ClientTarget;
}) {
  const carryAtOpen = useRef(carry);
  const [draft, setDraft] = useState<WorkoutDraft | null>(
    workoutId === null
      /* 🔴 W32(a) — A FRESH WORKOUT HOLDS NO SECTION. His screenshot of their empty builder: a
         dashed "Drag exercises from the left to add" zone and two buttons, with no `Section 1`
         standing there already. Ours manufactured one on mount, so every new workout began with
         a container the coach had not asked for and could not tell from one they had.
         ⚠️ THE COMMENT THIS REPLACES WAS RIGHT ABOUT ITS OWN DANGER AND IS NOT BEING PATCHED
         AROUND. It said the mount-time section existed because `addTargetIndex(0, …)` answers
         null, which made `canAdd` false, which made every picker row `disabled` AND
         `draggable={false}` — a coach who could add nothing by any gesture. That regression is
         real and it is closed BELOW, at `canAdd` and `onPick`: with no section, an add lands
         LOOSE, which creates the implicit section to hold it. The mount-time section was the
         workaround; the loose path is the answer. */
      ? { id: null, name: "", description: null, sections: [] }
      : null,
  );
  /* 🔴 `| null` UNTIL THE FETCH RESOLVES — R18. `[]` meant BOTH "loaded, and the coach has
     nothing" and "not loaded yet", and the screen drew the same thing for both: a workout of rows
     each asserting it had no video, which then quietly filled in. An empty array cannot carry a
     loading state, so the state is a value. */
  const [library, setLibrary] = useState<LibraryExercise[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  /* 🔴 WHICH SAVE IS IN FLIGHT, NOT WHETHER ONE IS — and it is one state rather than two.
     The footer carries two verbs now (his word, below), so "Saving…" has to appear on the button
     that was pressed; a `saving` boolean beside a `savingWhich` would be two stores of one fact,
     which is the shape that disagrees the first time one of them is set and the other is not. */
  const [saving, setSaving] = useState<SaveIntent | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  /* W84-1 — the 29th character of the name was just refused; the one sentence shows under the head (`workoutNameCap`). */
  const [nameCapped, setNameCapped] = useState(false);
  /* (`saved`, the Save pair's "Saved." sentence, went with the pair — `savedAt` below is the head's `Saved · 03:41`.) */
  const [problems, setProblems] = useState<Problem[]>([]);
  /* ⚠️ `dirty` IS NOT `!saved`, AND THE TWO ARE ONE KEYSTROKE APART. A workout that has just
     loaded is unsaved-since-loading and has nothing at stake; `saved` is false there, so reading
     it as "has unsaved work" would ask the coach to confirm a discard of edits they never made —
     and a confirmation that fires when nothing is at stake is one people learn to click through,
     which is how the real one stops working. This is set by an EDIT and cleared by a SAVE. */
  const [dirty, setDirty] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const heading = useRef<HTMLHeadingElement>(null);
  /* W32(7a): the title FIELD is what focus lands on now — see the effect below. */
  const titleField = useRef<HTMLTextAreaElement>(null);
  const errorRef = useRef<HTMLDivElement>(null);
  const dialogRef = useRef<HTMLDialogElement>(null);
  /* WHICH SECTION A PICKED EXERCISE LANDS IN — the design question the brief flagged, and it is
     answered EXPLICITLY rather than by where you drop.
     `null` means "the one you are building", which `addTargetIndex` reads as the last. A section's
     own click or focus sets it (§6.1 deleted the button). What is stored is the coach's REQUEST
     rather than a resolved index, because a `SectionDraft` carries no id and the list changes
     under it — resolving on every render is what stops a remembered index pointing at a section
     the coach has since removed. */
  /* 🔴 THE FIRST SECTION IS THE DESTINATION UNTIL THE COACH CLAIMS ANOTHER, AND THIS LINE IS A
     REGRESSION FIX, NOT A PREFERENCE. It used to start null because "Add exercise here" was the
     one gesture that set it; §6.1 deleted that button, and with nothing claimed
     `addTargetIndex` correctly answers null for any workout with more than one section — which
     made `canAdd` false, which made every picker row `disabled` AND `draggable={false}`. A coach
     opening this window could add nothing at all, by any gesture, until they happened to click a
     section. FOUND BY DRIVING THE REAL CONTROL: my own drag test had dispatched `dragstart`
     directly onto the row, which bypasses `disabled` entirely — it proved a gesture no user could
     make. A probe that synthesises the event instead of using the affordance cannot see a dead
     affordance.
     ⚠️ IT IS NOT A GUESS, WHICH §12 FORBIDS: the destination is PRINTED above the search box on
     every render, with its ordinal, and clicking or focusing any section changes it. 0 is a
     starting state the coach can see and move, not an inference about what they meant. */
  const [wantedTarget, setWantedTarget] = useState<number | null>(0);
  /* 🔴 W96 — THE DRAG, ON @dnd-kit (docs/NEXT-WORK.md W96; `builderDrag.tsx`, `lib/dragModel.ts`). What is carried: a RUN
     (a loose exercise or a superset's whole run, from the main column or the Arrange outline), a LIBRARY row flown in,
     or a PART (by its head in the outline). `html` is the overlay's copy of it — its own rendered look. */
  const [carried, setCarried] = useState<
    | { kind: "run"; surface: "main" | "outline"; key: string; html: string; h: number }
    | { kind: "library"; id: string; html: string; h: number }
    | { kind: "part"; section: number; html: string; h: number }
    | null
  >(null);
  const carriedRef = useRef(carried);
  carriedRef.current = carried;
  const dragging = carried !== null;
  /* 🔴 W103 (his word 2026-09-30: *"when we release the dragged thing to hide again"*) — after every drop and every cancel
     the Arrange panel draws no hover outline until the pointer MOVES: the item just let go of sits under a still pointer,
     and an outline there reads as the outline that stayed. `is-hover-quiet` on the body; the next `pointermove` ends it. */
  const [hoverQuiet, setHoverQuiet] = useState(false);
  const wasDragging = useRef(false);
  useEffect(() => {
    if (dragging) { wasDragging.current = true; return; }
    if (!wasDragging.current) return;
    wasDragging.current = false;
    setHoverQuiet(true);
    const wake = () => { setHoverQuiet(false); };
    window.addEventListener("pointermove", wake, { once: true });
    return () => { window.removeEventListener("pointermove", wake); };
  }, [dragging]);
  /* THE LIVE ARRANGEMENT — each part's runs as keys, with the carried key where the gap is. Both surfaces draw from it,
     so dragging in either moves both; null at rest (drawn from the draft). It outlives the gesture by the landing
     (`DROP_MS`), so the card lands on the gap before the draft changes under it. */
  const [arrangement, setArrangement] = useState<string[][] | null>(null);
  const arrangementRef = useRef(arrangement);
  arrangementRef.current = arrangement;
  const arrangementAtPickUp = useRef<string[][] | null>(null);
  /* The parts' order while a PART is carried (the outline's heads), the same way. */
  const [partOrder, setPartOrder] = useState<number[] | null>(null);
  const partOrderRef = useRef(partOrder);
  partOrderRef.current = partOrder;
  const landing = useRef<number | null>(null);
  const [committing, setCommitting] = useState(false);
  useEffect(() => {
    if (!committing) return;
    const id = window.requestAnimationFrame(() => { setCommitting(false); });
    return () => { window.cancelAnimationFrame(id); };
  }, [committing]);
  /* 🔴 A FOLDED PART THAT RECEIVES A ROW OPENS (his 2026-09-22 report: *"when the empty section is not opened, and i try
     drag ... it won't be dragged at all!"* — the drop had landed, invisibly). The part a drop landed in, with a counter so
     the same part can open twice; both surfaces unfold it. */
  const [landedIn, setLandedIn] = useState<{ section: number; n: number } | null>(null);
  /* 🔴 W99 — A CARRIED PART IS ITS HEAD (his idea, Fable's yes, 2026-09-30). The Arrange panel's folded parts live HERE
     (one store, by index, following each part through a move — `followPartMove`); `partInFlight` folds the carried part
     from pick-up until the drop is WRITTEN (so it lands as a head, never re-opening under the overlay), and `partLanded`
     lets an open part's rows come back with a short fade where it landed. Its state before pick-up is its state after. */
  const [outlineFolded, setOutlineFolded] = useState<ReadonlySet<number>>(new Set());
  const [partInFlight, setPartInFlight] = useState<number | null>(null);
  const [partLanded, setPartLanded] = useState<{ section: number; n: number } | null>(null);
  const reducedMotion = useReducedMotion();
  const sensors = useBuilderSensors();
  /* The one polite live line for moves: the keyboard route's (⋯ Move up / down / to / out of the part) — a drag's own
     announcements are dnd-kit's (`dragAnnouncements`). */
  const [moveSay, setMoveSay] = useState("");
  /* 🔴 EVERY NO-POSITION ENDING HE NAMED CANCELS: the pointer leaving the window, blur, a scroll, a resize, a second
     pointer (`builderDrag.useNoPositionEndings`); Escape and a release outside the list are dnd-kit's and `onDragEnd`'s. */
  // The ending's reason, for the drag instrument (`tools/w80-drag/drive.mjs` reads the page's debug console).
  useNoPositionEndings(carried !== null, (why) => console.debug("[drag-ending]", why));
  /** Where the keyboard path's next pick lands (`⋯ → Add exercise below…`), or null for the claimed section's end. */
  const [addAt, setAddAt] = useState<{ section: number; position: number } | null>(null);
  /* 🔴 W80 (Fable's add) — A KEYBOARD MOVE KEEPS THE COACH'S PLACE. MEASURED 2026-09-25: after `⋯ → Move down` (or Move
     to / out) focus fell to `<body>`, because the menu item that held it unmounted with the menu. The row's move records
     where it came FROM and the sections BEFORE; once the new sections render, `landedAt` finds the row in them and focus
     goes to that row's own `⋯` — the control the coach used, now beside the row where it went. */
  const keyboardMove = useRef<{ before: SectionDraft[]; from: { section: number; index: number } } | null>(null);
  /** The phone preview window (Proposal 3's head). */
  const [phoneOpen, setPhoneOpen] = useState(false);
  /** Done was pressed and is waiting on the write — so the button says so. */
  const [closing, setClosing] = useState(false);
  /** How many assigned days were copied from this template (`root_workout_id`), for the foot's second sentence. */
  const [usedOn, setUsedOn] = useState<number | null>(null);
  /** The time of the last write — the head's `Saved · 03:41`. */
  const [savedAt, setSavedAt] = useState<Date | null>(null);
  /* THE WRITE, REVISIONED: every edit bumps `rev`; a write records the revision it carried, and `dirty` clears only if
     nothing was typed while it was in flight. (W95: the workout saves only on Save — the autosave is gone.) */
  const rev = useRef(0);
  /* ⌘Z — the workout's own undo (Proposal 3's foot). A burst of edits inside UNDO_BURST_MS is one step, so undo
     walks back whole thoughts rather than single keystrokes. Text fields keep the browser's own undo. */
  const history = useRef<History<WorkoutDraft>>({ past: [], future: [], lastAt: 0 });
  /* 🔴 W85 — UNDO · REDO, ONE STEP FUNCTION for the two buttons and the two keys (⌘Z / ⇧⌘Z). It reads the draft through a
     ref because the keys' listener is registered once, at mount, and would otherwise hold the first render's draft. */
  const draftNow = useRef<WorkoutDraft | null>(draft);
  draftNow.current = draft;
  const stepHistory = useCallback((dir: "undo" | "redo"): boolean => {
    const cur = draftNow.current;
    if (cur === null) return false;
    const r = dir === "undo" ? undoStep(history.current, cur) : redoStep(history.current, cur);
    if (r === null) return false;
    history.current = r.history;
    rev.current += 1;
    setDraft(r.draft); setDirty(true);
    return true;
  }, []);

  /* 🔴 W85 — THE ARRANGEMENT IS ALWAYS OPEN, HIS WORD 2026-09-27: *"the arrange part left doesn't need to be closing. so just
     have the arrange as title, in the middle horizontally of the arrange space … shown as title so a little bigger or bolder
     text"*. The show/hide toggle, its chevron and its state (`outlineOpen`, the DEV `?arrange=` pin) are gone; `Arrange` is
     the panel's heading. Its history — open by default (his 2026-09-18 word), never remembered, hideable (2026-09-25) —
     is in `DECISIONS.md`; this supersedes the hideable half. */
  const searchRef = useRef<HTMLInputElement>(null);
  /* ⚠️ A PICKED EXERCISE CAN LAND OFF-SCREEN, AND SILENCE IS INDISTINGUISHABLE FROM A DEAD
     BUTTON. Column 2 sticks; the section it adds to may be three screens down. Without a word
     back, clicking an exercise looks exactly like clicking a control that does nothing — which
     is the flyout-header shape this app refuses everywhere else. */
  const [lastAdded, setLastAdded] = useState<{ name: string; place: string } | null>(null);
  /* 🔴 NO FOLDED EXERCISES AT ALL — his word, 2026-09-29 (*"NO Folded exercises at all!"*), confirmed the same night: W93's
     name-tap fold is gone with W88's one-open-row before it. Every card is open, always; there is no fold state. */
  /* 🔴 W88 — THE ARRANGE OUTLINE IS BEHIND A ROUND ‹ / › BUTTON ON ITS DIVIDER (his yes, 2026-09-28), SUPERSEDING W85's
     always-open panel. It starts CLOSED and the coach's last choice is remembered PER VIEWER (like the theme and the
     picker's view) — a reading posture, never a property of the workout. A blocked or empty store reads as closed. */
  const [outlinePref, setOutlinePref] = useState<boolean>(() => {
    return readPref(OUTLINE_KEY) === "open"; // never throws: a blocked or empty store reads as closed
  });
  useEffect(() => {
    writePref(OUTLINE_KEY, outlinePref ? "open" : "closed");
  }, [outlinePref]);
  /* 🔴 STATE LIVES **HERE**, ABOVE EVERY EARLY RETURN — W32(2b)'s three did not, AND THAT SHIPPED A
     BLANK BUILDER. They were declared beside `insertSection` (gone with the Sections tab, W88), which sat after
     the `return frame(...)` guards for the loading and not-found states. React counts hooks per
     render: the loading render called 30, the ready render called 33, and the whole screen died
     with "Rendered more hooks than during the previous render."
     ⚠️ `tsc` PASSED AND 2,562 TESTS PASSED. Hook ORDER is a runtime contract that no type and no
     source ruler in this repo can see, and the only instrument that catches it is opening the
     page — which is the codex's own line about SwiftUI one language over, and I had not opened it
     before committing. The rule it leaves: a commit that adds a hook is a commit that gets
     rendered, not merely compiled. */
  /* W32(7) — the `⋯` beside the title. State here, above the early returns, for the reason the
     block above spells out: a hook below a `return frame(...)` kills the whole component. */
  const [moreOpen, setMoreOpen] = useState(false);
  const [duplicating, setDuplicating] = useState(false);
  const [duplicateSay, setDuplicateSay] = useState<string | null>(null);
  const moreRef = useRef<HTMLDivElement | null>(null);
  /* W95 — the Arrange column's own scroller (the pane inside it grows to its content), handed to the pane's edge scroll. */
  const arrangeScroll = useRef<HTMLDivElement | null>(null);
  /* W95 — Fable's drag plan step 2: the work column scrolls itself while a drag is held near its edge (the same hook as
     Arrange). Two cards fit on screen, so without it a long move cannot be aimed; Chromium's own is a ~40 px band. */
  const workScroll = useRef<HTMLDivElement | null>(null);
  /* W85 — the `⋯` list in the TOP LAYER (W79's `useTopLayerMenu`, the library rows' too): MEASURED 2026-09-27 it fits at
     1024 × 768 (152→229 inside the column's 32→693), but it is drawn inside `.builder-work`, a scroll container inside the
     window's `overflow: hidden` frame — a clip waiting for a short window. Top layer, so no ancestor can cut it. */
  const moreTrigger = useRef<HTMLButtonElement>(null);
  const moreList = useRef<HTMLDivElement>(null);
  useTopLayerMenu(moreOpen, moreTrigger, moreList);
  /* 🔴 W85 — DELETE FROM THE `⋯` (his report, 2026-09-27). The overview window's two-press flow, the same rule
     (`removeWorkout` over `removeDeps`), drawn in `RemoveWorkoutWindow`. State HERE, above every early return. */
  const [removing, setRemoving] = useState<
    | { kind: "idle" }
    | { kind: "measuring" }
    | { kind: "confirm"; usage: WorkoutUsage }
    | { kind: "working"; usage: WorkoutUsage }
    | { kind: "failed"; message: string; usage: WorkoutUsage | null }
  >({ kind: "idle" });
  /** Set once the window is on its way out (a delete, a discard, a removal): no write may land after it. */
  const leaving = useRef(false);
  /* 🔴 W85 — THE DRAFT THE WINDOW OPENED ON: what `changedSinceOpen` compares against (W95: nothing is written before Save,
     so `Discard changes` has nothing to put back — the database still holds this draft). A
     new workout opens on the empty draft; an existing one on the draft its read produced, set in the load below. */
  const atOpen = useRef<WorkoutDraft | null>(draft);
  /** Opened on `#/workouts/new`: any row this window holds, it created. (The first save rewrites the address IN PLACE —
   *  `replaceState`, no remount — so `workoutId` stays null for the window's whole life.) */
  /* William's item 4: over a client's week, a copy this window created is no longer "created here" once its first Save
     landed — it is that client's DAY now, so Discard and an emptied close leave it as saved rather than removing it. */
  const createdHere = workoutId === null && !(client !== undefined && draft !== null && draft.id !== null);
  /** A CLIENT'S COPY — a day in their week (William's item 4) or the review's `Edit next week` (2026-10-15): an assignment
   *  points at it, so the head draws no `⋯` (its Delete would archive the copy the day still points at). */
  const onClientCopy = client !== undefined || route.name === "reviewNextWeek";
  /* W80 — the keyboard move's focus return (see `keyboardMove`). HERE, above every `return frame(...)`: first written
     below them, the builder rendered NOTHING ("Rendered more hooks than during the previous render") — the trap the
     block above records, caught again by rendering and by `hooksBeforeReturn.test.ts`. */
  const draftSections = draft?.sections ?? null;
  useEffect(() => {
    const k = keyboardMove.current;
    if (k === null || draftSections === null || k.before === draftSections) return;
    keyboardMove.current = null;
    const at = landedAt(k.before, draftSections, k.from);
    if (at === null) return;
    /* W96 — THE KEYBOARD ROUTE SAYS WHERE THE ROW WENT (W28(e) kept): "Moved Back Squat to position 3 of 6 in Main". */
    const sec = draftSections[at.section];
    const row = sec?.exercises[at.index];
    if (sec !== undefined && row !== undefined) {
      setMoveSay(`Moved ${row.name} to position ${String(at.index + 1)} of ${String(sec.exercises.length)} in ${sectionReference(sec.name, at.section)}`);
    }
    window.requestAnimationFrame(() => {
      document.querySelector<HTMLButtonElement>(`#builder-exercise-${at.section}-${at.index} .builder-section-kebab`)?.focus();
    });
  }, [draftSections]);


  /* THE WINDOW OPENS ONCE AND IS ONLY EVER CLOSED BY GOING SOMEWHERE ELSE.
   *
   * ⚠️ NAVIGATION IS THE CLOSE, WHICH IS WHY NOTHING LISTENS TO THE `close` EVENT. Every exit
   * from here sets the hash, which unmounts this component, which runs the cleanup below. A
   * `close` listener that navigated as well would give the window two sources of truth about
   * whether it is open — and the cleanup's own `close()` would then fire it, navigating during
   * an unmount. One direction only: route decides, dialog follows.
   *
   * `showModal()` on an already-open dialog THROWS, so it is guarded rather than assumed.
   * Focus is moved to the heading straight afterwards: the browser's own choice would be the
   * first focusable control, which announces `Close` to a screen-reader user as the first thing
   * the window says about itself.
   *
   * 🔴 AND A CORRECTION THAT ONLY RENDERING COULD MAKE. This comment used to say that closing
   * *"returns the coach to the library row they came from, with nothing here spent on it"* —
   * `<dialog>` captures the previously-focused element at `showModal()` and restores it on
   * `close()`, so the claim was reasonable and it is FALSE HERE. Measured: open this window by
   * clicking a workout name, close it, and `document.activeElement` is `<body>`. The restore
   * does not survive the element being unmounted in the same tick the navigation causes.
   * So focus is moved DELIBERATELY instead, and by the screen that receives it — `Workouts`
   * takes its own heading when it becomes the foreground again. Leaving it on `<body>` is
   * precisely the defect `b4-2`'s ruler 6 exists to prevent, one component over.
   */
  useEffect(() => {
    const el = dialogRef.current;
    if (el !== null && !el.open) el.showModal();
    /* 🔴 W32(7a) — FOCUS LANDS ON THE TITLE FIELD, NOT ON A HEADING NOBODY CAN SEE. His word
       deleted the separate heading ("just have the workout title text which is also editable
       text so we type the title there in 1 place"), so the `h2` that carries this window's
       accessible name is visually hidden now — and focusing a hidden element would leave a
       sighted keyboard coach with no focus ring anywhere on the screen.
       Their own empty state shows exactly this: the name field focused, ready to type.
       ⚠️ The heading is still the FALLBACK, because the message states (loading, refused,
       not-found) have no title field — `frame()` renders a real visible `h2` for those. */
    fitTitle();
    (titleField.current ?? heading.current)?.focus();
    return () => { if (el !== null && el.open) el.close(); };
  }, []);

  /* The `⋯` menu's two dismissals, the same shape as the rail's filter popover — `mousedown`
     rather than `click`, because an item that re-renders on press can be detached by the time a
     `click` bubbles, and `contains()` would then close the menu under the coach's own finger. */
  useEffect(() => {
    if (!moreOpen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setMoreOpen(false); };
    const onDown = (e: MouseEvent) => {
      const w = moreRef.current;
      if (w !== null && !w.contains(e.target as Node)) setMoreOpen(false);
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onDown);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onDown);
    };
  }, [moreOpen]);


  // The library, once. `tracking_fields` is the column both surfaces read to decide which boxes
  // an exercise shows — never inferred here, never inferred on the phone.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase
        .from("coach_exercises")
        .select("id, name, tracking_fields, is_hold, equipment, thumbnail_url, demo_video_url, notes, progression_config, category, primary_muscles, hidden_at")
        // HIS library only (`lib/me.ts`) — a coach who is also a client must not pick from theirs.
        .eq("coach_id", await myId())
        /* 🔴 W32(4) — MOST RECENT FIRST, BECAUSE THAT IS WHAT THE HEADING ABOVE THE LIST SAYS.
           Theirs reads `MOST RECENT (2649)` and is ordered that way; ours read the same word over
           an ALPHABETICAL list, which would have been a false sentence on screen — worse than a
           departure, because nobody can see it is wrong.
           ⚠️ `name` IS THE SECOND KEY AND IT IS LOAD-BEARING, NOT TIDINESS. MEASURED on his own
           library: 142 rows carry just **9 distinct `updated_at` values** — they were written in
           bulk — so recency alone leaves 142 rows in nine buckets of arbitrary order, which reads
           as a list with no order at all. Alphabetical within each bucket is what makes it
           scannable while the heading stays true. */
        .order("updated_at", { ascending: false })
        .order("name");
      if (cancelled) return;
      if (error) { setLoadError(error.message); return; }
      setLibrary((data ?? []).map((e) => ({
        id: e.id, name: e.name,
        trackingFields: (e.tracking_fields ?? []) as TrackingField[],
        thumbnailUrl: e.thumbnail_url,
        demoVideoUrl: e.demo_video_url,
        isHold: e.is_hold ?? false,
        equipment: e.equipment,
        note: e.notes,
        progressionConfig: e.progression_config,
        category: e.category,
        muscles: e.primary_muscles ?? [],
        hiddenAt: e.hidden_at,
      })));
    })();
    return () => { cancelled = true; };
  }, []);

  /* 🔴 W88 — THE PICKER'S `Sections` TAB IS GONE (his yes, 2026-09-28): a part is added in place (`+ Add part`), so the
     second read that listed the coach's old sections, and inserting one as a block (W32(2)/(2b)), went with it. */

  // An existing workout, read back into the same draft shape the builder edits.
  useEffect(() => {
    if (workoutId === null) return;
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase
        .from("coach_workouts")
        .select(`id, name, description, updated_at,
          coach_workout_sections(id, name, position, format, section_type, description, format_config,
            coach_workout_exercises(id, exercise_id, position, each_side, hold_until_lost, tempo, notes,
              superset_with_next, coach_exercises(id, name, tracking_fields),
              coach_workout_sets(id, position, reps, weight_kg, time_seconds, rounds, rest_seconds, set_type, distance_m)))`)
        .eq("id", workoutId)
        .maybeSingle();
      if (cancelled) return;
      if (error) { setLoadError(error.message); return; }
      if (data === null) { setLoadError("That workout does not exist, or is not yours."); return; }
      const loaded = toDraft(data);
      const opened = toDraft(carryIntoWorkout(data, carryAtOpen.current));
      atOpen.current = loaded;
      setDraft(opened);
      if (!sameDraft(opened, loaded)) setDirty(true);
      // The head says WHEN it was last saved from the first frame (the trigger of 20260925140000 keeps it true).
      const at = (data as { updated_at?: string | null }).updated_at;
      if (typeof at === "string") setSavedAt(new Date(at));
    })();
    return () => { cancelled = true; };
  }, [workoutId]);

  const byId = useMemo(() => new Map((library ?? []).map((e) => [e.id, e])), [library]);
  /* The card's picture, looked up rather than stored — and THREE answers, not two (R18). The rule
     is `builder.rowMedia`, with a test per case, because "this row has no video" is a sentence the
     screen was making up about a library that had not arrived. */
  const mediaFor = useCallback((id: string) => rowMedia(library, id), [library]);
  /* ⚠️ ONE MAP FOR THE BADGES AND THE HEADER'S COUNT. Two derivations of "which rows have no
     rule" is how a header ends up saying 12 above thirteen badges — the property
     `progressionRule.test.ts` pins directly. An empty map while the library is in flight is
     UNKNOWN, not "everything is fine": every reader returns null on an id it has not got. */
  /* F15 step 3 (workout-based) — the switch and each row's rule, read and written on the ROOT workout. Above every early
     return (the hooks rule); null while hidden or for a workout never saved. */
  const workoutEngine = useWorkoutEngine(workoutId);

  const ruleByExerciseId = useMemo(
    () => (library === null ? new Map<string, boolean>() : ruleMap(library.map(
      (e) => ({ id: e.id, progressionConfig: e.progressionConfig })))),
    [library]);
  /* ⚠️ A NULL DRAFT MEASURES NOTHING, AND SAYS SO AS `counted: 0`. The workout has not loaded
     yet; an empty row list would make `missingRuleSentence` return null for the right reason by
     accident, and the next person to change that function would silently change this screen. */
  const ruleGap = useMemo(
    () => missingRuleCount(
      draft === null
        ? []
        : draft.sections.flatMap((s) => s.exercises.map((e) => ({ exerciseId: e.libraryExerciseId }))),
      ruleByExerciseId),
    [draft, ruleByExerciseId]);

  /* THE TITLE WRAPS AND GROWS (the brief's "a two-line title"): one line until the name needs two, never a field that
     hides the end of what the coach typed. Measured on every name change, before paint. */
  /* 🔴 W85 — NEVER A HEIGHT MEASURED WHILE THE WINDOW IS CLOSED (`titleHeight`): a new workout's first run is before
     `showModal()`, and it wrote `0px`. The window's open effect below measures again once the field is laid out. */
  const fitTitle = useCallback(() => {
    const el = titleField.current;
    if (el === null) return;
    el.style.height = "auto";
    el.style.height = titleHeight(el.scrollHeight) ?? "";
  }, []);
  useLayoutEffect(fitTitle, [draft?.name, fitTitle]);
  /* 🔴 HIS 2026-11-02 STILL — A HEIGHT MEASURED AT ANOTHER WIDTH: the field was fitted once (open, or a name change) while its
     column was narrow — the columns animate on open (`grid-template-columns 0.25s`), a window resized — so the name wrapped a
     letter a line, the field kept that height when the column widened, and the head (`align-items: center`) hung Saved /
     undo / Save halfway down an empty band. The height is a function of the WIDTH too: fit again whenever the width moves
     (only the width — the fit itself changes the height, and must not wake this). Declared above the early returns. */
  const titleShown = draft !== null;
  useEffect(() => {
    const el = titleField.current;
    if (!titleShown || el === null || typeof ResizeObserver === "undefined") return;
    let width = el.getBoundingClientRect().width;
    const watch = new ResizeObserver(() => {
      const next = el.getBoundingClientRect().width;
      if (next !== width) { width = next; fitTitle(); }
    });
    watch.observe(el);
    return () => watch.disconnect();
  }, [titleShown, fitTitle]);

  /* 🔴 W95 — NO AUTOSAVE (his word, 2026-09-29): the workout is written only by Save. So a tab closed on unsaved work is
     asked about by the browser's own leave prompt. Declared ABOVE the early returns (hooks-before-return). */
  useEffect(() => {
    if (!dirty) return;
    const warn = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);

  /* ⌘Z / Ctrl+Z — the workout's undo; ⇧⌘Z / Ctrl+Shift+Z — its redo (W85). Only when the caret is NOT in a text field (a
     field keeps the browser's own undo of what was typed in it). The same `stepHistory` the foot's two buttons press. */
  useEffect(() => {
    const el = dialogRef.current;
    if (el === null) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key.toLowerCase() !== "z" || !(e.metaKey || e.ctrlKey) || e.altKey) return;
      const t = e.target as HTMLElement | null;
      if (t !== null && (t.closest("input, textarea, select, [contenteditable='true']") !== null)) return;
      if (stepHistory(e.shiftKey ? "redo" : "undo")) e.preventDefault();
    };
    el.addEventListener("keydown", onKey);
    return () => el.removeEventListener("keydown", onKey);
  }, [stepHistory]);

  /* THE FOOT'S "Used on N assigned days" — the template's COPIES (`root_workout_id`), read once per workout. */
  useEffect(() => {
    const id = draft?.id ?? null;
    if (id === null) { setUsedOn(null); return; }
    let cancelled = false;
    void supabase.from("coach_workouts").select("id", { count: "exact", head: true })
      .eq("root_workout_id", id).not("client_id", "is", null)
      .then(({ count, error }) => { if (!cancelled) setUsedOn(error !== null ? null : (count ?? null)); });
    return () => { cancelled = true; };
  }, [draft?.id]);

  // Escape. ALWAYS prevented, then decided — letting the browser close natively as well would be
  // a second close path, and the one that skips the rule.
  /* W96 — Escape during a drag cancels the DRAG (dnd-kit's), never the window: the dialog's own `cancel` stands down. */
  const onCancel = (e: { preventDefault: () => void }) => { e.preventDefault(); if (carriedRef.current === null) requestClose(); };

  /* The window's chrome, shared by all three states. The loading and failed states are the SAME
     window as the working one — a builder that failed to load is still the thing the address
     names, and giving it a different frame would make the failure look like a different page. */
  /* ⚠️ `bodyOwnsTitle` EXISTS BECAUSE THE THREE STATES DIVERGED, NOT AS A CONVENIENCE.
     W32(1) moved the working state's heading into the middle column, where their screen puts it.
     The LOADING and FAILED states have no middle column — they are one sentence in an empty
     window — so if `frame` stopped rendering a heading outright, those two would lose the
     dialog's `aria-labelledby` target and its accessible name with it. The flag is which of the
     two places the single `#builder-window-title` lives in, never whether it exists. */
  const frame = (title: string, body: React.ReactNode, foot?: React.ReactNode,
                 bodyOwnsTitle = false, edge: React.ReactNode = null) => (
    /* ⚠️ THE WINDOW SHIFTS LEFT WHILE THE OUTLINE IS OPEN, AND RE-CENTRES WHEN IT CLOSES — which
       is what "the window re-centres" means and what MEASURING forced. The card is 15rem and
       floats OUTSIDE the window's right edge; at 1440 the window's right edge is 1392, leaving
       48 px, so the card ran to 1643 — 200 px off-screen. A card nobody can see is not a card.
       Shifting the window by half the dock's width keeps the PAIR centred, which is the honest
       reading of the ask: hidden, the window is centred; shown, the window-plus-card is. */
    /* ⚠️ THE FRAGMENT IS NOT TIDINESS: the question is a SECOND top-layer dialog beside this one,
       never inside it. Nesting would put the builder's own `overflow: hidden` frame between the
       two, and it would tie the question's lifetime to a subtree that re-renders on every
       keystroke in the workout. The indentation below is left alone so the diff that introduced
       this wrapper is four lines rather than the whole window. */
    <>
    <dialog ref={dialogRef}
            className="ht-window builder-window wb-window"
            aria-labelledby="builder-window-title" onCancel={onCancel}>
      {/* 🔴 W105 — THE ✕ ON THE WINDOW'S TOP-LEFT CORNER, THREE-QUARTERS OUTSIDE (his word 2026-09-30: *"not inside the
          container, but on the corner … or it needs to be top left better? cause the exercise list is fixed"*; Fable:
          top-LEFT), SUPERSEDING W93's top-right that rode with Arrange. The dialog's FIRST child (first in its tab order),
          beside the frame (which clips). `.ht-corner-close` is the ONE rule both builders use, and — the W105 follow-up —
          EVERY state of this window draws it: the loading and error states had their own `.ht-window-x` in the frame.
          One close per window, asking the SAME `requestClose` Escape asks (W85). */}
      <button type="button" className="ht-corner-close" aria-label="Close the builder" title="Close" onClick={requestClose}>
        <Icon name="x" size={18} stroke={2} />
      </button>
      <div className="ht-window-frame">
        {/* 🔴 W32(1), HIS EYE 2026-09-20: NO FULL-WIDTH MASTHEAD. Everfit's builder has no bar
            across the top — *"on top there's no long row in the whole width. the left part is
            it's own thing... and the middle part is its own thing too"*. The rail and the middle
            column each start at the window's top edge, and the ONLY exit is the ✕ below.
            ⚠️ THE BAR CARRIED THREE THINGS AND NONE OF THEM MAY SIMPLY VANISH: `Close` (the
            window's one named exit), the `h2` that is this dialog's `aria-labelledby` AND its
            focus target, and a padding rule reserving the dock's width. The heading MOVES into
            the middle column (where W32(7) puts it, and where their title sits); the exit becomes
            the ✕; the padding rule is replaced by a geometry the ruler measures as rectangles. */}
        {bodyOwnsTitle ? null : (
          <div className="builder-work-head builder-message-head">
            <p className="page-eyebrow">Library</p>
            <h2 className="ht-title builder-window-title" id="builder-window-title"
                tabIndex={-1} ref={heading}>{title}</h2>
          </div>
        )}
        {body}
        {foot}
      </div>
      {edge}
    </dialog>
    {/* 🔴 HIS WORD — the unsaved-changes question is a CENTRED modal over a dimmed page, not a
        callout in the footer nobody looks at. `requestClose` and not a bare `setConfirming(false)`
        so `closeAttempt` in `lib/builder.ts` stays the ONE rule every close gesture asks, and
        `leave` is still reached only from the control labelled `Discard changes`. */}
    <DiscardWindow open={confirming} onCancel={requestClose} onDiscard={() => { void discardChanges(); }}
                   keep={{
                     title: "Save your changes?",
                     label: "Save",
                     sentence: createdHere
                       ? "This new workout has not been saved yet. Discard leaves without it."
                       : "Your changes have not been saved yet. Discard leaves the workout as it was saved.",
                     onKeep: () => { void keepAndLeave(); },
                     busy: saving !== null,
                   }} />
    <RemoveWorkoutWindow open={removing.kind !== "idle"} name={draft?.name ?? ""}
                         usage={removing.kind === "confirm" || removing.kind === "working" ? removing.usage
                           : removing.kind === "failed" ? removing.usage : null}
                         working={removing.kind === "working"}
                         error={removing.kind === "failed" ? removing.message : null}
                         onCancel={() => setRemoving({ kind: "idle" })}
                         onConfirm={() => {
                           const u = removing.kind === "confirm" || removing.kind === "failed" ? removing.usage : null;
                           if (u !== null) void onRemoveConfirmed(u);
                         }} />
    {/* THE PHONE PREVIEW — W66's own component, drawing the LIVE draft (`draftTree`; W95: nothing is saved before Save, so
        the saved row may be older than the screen). A second top-layer dialog beside this one, never inside it. */}
    <PhoneWindow open={phoneOpen && draft !== null} tree={phoneOpen && draft !== null ? draftTree(draft) : null}
                 onClose={() => setPhoneOpen(false)} />
    </>
  );

  if (loadError !== null) {
    return frame("Workout", (
      <div className="builder-window-body builder-window-message">
        <p className="ht-callout builder-error" role="alert">{loadError}</p>
      </div>
    ));
  }

  if (draft === null) {
    return frame("Workout", (
      <div className="builder-window-body builder-window-message">
        <p className="ht-callout" aria-live="polite">Loading…</p>
      </div>
    ));
  }

  // Every edit in the builder goes through here, which is what makes `dirty` a fact about the
  // draft rather than a flag somebody has to remember to set at each call site.
  const update = (next: WorkoutDraft) => {
    history.current = pushHistory(history.current, draft, Date.now(), UNDO_BURST_MS);
    rev.current += 1;
    setDraft(next); setDirty(true);
  };

  const setSection = (si: number, s: SectionDraft) =>
    update({ ...draft, sections: draft.sections.map((x, i) => (i === si ? s : x)) });

  /* ── Leaving ───────────────────────────────────────────────────────────────────────────
   *
   * ONE function for every way out, because the ways out are where a window loses work. Escape,
   * the Close button and (were it ever added) a backdrop click are the same question asked by
   * three gestures, and three copies of the answer would differ the first time one of them
   * grew a case. `closeAttempt` is the rule and it is executed by `builder.test.ts`.
   */
  function leave() {
    // The address to leave TO comes from the grammar, never from a literal here — the same
    // reason `href()` is the only place a link is built.
    const back = backdropRoute(route);
    // William's item 4: back to the client page on ITS query (tab, week), so the week behind does not move.
    window.location.hash = client !== undefined && back !== null
      ? hrefKeepingQuery(back, window.location.hash)
      : href(back ?? { name: "workouts" });
  }

  /** His rule's three facts about the draft on screen (W85) — null while nothing has loaded. */
  function closeStake(): CloseStake | undefined {
    if (draft === null) return undefined;
    return {
      exercises: exerciseCount(draft),
      changedSinceOpen: atOpen.current === null ? false : !sameDraft(draft, atOpen.current),
      createdHere,
    };
  }

  function requestClose() {
    const stake = closeStake();
    switch (closeAttempt({ dirty, saving: saving !== null, confirming, stake })) {
      case "close":
        void closeNow(stake);
        return;
      case "confirm":
        setConfirming(true);
        // The focus move lives in an effect keyed on `confirming` rather than here — see the
        // note on that effect. Asking for it at this point cannot work, and it LOOKED like it
        // could.
        return;
      case "dismissConfirm":
        setConfirming(false);
        return;
      case "wait":
        // Nothing to do but say so — the sentence is already on screen beside the Save button,
        // and silently refusing would read as a dead control.
        return;
    }
  }

  /* 🔴 W85 — THE THREE SILENT EXITS (`closeOutcome`): nothing to save → leave (a row created here is removed); the last
     keystrokes unwritten → write, then leave; otherwise → leave. */
  async function closeNow(stake: CloseStake | undefined) {
    if (stake === undefined || draft === null) { leave(); return; }
    switch (closeOutcome(stake)) {
      case "leave": leave(); return;
      case "removeThenLeave": await removeCreatedThenLeave(); return;
    }
  }

  /** A workout this window created, removed by the ONE rule (`removeWorkout`: a real delete only while nothing points at
   *  it, measured now — otherwise an archive). A never-saved one has no row: just leave. */
  async function removeCreatedThenLeave() {
    if (draft === null || draft.id === null) { leave(); return; }
    leaving.current = true;
    const outcome = await removeWorkout(removeDeps, { id: draft.id, name: draft.name }, new Date());
    if (outcome.kind === "failed") {
      leaving.current = false;
      setConfirming(false);
      setSaveError(outcome.message);
      return;
    }
    onSaved();
    leave();
  }

  /* `Save` (the question's safe answer) — write the changes through the one write, then leave. */
  async function keepAndLeave() {
    setConfirming(false);
    if (!dirty) { leave(); return; }
    setClosing(true);
    await saveNow({ thenClose: true });
  }

  /* 🔴 `Discard changes` — W95 (his word, 2026-09-29: it said "saving" even when he discarded). Nothing is written before
     Save, so there is nothing to put back: a workout created here is removed (it has no row unless a Save refused
     mid-way wrote one), any other is left exactly as it was saved. No write, so no `Saving…`. */
  async function discardChanges() {
    const stake = closeStake();
    if (stake === undefined || saving !== null) return;
    setConfirming(false);
    if (discardOutcome(stake) === "remove") { await removeCreatedThenLeave(); return; }
    leaving.current = true;
    leave();
  }

  /* 🔴 W72 STEP 3 — ONE WRITE (W95: its callers are Save and the question's Save — the autosave is gone). The rules the Save pair carried
     are all still here — validate first, never write a workout a blocker refuses, the address rewrite for a new one —
     with two changes: the rewrite is IN PLACE (`replaceState`, no remount under the caret), and `dirty` clears only if
     nothing was edited while the write was in flight (`rev`), so the next pause writes the newer edit.
     ⚠️ A REFUSAL ON THE AUTOSAVE IS QUIET (the problems list and the head say it); the scroll to the first empty
     section — his 2026-09-21 ask — runs when the coach presses DONE, the moment they asked to leave with it.
     ⚠️ A HALF-WRITTEN TEMPLATE NEVER REACHES A CLIENT: the client reads ASSIGNED COPIES (copy-on-assign,
     `clone_coach_workout`), never this template, and the next pause writes the finished tree over the half one. */
  async function saveNow(opts: { thenClose: boolean }): Promise<string | null> {
    if (leaving.current) return null;
    const writing = draft!;
    const found = validate(draft!);
    setProblems(found);
    if (blocking(found).length > 0) {
      if (opts.thenClose) {
        setClosing(false);
        window.requestAnimationFrame(() => {
          errorRef.current?.focus({ preventScroll: true });
          const first = blocking(found).find((pr) => pr.sectionIndex !== null);
          if (first?.sectionIndex != null) scrollCardIntoView(`builder-section-${first.sectionIndex}`);
        });
      }
      return null;
    }
    const carried = rev.current;
    setSaving(opts.thenClose ? "close" : "stay"); setSaveError(null);
    const outcome = await executeSave(writing, client);
    setSaving(null);
    /* ⚠️ A ROW THE SAVE CREATED IS KEPT EVEN WHEN A LATER STEP REFUSED: without its id the next Save would create a SECOND
       workout (over a client's week, a second day's copy), where the replace path rewrites the one already there. */
    if (outcome.workoutId !== null && draft!.id === null) {
      const id = outcome.workoutId;
      setDraft((d) => (d === null ? d : { ...d, id }));
      window.history.replaceState(null, "", client !== undefined
        ? hrefKeepingQuery({ name: "clientWorkout", clientId: client.clientId, workoutId: id }, window.location.hash)
        : href({ name: "workout", workoutId: id }));
      // The client hears about a new day once, as an assign tells them (`tellTheClient`).
      if (outcome.assignmentId !== null) tellTheClient(outcome.assignmentId, 1);
    }
    if (outcome.error !== null) {
      setSaveError(outcome.error);
      setClosing(false);
      if (opts.thenClose) window.requestAnimationFrame(() => errorRef.current?.focus());
      // A copy created before the refusal is already on the client's day: the page behind reads it.
      if (outcome.workoutId !== null && client !== undefined) onSaved();
      return null;
    }
    setSavedAt(new Date());
    if (rev.current === carried) setDirty(false);
    // The library behind this window is stale until told (the window never unmounted it).
    onSaved();
    if (opts.thenClose) leave();
    return outcome.workoutId;
  }
  /* SAVE (W95, his word: "should save only when i tap done (or rename this to save?)" — renamed) — the one write. Nothing
     pending: leave. A pending change: write it, then leave. (`done` keeps its name: the press is the same act.) */
  async function done() {
    if (saving !== null) return;
    if (!dirty) { leave(); return; }
    setClosing(true);
    await saveNow({ thenClose: true });
  }

  const status = saveStatus({
    saving: saving !== null, error: saveError, dirty,
    problems: blocking(problems).map((p) => p.message), exists: draft.id !== null, savedAt,
  });

  /* 🔴 W96 — THE GESTURE, DECIDED BY THE PAGE. Pick-up records what is carried and the arrangement it started from; every
     move asks the midpoint collision where the gap belongs and moves the ONE carried key there (both surfaces redraw from
     it, the neighbours sliding); the drop lands the overlay on the gap for `DROP_MS` and only then writes, through the ONE
     mover (`moveExerciseTo`, via `dropMove`) or the one insert (`addExercise`). Every ending without a position — a
     release outside the list or on a control, Escape, and the endings `useNoPositionEndings` listens for — puts the
     arrangement back as it was picked up, the overlay flies home, and nothing is written. */
  const settle = (then: () => void) => {
    if (landing.current !== null) window.clearTimeout(landing.current);
    // `LayoutFrozen` for the render that writes: the list already stands in its final order (see `builderDrag.LayoutFrozen`).
    landing.current = window.setTimeout(() => { landing.current = null; setCommitting(true); then(); }, reducedMotion ? 0 : DROP_MS);
  };
  const goHome = () => {
    const c = carriedRef.current;
    if (c?.kind === "part") setPartOrder(draft.sections.map((_, i) => i));
    else if (arrangementAtPickUp.current !== null) setArrangement(arrangementAtPickUp.current);
    setCarried(null);
    settle(() => { setArrangement(null); setPartOrder(null); setPartInFlight(null); arrangementAtPickUp.current = null; });
  };
  const onDragStart = ({ active }: DragStartEvent) => {
    if (landing.current !== null) { window.clearTimeout(landing.current); landing.current = null; }
    const d = active.data.current as ({ kind: string } & Record<string, unknown>) | undefined;
    if (d?.kind === "run") {
      const surface = d.surface as "main" | "outline";
      const key = String(d.key);
      const scope = surface === "main" ? ".builder-work" : ".wb-arrange";
      const el = document.querySelector<HTMLElement>(`${scope} [data-run="${key}"]`);
      arrangementAtPickUp.current = arrangementOf(draft.sections);
      setArrangement(arrangementAtPickUp.current);
      setCarried({ kind: "run", surface, key, html: snapshotOf(el), h: el?.getBoundingClientRect().height ?? 0 });
    } else if (d?.kind === "library") {
      const id = String(d.id);
      const el = document.querySelector<HTMLElement>(`[data-lib="${id}"]`);
      arrangementAtPickUp.current = arrangementOf(draft.sections);
      setArrangement(arrangementAtPickUp.current);
      setCarried({ kind: "library", id, html: snapshotOf(el), h: Math.round(el?.getBoundingClientRect().height ?? 64) });
    } else if (d?.kind === "partitem") {
      const section = Number(d.section);
      const el = document.querySelector<HTMLElement>(`.wb-arrange [data-part="${String(section)}"]`);
      setPartOrder(draft.sections.map((_, i) => i));
      // W99: carried as its HEAD — the gap is the part's height without its rows (the rows fold in this same render,
      // before dnd-kit measures), and the copy that follows the pointer leaves them out.
      const rows = el?.querySelector<HTMLElement>(":scope > .builder-outline-exercises") ?? null;
      const rowsH = rows === null ? 0 : (() => {
        const cs = getComputedStyle(rows);
        return rows.getBoundingClientRect().height + parseFloat(cs.marginTop) + parseFloat(cs.marginBottom);
      })();
      setPartInFlight(section);
      setCarried({ kind: "part", section, html: snapshotOf(el, ":scope > .builder-outline-exercises"),
                   h: (el?.getBoundingClientRect().height ?? 0) - rowsH });
    }
  };
  const onDragMove = ({ collisions }: DragMoveEvent) => {
    const c = carriedRef.current;
    if (c === null) return;
    const target = targetOf(collisions);
    if (c.kind === "part") {
      const order = partOrderRef.current ?? draft.sections.map((_, i) => i);
      const next = target === null ? draft.sections.map((_, i) => i)
        : (moveKey([order.map(String)], String(c.section), 0, target.index)[0] ?? []).map(Number);
      if (next.join() !== order.join()) setPartOrder(next);
      return;
    }
    const cur = arrangementRef.current ?? arrangementOf(draft.sections);
    const key = c.kind === "run" ? c.key : NEW_KEY;
    let next: string[][];
    if (target === null || target.section === null) {
      // Over no list: the gap goes home (a library row leaves the builder), and a release now changes nothing.
      next = c.kind === "run" ? (arrangementAtPickUp.current ?? cur) : withoutKey(cur, NEW_KEY);
    } else {
      const at = locate(cur, key);
      if (at !== null && at.section === target.section && at.index === target.index) return;
      next = moveKey(cur, key, target.section, target.index);
    }
    if (JSON.stringify(next) !== JSON.stringify(cur)) setArrangement(next);
  };
  const onDragEnd = ({ collisions, activatorEvent, delta }: DragEndEvent) => {
    const c = carriedRef.current;
    if (c === null) return;
    const start = activatorEvent as PointerEvent | TouchEvent | KeyboardEvent;
    const origin = "clientX" in start ? { x: start.clientX, y: start.clientY }
      : "touches" in start && start.touches[0] !== undefined ? { x: start.touches[0].clientX, y: start.touches[0].clientY } : null;
    const target = targetOf(collisions);
    if (target === null || (origin !== null && releasedOnControl(origin.x + delta.x, origin.y + delta.y))) { goHome(); return; }
    setCarried(null);
    if (c.kind === "part") {
      const to = (partOrderRef.current ?? []).indexOf(c.section);
      settle(() => {
        setPartOrder(null);
        setPartInFlight(null);
        if (to !== -1 && to !== c.section) {
          setOutlineFolded((f) => followPartMove(f, c.section, to));
          reorderSections(c.section, to);
        }
        setPartLanded((l) => ({ section: to === -1 ? c.section : to, n: (l?.n ?? 0) + 1 }));
      });
      return;
    }
    const arr = arrangementRef.current ?? arrangementOf(draft.sections);
    const sections = draft.sections;
    const into = locate(arr, c.kind === "run" ? c.key : NEW_KEY);
    if (into !== null) setLandedIn((l) => ({ section: into.section, n: (l?.n ?? 0) + 1 }));
    settle(() => {
      setArrangement(null);
      arrangementAtPickUp.current = null;
      if (c.kind === "run") {
        const m = dropMove(sections, arr, c.key);
        if (m !== null) update({ ...draft, sections: tidyImplicit(moveExerciseTo(sections, m.from, m.to)) });
      } else if (sections.length === 0) {
        addExerciseLoose(c.id, 0);
      } else {
        const at = dropInsert(sections, arr);
        if (at !== null) addExercise(c.id, at.section, at.position);
      }
    });
  };
  /* A part carried to a new place is `moveSection`'s ±1 step repeated — never a second section mover. */
  const reorderSections = (from: number, to: number) => {
    let next = draft;
    const step: -1 | 1 = to > from ? 1 : -1;
    for (let i = from; i !== to; i += step) next = moveSection(next, i, step);
    update(next);
  };
  /* What the midpoint collision measures: the list the carried thing belongs to, and what is in it now. */
  const collisionDetection = midpointCollision(() => {
    const c = carriedRef.current;
    if (c === null) return null;
    if (c.kind === "part") {
      const order = partOrderRef.current ?? draft.sections.map((_, i) => i);
      return { mode: "parts", surface: "outline", scroller: arrangeScroll.current,
               order: () => order.map(String), idOf: (k) => `o:partitem:${k}`, self: `o:partitem:${String(c.section)}` };
    }
    const surface = c.kind === "run" ? c.surface : "main";
    const prefix = surface === "main" ? "m:" : "o:";
    const arr = arrangementRef.current ?? arrangementOf(draft.sections);
    return { mode: "runs", surface, scroller: surface === "main" ? workScroll.current : arrangeScroll.current,
             order: (s) => arr[s] ?? [], idOf: (k) => prefix + k,
             self: prefix + (c.kind === "run" ? c.key : NEW_KEY) };
  });

  const blockers = blocking(problems);
  // Resolved on every render rather than stored: the section list changes under the request.
  const target = addTargetIndex(draft.sections.length, wantedTarget);

  /* 🔴 §6 RULE (b) — ONE WRITE PATH, AND IT IS THIS FUNCTION. The pointer DRAG, a CLICK on a
     picker row and the keyboard's ENTER all call `addExercise`, which goes through
     `insertExerciseAt`; no drop handler builds a section by hand. That is the rule the brief
     states and the thing a ruler can check: a drag can never write what the other two cannot.
     `position` null means "the end of the section" — a coach who did not point at a place has
     not asked for one, which is exactly what a click and Enter mean. */
  /* ⚠️ `sections` IS A PARAMETER SO THIS STAYS THE ONE INSERT. A loose drop has to OPEN an empty
     implicit section and then add into it, and React state has not landed by the time the second
     half runs — so without this the gesture grew its own `insertExerciseAt`, which `oneWritePath`
     caught twice while this was being written. One call, one set of rules (the link cleared on the
     row it lands in front of, the position clamped, the target described), for every gesture. */
  const addExercise = (
    id: string, sectionIndex?: number | null, position?: number | null,
    sections: SectionDraft[] = draft.sections,
  ) => {
    const lib = byId.get(id);
    const into = sectionIndex ?? target;
    if (lib === undefined || into === null) return;
    const at = position ?? (sections[into]?.exercises.length ?? 0);
    update({ ...draft, sections: insertExerciseAt(sections, into, at, seededRow(lib)) });
    setLastAdded({ name: lib.name, place: describeAddTarget(sections, into) });
  };

  /* 🔴 A DROP OUTSIDE EVERY SECTION — §6.6 item 1's fifth position, and the one the builder had
     no way to express. Every slot until now was INSIDE a section, so "between an exercise and
     section" (his words) could only be approximated by picking one of the two sections next to
     the pointer — the container-first answer that lands a row where the eye did not point.
     ⚠️ IT REUSES `newImplicitSection` + `tidyImplicit` RATHER THAN INVENTING A SHAPE. The loose
     row is our §6.5 implicit section, already decided, already tested; `tidyImplicit` then merges
     it into an adjacent implicit run, so "append to the loose rows" and "open a new loose
     section" converge on one workout — which is why `dropSlot`'s own test asserts the OUTCOME
     rather than either encoding. */
  const addExerciseLoose = (id: string, boundary: number) => {
    const lib = byId.get(id);
    if (lib === undefined) return;
    const at = Math.max(0, Math.min(boundary, draft.sections.length));
    /* 🔴 THE EMPTY SECTION FIRST, THEN THE ORDINARY ADD — and the first version of this did NOT,
       which `oneWritePath` caught within a minute of being written. It spliced a section already
       holding `seededRow(lib)`, which is a SECOND insert path: `insertExerciseAt` stops being the
       only way a row enters a workout, and every rule that pass carries (clearing the link of the
       row it lands in front of, clamping the position) silently does not apply to this gesture.
       ⚠️ IT IS `dropLooseAt`'S OWN PRECEDENT, and that function says so in its comment: it creates
       the empty implicit section FIRST and then performs the ordinary move, which is what keeps
       §6.4's rule (c) true for a gesture it was not written for. Same shape, same reason.
       ⚠️ AND `tidyImplicit` IS NOT CALLED HERE: an implicit section holding one row is not empty,
       and merging with a neighbour is `addExercise`'s business on the next save — calling it now
       would delete the section between the two statements below. */
    const opened = [...draft.sections];
    opened.splice(at, 0, newImplicitSection([]));
    addExercise(id, at, 0, opened);
  };

  /* 🔴 W32(7) — DUPLICATE, FROM THE BUILDER'S `⋯`. It is the SAME `duplicateWorkout` the library
     row and the overview window call, through the deps `workoutActionDeps` now holds once.
     ⚠️ IT COPIES THE SAVED ROW, BECAUSE THAT IS THE ONLY THING `clone_coach_workout` CAN COPY —
     the RPC reads the database, and the draft on screen may be ahead of it. So a dirty draft is
     not refused and is not silently mistaken for the copy's contents: the outcome SAYS which one
     was copied. A disabled item or a "Save first" would be this window inventing a policy over a
     fact the coach can simply be told.
     ⚠️ AND IT DOES NOT NAVIGATE, WHICH IS WHERE IT DIFFERS FROM THE OVERVIEW WINDOW ON PURPOSE.
     There, a coach duplicated in order to go and change the copy, and nothing was at stake.
     Here they are mid-edit on the original; carrying them to the copy would abandon the draft
     they are standing in. It names the copy instead and leaves them where they are. */
  const onDuplicate = async () => {
    if (draft === null) return;
    setMoreOpen(false);
    setDuplicateSay(null);
    if (draft.id === null) {
      setDuplicateSay("This workout has not been saved yet, so there is nothing to copy.");
      return;
    }
    setDuplicating(true);
    const names = await existingTemplateNames();
    const outcome = await duplicateWorkout(
      duplicateDeps, { id: draft.id, name: draft.name }, names);
    setDuplicating(false);
    if (outcome.kind === "failed") { setDuplicateSay(outcome.message); return; }
    if (outcome.kind === "partial") { setDuplicateSay(outcome.message); return; }
    setDuplicateSay(dirty
      ? "Copied the saved version; your unsaved edits stay here."
      : "Copied. The new workout is in your library.");
  };

  /* 🔴 WILLIAM'S ITEM 4 — `SAVE TO LIBRARY` ON A CLIENT'S COPY (his words: *"create the workout in this section and then
     have the option to save it as a template"*). The SAME `duplicateWorkout` as Duplicate — `clone_coach_workout` with no
     client, then root → null: a clean template under the copy's name (`duplicateName` handles a collision). One press:
     unsaved edits are saved to the client's day FIRST (so the template is what is on screen), then copied. The copy stays
     detached from the template (Claude's call, the plan's point 4). Said once. */
  const onSaveToLibrary = async () => {
    if (draft === null || saving !== null || duplicating) return;
    setDuplicateSay(null);
    let id = draft.id;
    if (dirty || id === null) {
      id = await saveNow({ thenClose: false });
      if (id === null) return;
    }
    setDuplicating(true);
    const names = await existingTemplateNames();
    const outcome = await duplicateWorkout(duplicateDeps, { id, name: draft.name }, names, libraryName);
    setDuplicating(false);
    if (outcome.kind === "failed" || outcome.kind === "partial") { setDuplicateSay(outcome.message); return; }
    setDuplicateSay(outcome.name === draft.name.trim() ? "Saved to your library." : `Saved to your library as ${outcome.name}.`);
  };

  /* 🔴 W85 — DELETE, FROM THE BUILDER'S `⋯`: press 1 measures and SAYS what will happen (`removeSentence`: Delete when
     nothing points at the workout, Archive otherwise — W17d's rule, his 2026-09-19 word); press 2 acts through the ONE
     `removeWorkout`, which re-reads the counts and only ever downgrades to an archive. Then the builder leaves to the
     Library — there is nothing left here to edit. ⚠️ `leaving` is set BEFORE the act, so an autosave timer that fires
     mid-delete cannot write the draft back over a row that is being removed. */
  const onDeletePressed = async () => {
    setMoreOpen(false);
    if (draft.id === null) return;
    setRemoving({ kind: "measuring" });
    const read = await readWorkoutUsage(draft.id);
    if (read.error !== null || read.usage === null) {
      setRemoving({ kind: "failed", usage: null,
        message: `Could not check what uses this workout, so nothing was changed: ${read.error ?? "the check returned nothing."}` });
      return;
    }
    setRemoving({ kind: "confirm", usage: read.usage });
  };
  const onRemoveConfirmed = async (usage: WorkoutUsage) => {
    if (draft.id === null) return;
    leaving.current = true;
    setRemoving({ kind: "working", usage });
    const outcome = await removeWorkout(removeDeps, { id: draft.id, name: draft.name }, new Date());
    if (outcome.kind === "failed") {
      leaving.current = false;
      setRemoving({ kind: "failed", message: outcome.message, usage });
      return;
    }
    onSaved();
    leave();
  };

  // What this workout needs, derived from the exercises it holds — the owner's second ask from
  // the same screenshots. Never stored: a coach who had to keep an equipment list in step with
  // the rows they added would keep it wrong.
  const kit = workoutEquipment(
    draft.sections.flatMap((sec) => sec.exercises), byId, EQUIPMENT_AMBIENT);

  /* ONE definition of the window's title: `frame` is given it for the two message states and the
     middle column renders it for this one. Computing it twice is how the two come to disagree. */
  const windowTitle = draft.id === null ? "New workout" : draft.name || "Workout";
  /* The button exists only when there is something to arrange (2+ items, exercises and parts together). */
  const outlineAvailable = arrangeItemCount(draft.sections) >= 2;
  /* W96 — A DRAG SAYS WHAT IT IS DOING (keyboard and screen reader): what was picked up, where it would land, where it
     went, and that a cancelled one went back. A position counts the part's places (a superset is one place). */
  const nameOf = (c: typeof carried): string => {
    if (c === null) return "the item";
    if (c.kind === "library") return byId.get(c.id)?.name ?? "the exercise";
    if (c.kind === "part") return sectionReference(draft.sections[c.section]?.name ?? null, c.section);
    const at = parseRunKey(c.key);
    return at === null ? "the exercise" : draft.sections[at.section]?.exercises[at.start]?.name ?? "the exercise";
  };
  const whereNow = (): string => {
    const c = carriedRef.current;
    if (c === null || c.kind === "part") {
      const order = partOrderRef.current;
      return c?.kind === "part" && order !== null ? `position ${String(order.indexOf(c.section) + 1)} of ${String(order.length)}` : "";
    }
    const arr = arrangementRef.current;
    const key = c.kind === "run" ? c.key : NEW_KEY;
    const at = arr === null ? null : locate(arr, key);
    if (at === null || arr === null) return "no place";
    const place = c.kind === "library" ? dropInsert(draft.sections, arr) : null;
    const index = place?.position ?? arr[at.section]!.slice(0, at.index).length;
    return `position ${String(index + 1)} in ${sectionReference(draft.sections[at.section]?.name ?? null, at.section)}`;
  };
  const dragAnnouncements = {
    onDragStart: () => `Picked up ${nameOf(carriedRef.current)}.`,
    onDragOver: () => `${nameOf(carriedRef.current)} would go to ${whereNow()}.`,
    onDragMove: () => undefined,
    onDragEnd: () => `Moved ${nameOf(carriedRef.current)} to ${whereNow()}.`,
    onDragCancel: () => `Cancelled. ${nameOf(carriedRef.current)} went back to its place.`,
  };
  /* W96 — what both surfaces draw: the live arrangement while a drag (or its landing) is on, the draft's otherwise. */
  const shownArrangement = arrangement ?? arrangementOf(draft.sections);
  const outlineOpen = outlineAvailable && outlinePref;
  return frame(
    windowTitle,
    <div className={`builder-window-body wb-body${outlineOpen ? " is-outline-open" : ""}${dragging ? " is-dragging" : ""}${hoverQuiet ? " is-hover-quiet" : ""}`}>
      <DndContext sensors={sensors} collisionDetection={collisionDetection} autoScroll={AUTOSCROLL}
                  onDragStart={onDragStart} onDragMove={onDragMove} onDragEnd={onDragEnd} onDragCancel={goHome}
                  accessibility={{ announcements: dragAnnouncements, screenReaderInstructions: DRAG_INSTRUCTIONS }}>
      <LayoutFrozen.Provider value={committing}>
      <GapHeight.Provider value={carried === null || carried.kind === "library" ? null : carried.h}>
      {/* 🔴 THE LIBRARY ON THE LEFT — his word, 2026-09-25: *"the exercise list needs to move left … cause the drag icons
          are gonna be on their right side of each exercise container so better be closer to the main central builder"*.
          A list, never a thumbnail grid; each row's six-dot grip on its RIGHT edge, beside the work. */}
      <aside className="wb-pane" aria-label="Your library">
        <LibraryColumn
          library={library ?? []}
          libraryLoaded={library !== null}
          searchRef={searchRef}
          destination={addAt !== null ? describeAddTarget(draft.sections, addAt.section) + " · below the row you chose" : describeAddTarget(draft.sections, target)}
          canAdd={target !== null || draft.sections.length === 0}
          hasSections={draft.sections.length > 0}
          lastAdded={lastAdded}
          onPick={(id, sectionIndex, position) => {
            if (draft.sections.length === 0) { addExerciseLoose(id, 0); return; }
            if (sectionIndex == null && addAt !== null) {
              addExercise(id, addAt.section, addAt.position);
              setAddAt({ section: addAt.section, position: addAt.position + 1 });
              return;
            }
            addExercise(id, sectionIndex, position);
          }}
        />
      </aside>
      <div className="builder-work" ref={workScroll}>
        {/* The dialog's name, visually hidden: the visible title is the field the coach types in (W32(7a)). */}
        <h2 className="ht-visually-hidden" id="builder-window-title" tabIndex={-1} ref={heading}>
          {windowTitle}
        </h2>
        {/* 🔴 W72 STEP 3 — THE HEAD (Proposal 3): the title · the save line · Undo · Redo · Phone preview · Save · ⋯ (job 3 p3). W95: the workout is
            written ONLY by Save (his word, 2026-09-29) — `Done` renamed; the line says `Unsaved changes` until then. */}
        <div className={`builder-title wb-head${client !== undefined ? " is-client-day" : ""}`} ref={moreRef}>
          {/* William's item 4 — over a client's week the head says whose day this is, and when. */}
          {client !== undefined && <p className="page-eyebrow wb-client-eyebrow">{clientDayEyebrow(client.clientName, client.day)}</p>}
          <label className="ht-visually-hidden" htmlFor="wname">Workout name</label>
          {/* A NAME IS ONE LINE OF TEXT that may WRAP: Enter does not break it, a pasted line break becomes a space. */}
          <textarea id="wname" ref={titleField} className="builder-title-input wb-title" value={draft.name} rows={1}
                    placeholder="Name your workout"
                    onKeyDown={(e) => { if (e.key === "Enter") e.preventDefault(); }}
                    onChange={(e) => {
                      /* 🔴 W84-1 — A NAME STOPS AT 28 CHARACTERS (his 2026-09-26 word), so the client's day row never
                         wraps: a keystroke or a paste past the cap is refused and ONE sentence says why. The rule and the
                         sentence are GENERATED from the phone's `WorkoutNameCap.swift` — one constant, two products. */
                      const typed = e.target.value.replace(/[\r\n]+/g, " ");
                      const kept = clampWorkoutName(typed);
                      setNameCapped(kept !== typed || (nameCapped && workoutNameLength(kept) >= WORKOUT_NAME_MAX));
                      update({ ...draft, name: kept });
                    }} />
          <p className={"xe-status wb-status" + (status.tone === "warn" ? " is-warn" : "")} role="status" aria-live="polite">
            {status.text}
          </p>
          {nameCapped && <p className="wb-name-cap" role="status">{WORKOUT_NAME_REFUSAL}</p>}
          {/* 🔴 JOB 3 STEP 5 PAGE 3 — THE HEAD'S TOOLS IN ONE PLACE (Fable's ruling: Undo/Redo/Phone preview/More into one place):
              W85's two buttons (his word: *"like we do on the app"*) moved up from the foot, still two buttons over the one
              `stepHistory`, the empty one disabled, ⌘Z / ⇧⌘Z unchanged. MEASURED at 1440: the head is 686 px and holds them
              in one row only as glyphs (with their words it needs ~772), so the glyph stands and the word is the button's
              name and tooltip — Phone preview's own W96 shape, now at every width. Save stays the one word, the one primary. */}
          <div className="wb-undo" role="group" aria-label="Undo and redo">
            <button type="button" className="ht-button wb-undo-button" title="Undo ⌘Z" aria-keyshortcuts="Meta+Z Control+Z"
                    disabled={history.current.past.length === 0} onClick={() => { stepHistory("undo"); }}>
              <Icon name="undo" size={18} /><span className="wb-undo-label">Undo</span>
            </button>
            <button type="button" className="ht-button wb-undo-button" title="Redo ⇧⌘Z" aria-keyshortcuts="Meta+Shift+Z Control+Shift+Z"
                    disabled={(history.current.future ?? []).length === 0} onClick={() => { stepHistory("redo"); }}>
              <Icon name="redo" size={18} /><span className="wb-undo-label">Redo</span>
            </button>
          </div>
          <button type="button" className="ht-button wb-phone" title="Phone preview" onClick={() => setPhoneOpen(true)}>
            <Icon name="phone" className="wb-phone-icon" /><span className="wb-phone-label">Phone preview</span>
          </button>
          {/* William's item 4 — a client's copy goes to the library from HERE (the template is a copy of it; the day keeps its
              own). It replaces the `⋯`'s Duplicate on a client day, where Duplicate would make the same template. */}
          {client !== undefined && (
            <button type="button" className="ht-button wb-to-library" disabled={saving !== null || duplicating}
                    onClick={() => { void onSaveToLibrary(); }}>
              {duplicating ? "Saving…" : "Save to library"}
            </button>
          )}
          <button type="button" className="ht-button ht-button-primary wb-done" disabled={saving !== null}
                  onClick={() => { void done(); }}>
            {saving !== null && closing ? "Saving…" : "Save"}
          </button>
          {/* On a client's copy the `⋯` has nothing of its own: on a day Duplicate is `Save to library` above, and a day is
              removed from its card (`Remove…`, which says what it would destroy) — never by deleting its copy from in here.
              The review's next week offers no `Save to library`: its copy came FROM the library, which the foot says stays. */}
          {!onClientCopy && (
          <button type="button" className="builder-more-button" ref={moreTrigger}
                  aria-expanded={moreOpen} aria-haspopup="menu"
                  aria-label="More actions for this workout"
                  onClick={() => setMoreOpen((o) => !o)}>
            <span aria-hidden="true">⋯</span>
          </button>
          )}
          {moreOpen && !onClientCopy && (
            <div className="builder-more-menu is-top-layer" role="menu" ref={moreList}>
              <button type="button" role="menuitem" className="builder-more-item"
                      disabled={duplicating} onClick={() => { void onDuplicate(); }}>
                {duplicating ? "Duplicating…" : "Duplicate"}
              </button>
              {/* W85 — absent, not disabled, for a workout never saved: there is no row to delete yet. */}
              {draft.id !== null && (
                <button type="button" role="menuitem" className="builder-more-item is-destructive"
                        disabled={saving !== null || removing.kind !== "idle"}
                        onClick={() => { void onDeletePressed(); }}>
                  Delete
                </button>
              )}
            </div>
          )}
        </div>
        {duplicateSay !== null && (
          <p className="ht-caption builder-duplicate-say" role="status">{duplicateSay}</p>
        )}

        {/* 🔴 W88 — THE DESCRIPTION IS PLAIN TEXT UNDER THE NAME (his yes, 2026-09-28): no `DESCRIPTION` label over it. The
            label stays for a screen reader; the field is still the one place the description is typed. */}
        <div className="builder-describe wb-describe">
          <label className="ht-visually-hidden" htmlFor="wdesc">Description</label>
          <textarea id="wdesc" className="builder-describe-input" rows={1}
                    value={draft.description ?? ""} placeholder="Add a description"
                    onChange={(e) => update({
                      ...draft,
                      description: e.target.value.trim() === "" ? null : e.target.value,
                    })} />
        </div>

        {/* 🔴 W88 — THE WORKOUT'S ONE CAPTION: the count and the equipment it needs, where the count line and the
            `EQUIPMENT` chips sat as two blocks (`workoutCaption`). The rule count is engine data, withheld from coaches for
            now (`COACH_SEES_ENGINE`, his 2026-09-28 word). */}
        {(() => {
          const line = workoutCaption(draft.sections.reduce((n, s) => n + s.exercises.length, 0),
                                      COACH_SEES_ENGINE ? ruleGap : { missing: 0, counted: 0 },
                                      { labels: kit.items.map(vocabularyLabel), unknown: kit.unknown });
          return line === null ? null : <p className="ht-caption wb-summary">{line}</p>;
        })()}
        {/* F15 4.2 — the switch and the deload live on the PROGRAM page (his 10-31 word); the editor keeps the rules. */}
        {workoutEngine.error !== null && <p className="ht-callout autoapply-error" role="alert">{workoutEngine.error}</p>}

        <div className="builder-problems" tabIndex={-1} ref={errorRef}
             role={blockers.length > 0 ? "alert" : undefined}>
          {problems.map((p, i) => (
            <p key={i} className={blockers.includes(p) ? "ht-callout builder-blocker" : "ht-callout builder-warning"}>
              {p.sectionIndex !== null && <strong>{sentenceStart(sectionReference(draft.sections[p.sectionIndex]?.name ?? null, p.sectionIndex))}: </strong>}
              {p.message}
            </p>
          ))}
          {saveError !== null && <p className="ht-callout builder-blocker" role="alert">{saveError}</p>}
        </div>
        <p className="ht-visually-hidden" aria-live="polite">{moveSay}</p>

        {/* W96 — the parts in their order (a PART being carried reorders them live), each drawing its runs from the live
            arrangement: the carried run's gap is wherever the arrangement puts it, in either surface. */}
        {(partOrder ?? draft.sections.map((_, i) => i)).map((si) => {
          const section = draft.sections[si];
          if (section === undefined) return null;
          return (
          <div key={`wrap-${si}`}>
          <SectionEditor
            key={si} section={section} index={si} sectionCount={draft.sections.length}
            runKeys={shownArrangement[si] ?? []} allSections={draft.sections}
            carriedKey={carried?.kind === "run" && carried.surface === "main" ? carried.key : carried?.kind === "library" ? NEW_KEY : null}
            newHeight={carried?.kind === "library" ? carried.h : 0} reducedMotion={reducedMotion} landedIn={landedIn}
            isTarget={target === si} mediaFor={mediaFor}
            ruleFor={(id: string) => (COACH_SEES_ENGINE ? ruleTag(byId.get(id)?.progressionConfig ?? null) : null)}
            ruleLineFor={(() => {
              const engine = workoutEngine.engine;
              /* Only the main block is ruled (`CoachProgression` rules `.main` alone): warm-up and cool-down rows draw no line. */
              if (engine === null || section.sectionType !== "workout") return undefined;
              return (ex: ExerciseDraft) => (
                <RowRuleLine engine={engine} exercise={ex} libraryConfig={byId.get(ex.libraryExerciseId)?.progressionConfig ?? null}
                             library={library ?? []}
                             onPick={(choice) => void workoutEngine.setRule(ex.libraryExerciseId, choice)} />
              );
            })()}
            stepsFor={(() => {
              const engine = workoutEngine.engine;
              /* F15 4.1a — his rule tabs under the title, the card the editor; the main block only, behind `COACH_RULE_TABS`. */
              if (!COACH_RULE_TABS || engine === null || section.sectionType !== "workout") return undefined;
              return (ex: ExerciseDraft): RowStepsInput => ({
                engine, libraryConfig: byId.get(ex.libraryExerciseId)?.progressionConfig ?? null, library: library ?? [],
                write: (choice) => workoutEngine.setRule(ex.libraryExerciseId, choice),
              });
            })()}
            dragging={dragging}
            onAddBelow={(ei: number) => {
              /* THE KEYBOARD PATH FOR "DRAG WHERE IT GOES" (his note 3, 2026-09-25): the row's `⋯` names the place,
                 the library's search takes the caret, and the next pick lands there. */
              setWantedTarget(si);
              setAddAt({ section: si, position: runAt(section.exercises, ei).start + runAt(section.exercises, ei).count });
              window.requestAnimationFrame(() => searchRef.current?.focus());
            }}
            onClaim={() => setWantedTarget(si)}
            moveTo={moveTargets(draft.sections, si)}
            onMoveExerciseTo={(ei, toSection) => {
              const run = runAt(section.exercises, ei);
              const target = draft.sections[toSection];
              if (target === undefined) return;
              update({ ...draft, sections: tidyImplicit(moveExerciseTo(draft.sections,
                { section: si, index: run.start, count: run.count },
                { section: toSection, index: target.exercises.length })) });
            }}
            looseTo={looseTarget(draft.sections, si)}
            onLoosen={(ei, at) => {
              const run = runAt(section.exercises, ei);
              update({ ...draft, sections: dropLooseAt(draft.sections,
                { section: si, index: run.start, count: run.count }, at) });
            }}
            onKeyboardMove={(ei: number) => { keyboardMove.current = { before: draft.sections, from: { section: si, index: ei } }; }}
            onChange={(s) => setSection(si, s)}
            onMoveSection={(delta) => update(moveSection(draft, si, delta))}
            onRemove={() => update({ ...draft, sections: draft.sections.filter((_, i) => i !== si) })}
            onDuplicate={() => update({
              ...draft,
              sections: [
                ...draft.sections.slice(0, si + 1),
                { ...section, isImplicit: false,
                  name: section.name === null ? null : `${section.name} (copy)`,
                  exercises: section.exercises.map((e) => ({ ...e, sets: e.sets.map((s) => ({ ...s })) })) },
                ...draft.sections.slice(si + 1),
              ],
            })}
          />
          </div>
          );
        })}

        {draft.sections.length === 0 && (
          <EmptyDrop armed={dragging}>
            <svg viewBox="0 0 24 16" aria-hidden="true" focusable="false" className="builder-empty-drop-mark">
              <rect x="0.75" y="5.75" width="22.5" height="4.5" rx="1.25"
                    fill="none" stroke="currentColor" strokeWidth="1.5" strokeDasharray="3 2" />
            </svg>
            <p className="builder-empty-drop-say">Drag exercises from the library on the left</p>
          </EmptyDrop>
        )}

        <p className="wb-add-section">
          <button type="button" className="ours-text-action"
                  onClick={() => update({ ...draft, sections: [...draft.sections, newSection()] })}>
            + Add section
          </button>
        </p>
      </div>

      {/* THE ARRANGEMENT — the whole workout at a glance, to move a row or a section across it without scrolling. Both it and
          the rows' own grips write through the one mover (`moveExerciseTo` / `moveSection` / `dropLooseAt`). */}
      {outlineAvailable && (
      /* The column slides between 0 and its width; the list inside keeps its own width, so it slides in whole rather than
         re-wrapping as it grows. Closed, it is `visibility: hidden` (out of the tab order and the reading order). */
      <aside className={`wb-arrange${outlineOpen ? " is-open" : ""}`} id="wb-arrange" aria-labelledby="arrange-h">
        <div className="wb-arrange-inner" ref={arrangeScroll}>
        <h3 className="wb-arrange-title" id="arrange-h">Arrange</h3>
        <OutlinePane sections={draft.sections} arrangement={shownArrangement}
                     order={partOrder ?? draft.sections.map((_, i) => i)}
                     carriedKey={carried?.kind === "run" && carried.surface === "outline" ? carried.key : null}
                     carriedPart={carried?.kind === "part" ? carried.section : null}
                     dragging={dragging} reducedMotion={reducedMotion} landedIn={landedIn}
                     folded={outlineFolded} setFolded={setOutlineFolded} partInFlight={partInFlight} partLanded={partLanded} />
        </div>
      </aside>
      )}
      {/* 🔴 W96 — THE CARRIED THING FOLLOWS THE POINTER: its own rendered look (a static copy taken at pick-up), lifted,
          0.92 opacity, no tilt and no browser snapshot image; it lands on the gap, or flies home, in `DROP_MS`. Inside
          `.wb-body` so the card's own styles reach it. */}
      <DragOverlay dropAnimation={dropAnimation(reducedMotion)} zIndex={40}>
        {carried === null ? null : (
          <div className={`wb-drag-overlay is-${carried.kind}${carried.kind === "run" ? ` is-${carried.surface}` : ""}`}>
            {carried.kind === "run" && carried.surface === "main"
              ? <ol className="builder-exercises" dangerouslySetInnerHTML={{ __html: carried.html }} />
              : carried.kind === "library"
                ? <div className="builder-picker-list" dangerouslySetInnerHTML={{ __html: carried.html }} />
                /* The Arrange panel's looks are scoped under `.wb-arrange`, so its copies are drawn inside that scope — a
                   part in a part list, a row in a row list (MEASURED from the stills: a part drawn in a ROW list, outside
                   the scope, lost its name and squeezed its rows into a column). */
                : <div className="wb-arrange wb-drag-arrange">
                    {carried.kind === "part"
                      ? <ol className="builder-outline-list" dangerouslySetInnerHTML={{ __html: carried.html }} />
                      : <ol className="builder-outline-exercises" dangerouslySetInnerHTML={{ __html: carried.html }} />}
                  </div>}
          </div>
        )}
      </DragOverlay>
      </GapHeight.Provider>
      </LayoutFrozen.Provider>
      </DndContext>
    </div>,

    /* THE FOOT — Proposal 3's two sentences. The second is MEASURED, not quoted: a template edit reaches only days
       assigned after it (the next-week writer clones LAST WEEK's copy, never the template — CoachNextWeekService §1). */
    <footer className="ht-window-foot wb-foot">
      {/* Job 3 step 5 page 3 — Undo · Redo left the foot for the head's one cluster of tools; the foot keeps its sentence. */}
      <p className="ht-caption">{route.name === "reviewNextWeek" ? NEXT_WEEK_COPY_LINE
        : client !== undefined ? CLIENT_DAY_COPY_LINE : usedOnLine(usedOn)}</p>
    </footer>,
    true,
    /* 🔴 W88 — THE ROUND BUTTON ON THE OUTLINE'S DIVIDER, drawn BESIDE the frame (a child of the dialog, which does not clip)
       so that closed it can straddle the WINDOW's own edge, half outside it — his word: *"the right half of the arrow can
       go further than our main container when that is closed"*. `‹` closed, `›` open: the arrow points the way the panel
       will move, and turns 180° as it slides. */
    <>
      {outlineAvailable ? (
        <button type="button" className={`wb-arrange-edge${outlineOpen ? " is-open" : ""}`}
                aria-expanded={outlineOpen} aria-controls="wb-arrange"
                aria-label={outlineOpen ? "Hide the outline" : "Show the outline"}
                onClick={() => setOutlinePref(!outlineOpen)}>
          <Icon name="chevron-left" size={14} />
        </button>
      ) : null}
    </>,
  );
}


/* ── The section ──────────────────────────────────────────────────────────────────────────── */

function SectionEditor({ section, index, sectionCount, runKeys, allSections, carriedKey, newHeight, reducedMotion, landedIn, isTarget, mediaFor, ruleFor, ruleLineFor, stepsFor, dragging, onAddBelow, moveTo, looseTo, onMoveExerciseTo, onLoosen, onKeyboardMove, onClaim, onChange, onRemove, onMoveSection, onDuplicate }: {
  /** W80 — a row is about to be moved from its `⋯` (the page returns focus to it where it lands). */
  onKeyboardMove: (exerciseIndex: number) => void;
  /** W72 step 3 — the row's rule, NAMED by kind (`builderHead.ruleTag`), or null. The page owns the library. */
  ruleFor: (libraryExerciseId: string) => { text: string; lacks: string | null } | null;
  /** F15 step 3 (workout-based) — the row's progression line and menu; undefined while hidden or outside the main block. */
  ruleLineFor?: (exercise: ExerciseDraft) => ReactNode;
  /** F15 step 4.1a — the row's rule tabs (the card is the editor); undefined while hidden or outside the main block. */
  stepsFor?: (exercise: ExerciseDraft) => RowStepsInput;
  /** A drag is in flight (a run, a library row or a part) — the seams between runs step aside for it. */
  dragging: boolean;
  /** 🔴 W96 — the runs this part draws, as keys of the LIVE arrangement (`lib/dragModel`): at rest its own runs; mid-drag
   *  the carried key sits wherever the gap is, even if it came from another part — so each run is drawn from
   *  `allSections` by its key, never from this part's own list by index. */
  runKeys: readonly string[]; allSections: readonly SectionDraft[];
  /** The key in flight in the main column (a run's, or the library's `new`), or null — its node is the GAP. */
  carriedKey: string | null;
  /** The library row's gap: the carried row's own height. */
  newHeight: number;
  reducedMotion: boolean;
  /** The part the last drop landed in: a folded part that receives something opens, so the result is visible. */
  landedIn: { section: number; n: number } | null;
  /** The keyboard path for "drop it here": the next pick lands below this row. */
  onAddBelow: (exerciseIndex: number) => void;
  /** W23(e) — every OTHER section, as `Section 3 · Main`. Computed once by the page and passed
   *  down, because it is the same list for every card in this section. */
  moveTo: { index: number; label: string }[];
  /** W28(e) — the boundary a row of THIS section is loosened to, or `null` when the gesture has
   *  no meaning here (this section IS the implicit one). Computed at the page for the reason
   *  `moveTo` is: a section does not know where it sits among the others. */
  looseTo: number | null;
  /** Move the run at this index into another section. The page owns it: a section cannot write
   *  into a section it is not. */
  onMoveExerciseTo: (exerciseIndex: number, toSection: number) => void;
  /** Move the run at this index OUT of every section, at the boundary `looseTo` named. */
  onLoosen: (exerciseIndex: number, at: number) => void;
  section: SectionDraft; index: number; sectionCount: number;
  /** The library's picture for a drafted exercise, by its `libraryExerciseId`. Passed as a
   *  FUNCTION rather than the whole library, so a section can answer only the question it has. */
  mediaFor: (libraryExerciseId: string) => RowMedia;
  /** ⚠️ PASSED, NOT DERIVED HERE — the same reason `mediaFor` is. A section deriving its own
   *  answer is a second copy of the rule, and the header's count would stop agreeing with the
   *  badges the moment either changed. */
  isTarget: boolean; onClaim: () => void;
  onMoveSection: (delta: -1 | 1) => void;
  /** §6.3's menu — a copy of this section, inserted after it. */
  onDuplicate: () => void;
  onChange: (s: SectionDraft) => void; onRemove: () => void;
}) {
  /* §6.3 — the bar's chevron. PER WINDOW ONLY, as the brief rules: collapsing is a way of reading
     a long session while you work on it, not a property of the workout. Persisting it would put a
     coach's reading posture into a coach's data, and the next person to open the workout would
     inherit somebody else's folded sections. */
  const [collapsed, setCollapsed] = useState(false);
  /** W88 — the coach pressed `Add what it's for` on a part that has no purpose yet: the line is drawn, focused. */
  const [writingPurpose, setWritingPurpose] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [customising, setCustomising] = useState(false);
  const setExercise = (ei: number, ex: ExerciseDraft) =>
    onChange({ ...section, exercises: section.exercises.map((x, i) => (i === ei ? ex : x)) });
  useEffect(() => { if (landedIn?.section === index) setCollapsed(false); }, [landedIn, index]);
  /* 🔴 W96 — THE PART IS A CONTAINER: the midpoint collision finds the part under the pointer (or the nearest) by this
     node, and the gap moves into it at the index the neighbours' midpoints give. */
  const { setNodeRef: setPartRef } = useDroppable({
    id: `m:part:${String(index)}`, data: { kind: "part", surface: "main", section: index } satisfies DropData,
  });
  /* The runs as drawn — each from the section its key names, so a run carried in from another part draws its own cards. */
  const views = runKeys.map((key) => {
    if (key === NEW_KEY) return { key, section: index, rows: [] as { ex: ExerciseDraft; ei: number }[] };
    const at = parseRunKey(key);
    const src = at === null ? undefined : allSections[at.section];
    const run = at === null ? [] : supersetRuns(src?.exercises ?? []).find((r) => r[0] === at.start) ?? [];
    return { key, section: at?.section ?? index, rows: run.map((i) => ({ ex: src!.exercises[i]!, ei: i })) };
  });
  const itemIds = useStableIds(views.map((v) => `m:${v.key}`));

  return (
    /* 🔴 THE SECTION CLAIMS ITSELF, WHICH IS WHAT "ADD EXERCISE HERE" USED TO DO. Deleting that
       button (§6.1) removed the only way to say WHICH section a click or an Enter adds to — and
       §12's rule is that the destination is STATED, never inferred. So the section claims the
       picker when it is clicked, and when focus enters it.
       ⚠️ `onFocusCapture` IS THE HALF THE KEYBOARD NEEDS: §6.1 keeps Enter-on-a-picker-row as the
       keyboard path and defines it as "adds to the section that holds the focus" — that sentence
       is only TRUE if moving focus into a section makes it the destination, which is this line.
       Neither gesture is destructive: claiming changes where the NEXT add goes and nothing else. */
    <section className="builder-section" id={`builder-section-${index}`} ref={setPartRef}
             onClick={onClaim} onFocusCapture={onClaim}>
      {/* §6.5 — an implicit section draws NO BAR. It is not a section a coach made, so a name, a
          format and a block type would all be answers to questions nobody asked. Customising one
          NAMES it, which turns it into a real section. */}
      {!section.isImplicit && (
        <div className="builder-section-bar">
          {/* §6.3 — the chevron collapses the section's CARDS, not the bar. A long session is read
              by folding what you are not working on. */}
          <button type="button" className="builder-section-chevron"
                  aria-expanded={!collapsed}
                  onClick={() => setCollapsed((c) => !c)}>
            <span aria-hidden="true">{collapsed ? "\u203A" : "\u2304"}</span>
            <span className="ht-visually-hidden">
              {collapsed ? `Show ${sectionReference(section.name, index)}'s exercises`
                         : `Hide ${sectionReference(section.name, index)}'s exercises`}
            </span>
          </button>
          {sectionName(section.name) !== null
            ? <span className="builder-section-title">{sectionName(section.name)}</span>
            : <span className="builder-section-title is-unnamed">Section</span>}
          {/* 🔴 THE FORMAT AS A WORD, AND ITS ONE NUMBER BESIDE IT (§6.6 item 4, measured from his
              own recording: *"Every minute · Interval"* shows `DURATION 10 min` in the bar;
              *"Three rounds for three · Timed"* shows `ROUND 3 rounds`). The comment here used to
              say the numbers live in Customise — they are EDITED there and they are READ here,
              which is not the same thing. A bar that names the format and hides its only number
              makes a coach open a popover to learn whether the AMRAP is ten minutes or twenty.
              ⚠️ `formatHeadline` already existed and was called from NOWHERE — the rule was
              written and never wired, which is the shape `CLAUDE.md` calls a flag with no writer:
              a decision that looks taken and does nothing. */}
          {/* 🔴 W88 — THE FORMAT WORD ONLY WHEN IT SAYS SOMETHING (his yes, 2026-09-28): straight sets is what a part IS
              unless told otherwise, so `Straight sets` on every bar was a word repeated for nothing. Any other format keeps
              its word and its one number. */}
          {section.format !== "straight_sets" && (
            <span className="builder-section-format">{FORMAT_LABEL[section.format]}</span>
          )}
          {(() => {
            const n = formatHeadline(section.format, section.formatConfig);
            // A format with no number, or one not yet set, prints NOTHING — never a placeholder
            // dash. `formatConfigProblems` is what says a required number is missing, in the
            // Customise popover where it can be fixed.
            return n === null ? null : <span className="builder-section-number ht-data-small">{n}</span>;
          })()}
          {/* DERIVED, never stored — §6.3's own rule. */}
          {/* W88 — a part with no purpose offers one HERE, in the bar's own line, and only while the part is pointed at
              or focused: an empty line under every unexplained part was a column of blank space (rendered). */}
          {(section.description ?? "") === "" && !writingPurpose && (
            <button type="button" className="wb-part-purpose-add" onClick={() => setWritingPurpose(true)}>
              Add what it’s for
            </button>
          )}
          <span className="builder-section-count">{exerciseCountWords(section.exercises.length)}</span>
          <div className="builder-section-menu-wrap">
            {/* W45(b)(ii)'s hover tip is GONE (W72 step 7, Proposal 7 rule 10 — dark tips with tails are theirs). The
                button keeps `Actions for <section>` in a visually-hidden span, which is its name for a screen reader. */}
            <button type="button" className="builder-section-kebab"
                    aria-expanded={menuOpen} aria-haspopup="menu"
                    onClick={() => setMenuOpen((o) => !o)}>
              <span aria-hidden="true">{"\u22EF"}</span>
              <span className="ht-visually-hidden">Actions for {sectionReference(section.name, index)}</span>
            </button>
            {menuOpen && (
              /* ⚠️ A real list of real buttons. Everfit's own work surface was measured at ZERO
                 focusable elements (§12); a menu of `div`s would repeat exactly that. */
              <ul className="builder-section-menu" role="menu"
                  onMouseLeave={() => setMenuOpen(false)}>
                <li role="none">
                  <button role="menuitem" onClick={() => { setMenuOpen(false); setCustomising(true); }}>
                    Customise section
                  </button>
                </li>
                <li role="none">
                  <button role="menuitem" onClick={() => { setMenuOpen(false); onDuplicate(); }}>
                    Duplicate section
                  </button>
                </li>
                <li role="none">
                  <button role="menuitem" disabled={index === 0}
                          onClick={() => { setMenuOpen(false); onMoveSection(-1); }}>
                    Move up
                  </button>
                </li>
                <li role="none" className="is-divided">
                  <button role="menuitem" disabled={index === sectionCount - 1}
                          onClick={() => { setMenuOpen(false); onMoveSection(1); }}>
                    Move down
                  </button>
                </li>
                <li role="none">
                  <button role="menuitem" className="is-danger"
                          onClick={() => { setMenuOpen(false); onRemove(); }}>
                    Delete section
                  </button>
                </li>
              </ul>
            )}
            {/* 🔴 THE CUSTOMISE PANEL LIVES IN THE KEBAB'S OWN WRAP NOW, so it can be lifted out
                of flow and anchored to the control that opened it. It used to render as a sibling
                of the whole bar, in normal flow, which is what he was looking at: *"as we do it
                now, the whole container moves and it's a huge mess."* MEASURED before the move —
                opening it pushed EVERY section below down by 516 px and the document from 6,930
                to 7,446. */}
            {customising && (
              <CustomiseSection section={section} index={index}
                                onCancel={() => setCustomising(false)}
                                onUpdate={(next) => { onChange(next); setCustomising(false); }} />
            )}
          </div>
        </div>
      )}


      {/* 🔴 W88 — EACH PART SAYS WHAT IT IS FOR, IN ONE LINE UNDER ITS NAME (his yes, 2026-09-28: *"Gets them ready — done
          first, lighter."*). It is the part's own `description` — the column the phone already shows the client as the
          section's note (`ClientTrainingService`), 27 of 265 live parts carry one — typed here in place. Empty, it is not
          drawn at all until `Add what it's for` is pressed in the bar; emptied and left, it goes again. */}
      {!section.isImplicit && ((section.description ?? "") !== "" || writingPurpose) && (
        <textarea className="wb-part-purpose" rows={1} autoFocus={writingPurpose}
                  aria-label={`What ${sectionReference(section.name, index)} is for`}
                  placeholder="What this section is for"
                  value={section.description ?? ""}
                  onBlur={() => setWritingPurpose(false)}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); e.currentTarget.blur(); } }}
                  onChange={(e) => {
                    const v = e.target.value.replace(/[\r\n]+/g, " ");
                    onChange({ ...section, description: v.trim() === "" ? null : v });
                  }} />
      )}

      {/* The chevron folds the CARDS. The bar, the menu and the count stay — a folded section
          must still say what it is and still be movable, or folding it would hide the controls
          that make folding useful. */}
      {/* 🔴 W96 — ONE SORTABLE ITEM PER RUN (a loose card, or a superset's cards and seams), drawn in the order the live
          arrangement gives; the carried one is the GAP (an empty dashed space of its own height, `is-placeholder`). No
          slots: the gap is the only target, it sits only at a run's edges, and a superset can never be split by a drag. */}
      <SortableContext id={`m:part:${String(index)}`} items={itemIds} strategy={verticalListSortingStrategy}>
      {/* A FOLDED PART keeps its labelled target while something is carried (the drop lands at its end), and draws the
          carried thing's gap inside it when the gap is here — the carried node must stay in the page. */}
      {collapsed && dragging && (
        <p className="builder-drop builder-fold-target">{`Into ${sectionReference(section.name, index)}`}</p>
      )}
      {(!collapsed || views.some((v) => v.key === carriedKey)) && (
      <ol className="builder-exercises">
        {views.map((view, vi) => {
          if (collapsed && view.key !== carriedKey) return null;
          if (view.key === NEW_KEY) {
            return (
              <SortableRun key={NEW_KEY} id={`m:${NEW_KEY}`} surface="main" runKey={NEW_KEY} disabled reduced={reducedMotion}
                           className="builder-run is-placeholder is-new">
                <div className="builder-new-gap" style={{ height: newHeight }} />
              </SortableRun>
            );
          }
          const n = view.rows.length;
          const lastRow = view.rows[n - 1];
          const nextView = views[vi + 1];
          return (
            <Fragment key={view.key}>
            <SortableRun id={`m:${view.key}`} surface="main" runKey={view.key} disabled={false} reduced={reducedMotion}>
              <ol className="builder-run-cards">
              {view.rows.map(({ ex, ei }, k) => {
                /* W88 — ONE grip per superset: a loose card keeps its own; an ODD run's middle card carries it; an EVEN
                   run's sits on the seam between its two middle cards. */
                const gripShown = n === 1 || (n % 2 === 1 && k === (n - 1) / 2);
                const seamGrip = n > 1 && n % 2 === 0 && k === n / 2 - 1;
                const first = k === 0;
                const run = view.rows.map((r) => r.ei);
                return (
                  <Fragment key={ei}>
                    <ExerciseRow
                      exercise={ex} media={mediaFor(ex.libraryExerciseId)} format={section.format}
                      ruleTag={ruleFor(ex.libraryExerciseId)}
                      ruleLine={ruleLineFor?.(ex)}
                      steps={stepsFor?.(ex) ?? null}
                      onAddBelow={() => onAddBelow(ei)}
                      domId={`builder-exercise-${index}-${ei}`}
                      joinedAbove={!first} joinedRun={run.length > 1} gripShown={gripShown}
                      index={ei} count={section.exercises.length}
                      /* W23(e) — computed per card, from the WHOLE draft's sections, because a move out
                         of this section is the one gesture a section cannot answer on its own. */
                      moveTo={moveTo}
                      looseTo={looseTo}
                      onLoosen={(at) => { onKeyboardMove(ei); onLoosen(ei, at); }}
                      pairedRun={run.length > 1}
                      onMoveTo={(toSection) => { onKeyboardMove(ei); onMoveExerciseTo(ei, toSection); }}
                      onMove={(delta) => { onKeyboardMove(ei); onChange(moveExercise(section, ei, delta)); }}
                      onChange={(x) => setExercise(ei, x)}
                      onDuplicate={() => onChange(duplicateExercise(section, ei))}
                      viewHref={href({ name: "exercise", exerciseId: ex.libraryExerciseId })}
                      onRemove={() => onChange({
                        ...section,
                        exercises: section.exercises.filter((_, i) => i !== ei)
                          .map((x, i, arr) => (i === arr.length - 1 ? { ...x, supersetWithNext: false } : x)),
                      })}
                    />
                    {k < n - 1 && allowsSupersets(section.format) && (
                      <JoinMark
                        showToggle={!dragging}
                        joined={ex.supersetWithNext}
                        upper={ex.name}
                        lower={section.exercises[ei + 1]?.name ?? "the next exercise"}
                        onToggle={() => setExercise(ei, { ...ex, supersetWithNext: !ex.supersetWithNext })}
                        grip={seamGrip
                          ? <RowGrip className="wb-grip-seam" label={ex.name} />
                          : null}
                      />
                    )}
                  </Fragment>
                );
              })}
              </ol>
            </SortableRun>
            {/* §6 adoption 4 — the link between two runs is the toggle, drawn in the gap it describes; it steps aside
                while anything is carried (two controls in one place mid-drag is how a coach drops onto the wrong one). */}
            {!dragging && lastRow !== undefined && nextView !== undefined && nextView.key !== NEW_KEY
              && allowsSupersets(section.format) && view.section === index && (
              <JoinMark
                showToggle
                joined={false}
                upper={lastRow.ex.name}
                lower={allSections[index]?.exercises[lastRow.ei + 1]?.name ?? "the next exercise"}
                onToggle={() => setExercise(lastRow.ei, { ...lastRow.ex, supersetWithNext: true })}
                grip={null}
              />
            )}
            </Fragment>
          );
        })}
      </ol>
      )}
      </SortableContext>

      {collapsed && !dragging && (
        <p className="ht-caption builder-section-folded">
          {section.exercises.length === 0
            ? "Empty"
            : section.exercises.map((e) => e.name).join(", ")}
        </p>
      )}

      {/* 🔴 "ADD EXERCISE HERE" IS DELETED — §6.1, his word: *"the only possible way to add
          exercises needs to be to drag exercises where we like."* The button was the thing he was
          looking at when he said it. What replaces it as the SECTION'S CLAIM is the section
          header itself: clicking anywhere in the section makes it the destination, so the picker
          still knows where a click or an Enter lands without a button saying so.
          ⚠️ The line below is kept because it is the ANSWER to "which section does this go to",
          which §12 says must be stated and never inferred — it is a statement, not a control. */}
      {isTarget && section.exercises.length === 0 && !dragging && (
        <p className="ht-caption builder-target-here" aria-live="polite">
          Nothing here yet — drag an exercise across.
        </p>
      )}
    </section>
  );
}

/* Scroll a card into view inside the builder's own scroll column.
 *
 * ⚠️ NOT `el.scrollIntoView()`, AND THE REASON IS MEASURED, NOT STYLISTIC. Called from the
 * outline's click handler it moved NOTHING — `.builder-work.scrollTop` stayed 0 — while the very
 * same call on the very same element moved it to 5009 when run straight from the console. The
 * handler was firing (the hash never changed, so `preventDefault` ran), so the API was being
 * defeated by something about the moment it was called inside a `<dialog>` whose ancestors carry
 * `overflow: hidden`. Rather than keep guessing at that, the offset is computed against the
 * scroll container and assigned — which is deterministic, works from any caller, and can be
 * asserted by reading one number back.
 *
 * `behavior` follows the reader: a 5,000 px smooth glide is exactly what
 * `prefers-reduced-motion` exists to refuse. */
function scrollCardIntoView(id: string): void {
  const el = document.getElementById(id);
  if (el === null) return;
  const box = el.closest<HTMLElement>(".builder-work");
  if (box === null) { el.scrollIntoView({ block: "center" }); return; }
  const top = el.getBoundingClientRect().top - box.getBoundingClientRect().top + box.scrollTop;
  const smooth = !window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  box.scrollTo({ top: Math.max(0, top - box.clientHeight / 2), behavior: smooth ? "smooth" : "auto" });
}

/* THE OUTLINE PANE — "Workout arrangement" (§6 adoption 5).
 *
 * 🔴 §6 RULE (c): IT IS A DERIVED VIEW OF THE SAME STATE, NO SECOND LIST. It takes `sections` and
 * reads them; it holds nothing of its own. A pane that kept its own copy would be the two-stores
 * bug this repo has paid for repeatedly — the copy drifts the first time anything edits the
 * draft, and the outline is precisely the surface a coach trusts to tell them the shape.
 *
 * ⚠️ TAKEN AFTER ALL, and the reason is his own libraries: a 6-section, 17-exercise session does
 * not fit a screen, so without this the whole shape is never visible at once.
 *
 * ⚠️ A TAP SCROLLS, IT DOES NOT SELECT. `scrollIntoView` on the card's own element, found by id —
 * so the outline cannot hold a "current" that disagrees with the page. Reordering here uses the
 * SECTION's own move, the same function the arrows call (§6 rule b, one write path). */
/* 🔴 W96 — ON @dnd-kit, THE SAME CONTRACT AS THE MAIN COLUMN AND THE SAME LIST (§6 rule (c) kept: it draws what it is handed
 * and holds only its folds). A row, or a superset as ONE pair, is one sortable item picked up anywhere on it; a PART is
 * picked up by its head and moves whole; each part is a container the gap can move into. Both surfaces draw the page's
 * live arrangement, so a drag in either moves both. The old gaps, the section boundary and the loose drop went with the
 * HTML5 drag: the gap is the only target (W96 clause 5) — `Move out of the part` stays in the card's `⋯` (W28(e)). */
function OutlinePane({ sections, arrangement, order, carriedKey, carriedPart, dragging, reducedMotion, landedIn,
                       folded, setFolded, partInFlight, partLanded }: {
  /** W99 — the folded parts (by index; the builder keeps them so a part move carries them), the part folded because it
   *  is being carried, and the part that last landed. */
  folded: ReadonlySet<number>;
  setFolded: (update: (f: ReadonlySet<number>) => ReadonlySet<number>) => void;
  partInFlight: number | null;
  partLanded: { section: number; n: number } | null;
  sections: SectionDraft[];
  /** The live arrangement (`lib/dragModel`) — the runs of each part as keys. */
  arrangement: readonly (readonly string[])[];
  /** The parts' order — live while a part is carried. */
  order: readonly number[];
  /** The run carried FROM THIS PANE (its node is the gap here), or null. */
  carriedKey: string | null;
  /** The part carried by its head, or null. */
  carriedPart: number | null;
  dragging: boolean;
  reducedMotion: boolean;
  /** The part the last drop landed in — opened here if it was folded, so the result is visible. */
  landedIn: { section: number; n: number } | null;
}) {
  /* Which sections are folded IN THIS PANE. His ask, on Everfit's own arrangement panel: a triangle on each heading that
     hides its rows. ⚠️ Kept BY INDEX and per window — folding is how a coach reads a long session, never workout data. */
  const toggleFold = (si: number) => setFolded((f) => {
    const next = new Set(f);
    if (next.has(si)) next.delete(si); else next.add(si);
    return next;
  });
  useEffect(() => {
    if (landedIn === null) return;
    setFolded((f) => { if (!f.has(landedIn.section)) return f; const next = new Set(f); next.delete(landedIn.section); return next; });
  }, [landedIn]);
  const { setNodeRef: setListRef } = useDroppable({ id: "o:parts", data: { kind: "parts" } satisfies DropData });
  const partIds = useStableIds(order.map((si) => `o:partitem:${String(si)}`));
  return (
    <aside className={dragging ? "builder-outline is-dragging" : "builder-outline"} id="builder-outline" aria-labelledby="outline-h">
      <div className="builder-outline-head">
        <h3 id="outline-h" className="ht-overline">Workout arrangement</h3>
      </div>
      {sections.length === 0 && (
        <div className="builder-outline-empty">
          <svg viewBox="0 0 40 30" aria-hidden="true" focusable="false"
               className="builder-outline-empty-mark">
            <rect x="1" y="2" width="26" height="6" rx="2" fill="none"
                  stroke="currentColor" strokeWidth="1.5" />
            <rect x="1" y="12" width="26" height="6" rx="2" fill="none"
                  stroke="currentColor" strokeWidth="1.5" />
            <rect x="1" y="22" width="26" height="6" rx="2" fill="none"
                  stroke="currentColor" strokeWidth="1.5" strokeDasharray="3 2" />
            {/* The arrow that says the rows MOVE — the one fact the empty panel exists to carry. */}
            <path d="M32 7 a7 7 0 0 1 0 16" fill="none" stroke="currentColor" strokeWidth="1.5"
                  strokeLinecap="round" />
            <path d="M29 20.5 L32 23.5 L35 20.5" fill="none" stroke="currentColor"
                  strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          <p className="builder-outline-empty-say">Drag and move exercises to rearrange</p>
        </div>
      )}
      <SortableContext id="o:parts" items={partIds} strategy={verticalListSortingStrategy}>
      <ol className="builder-outline-list" ref={setListRef}>
        {order.map((si) => {
          const sec = sections[si];
          return sec === undefined ? null : (
            <OutlinePart key={si} si={si} sec={sec} sections={sections} keys={arrangement[si] ?? []}
                         folded={folded.has(si)} onToggleFold={() => toggleFold(si)}
                         inFlight={partInFlight === si} landing={partLanded?.section === si}
                         carriedKey={carriedKey} carried={carriedPart === si} reducedMotion={reducedMotion} />
          );
        })}
      </ol>
      </SortableContext>
    </aside>
  );
}

function OutlinePart({ si, sec, sections, keys, folded, onToggleFold, carriedKey, carried, reducedMotion, inFlight, landing }: {
  si: number; sec: SectionDraft; sections: readonly SectionDraft[]; keys: readonly string[];
  folded: boolean; onToggleFold: () => void; carriedKey: string | null; carried: boolean; reducedMotion: boolean;
  /** W99 — this part is being carried (folded to its head until the drop is written), and it is the one that last landed. */
  inFlight: boolean; landing: boolean;
}) {
  const frozen = useContext(LayoutFrozen);
  const part = useSortable({
    id: `o:partitem:${String(si)}`, disabled: sec.isImplicit, data: { kind: "partitem", section: si } satisfies DropData,
    animateLayoutChanges: reducedMotion || frozen ? () => false : alwaysAnimate,
    transition: reducedMotion ? null : { duration: SLIDE_MS, easing: "ease" },
  });
  const gapH = useContext(GapHeight);
  const rowIds = useStableIds(keys.map((k) => `o:${k}`));
  const drop = useDroppable({ id: `o:sec:${String(si)}`, data: { kind: "part", surface: "outline", section: si } satisfies DropData });
  const shown = folded || inFlight ? keys.filter((k) => k === carriedKey) : keys;
  /* W104 — the coach's name, or the bare word `Section` in the hint ink (never a number). */
  const named = sectionName(sec.name);
  return (
    <li ref={(el) => { part.setNodeRef(el); drop.setNodeRef(el); }} data-part={si}
        style={{ transform: CSS.Translate.toString(part.transform), transition: part.transition ?? undefined,
                 ...gapStyle(part.isDragging, gapH) }}
        className={["builder-outline-section", sec.isImplicit ? "is-implicit" : "", carried || part.isDragging ? "is-placeholder" : "",
                    landing ? "is-landing" : ""]
          .filter(Boolean).join(" ")}>
      {/* §6.5 — AN IMPLICIT SECTION DRAWS NO HEAD (nobody named it), so it cannot be picked up; its rows still drag. */}
      {!sec.isImplicit && (
        <div className="builder-outline-section-head" ref={part.setActivatorNodeRef} {...part.attributes} {...part.listeners}
             aria-label={`Move ${sectionReference(sec.name, si)}`}>
          {sec.exercises.length === 0
            ? <span className="builder-outline-fold is-empty" aria-hidden="true" />
            : (
          <button type="button" className="builder-outline-fold"
                  aria-expanded={!folded}
                  onClick={(e) => { e.stopPropagation(); onToggleFold(); }}>
            <span aria-hidden="true">{folded ? "\u25B8" : "\u25BE"}</span>
            <span className="ht-visually-hidden">
              {folded ? `Show ${sectionReference(sec.name, si)}'s exercises`
                      : `Hide ${sectionReference(sec.name, si)}'s exercises`}
            </span>
          </button>
            )}
          <a className="builder-outline-link ht-body-medium"
             href={`#builder-section-${String(si)}`}
             onClick={(e) => { e.preventDefault(); scrollCardIntoView(`builder-section-${String(si)}`); }}>
            {named ?? <span className="is-unnamed">Section</span>}
          </a>
          <span className="builder-outline-count" aria-hidden="true">({sec.exercises.length})</span>
          <span className="builder-outline-handle" aria-hidden="true">{"\u2261"}</span>
        </div>
      )}
      <SortableContext id={`o:sec:${String(si)}`} items={rowIds} strategy={verticalListSortingStrategy}>
      {shown.length > 0 && (
        <ol className="builder-outline-exercises">
          {shown.map((key) => <OutlineRun key={key} runKey={key} sections={sections} reducedMotion={reducedMotion} />)}
        </ol>
      )}
      </SortableContext>
    </li>
  );
}

/** One run in the outline: a single row, or a superset as ONE pair with one handle (a pair is one object — §6.4 — and
 *  W96 moves it whole; nothing lands between its rows). Picked up anywhere on it; a click on the name still scrolls. */
function OutlineRun({ runKey, sections, reducedMotion }: {
  runKey: string; sections: readonly SectionDraft[]; reducedMotion: boolean;
}) {
  const at = parseRunKey(runKey);
  if (at === null) {
    // The library row flown into the main column shows here too, as a quiet gap (one list, two surfaces).
    return (
      <SortableRun id={`o:${runKey}`} surface="outline" runKey={runKey} disabled reduced={reducedMotion} handle="whole"
                   className="builder-outline-exercise is-placeholder is-new">
        <span className="builder-outline-new" aria-hidden="true" />
      </SortableRun>
    );
  }
  const sec = sections[at.section];
  const run = supersetRuns(sec?.exercises ?? []).find((r) => r[0] === at.start) ?? [at.start];
  const link = (i: number) => (
    <a href={`#builder-exercise-${String(at.section)}-${String(i)}`}
       onClick={(e) => { e.preventDefault(); scrollCardIntoView(`builder-exercise-${String(at.section)}-${String(i)}`); }}>
      {sec?.exercises[i]?.name}
    </a>
  );
  if (run.length === 1) {
    return (
      <SortableRun id={`o:${runKey}`} surface="outline" runKey={runKey} disabled={false} reduced={reducedMotion} handle="whole"
                   className="builder-outline-exercise">
        {link(at.start)}
        <span className="builder-outline-handle" aria-hidden="true">{"\u2261"}</span>
      </SortableRun>
    );
  }
  return (
    <SortableRun id={`o:${runKey}`} surface="outline" runKey={runKey} disabled={false} reduced={reducedMotion} handle="whole"
                 className="builder-outline-pair">
      <ol>
        {run.map((i) => (
          <li key={i} className={sec?.exercises[i]?.supersetWithNext ? "builder-outline-exercise is-joined" : "builder-outline-exercise"}>
            {link(i)}
          </li>
        ))}
      </ol>
      <span className="builder-outline-handle" aria-hidden="true">{"\u2261"}</span>
    </SortableRun>
  );
}

function CustomiseSection({ section, index, onCancel, onUpdate }: {
  section: SectionDraft; index: number;
  onCancel: () => void; onUpdate: (next: SectionDraft) => void;
}) {
  const [draft, setDraft] = useState<SectionDraft>(section);
  const first = useRef<HTMLInputElement>(null);
  useEffect(() => { first.current?.focus(); }, []);

  return (
    /* 🔴 NOT `role="dialog"` — IT WAS ONE, AND THE CLAIM WAS FALSE TWICE OVER. This panel sits
       INSIDE the builder's own native `<dialog>`, which already carries the modal semantics; a
       second dialog role nested in the first tells a screen-reader user they have entered a new
       modal when they have not, and it cannot be dismissed the way a dialog is. A labelled
       `<section>` is a region: honest about what this is, and the platform supplies the role. */
    <section className="builder-section-customise" aria-label={`Customise ${sectionReference(section.name, index)}`}
         onKeyDown={(e) => { if (e.key === "Escape") { e.stopPropagation(); onCancel(); } }}>
      <div className="ht-field">
        <label className="ht-field-label" htmlFor={`cs-name-${index}`}>Section name</label>
        <input id={`cs-name-${index}`} ref={first} className="ht-input" value={draft.name ?? ""}
               placeholder="Name this section"
               onChange={(e) => setDraft({ ...draft, name: e.target.value || null })} />
      </div>

      <fieldset className="builder-format-tiles">
        <legend className="ht-field-label">Format</legend>
        {OFFERED_FORMATS.map((f) => (
          <button key={f} type="button"
                  className={f === draft.format ? "builder-format-tile is-on" : "builder-format-tile"}
                  aria-pressed={f === draft.format}
                  onClick={() => setDraft({
                    ...draft, format: f,
                    formatConfig: rehomeFormatConfig(draft.formatConfig, draft.format, f),
                  })}>
            <span className="builder-format-glyph" aria-hidden="true">{FORMAT_GLYPH[f]}</span>
            {FORMAT_LABEL[f]}
          </button>
        ))}
      </fieldset>
      {/* The caption that used to sit under the Format select — kept, because §8d's verdict was
          "KEEP AND STEAL THE CAPTIONS", and this is now the moment of choosing. */}
      <p className="ht-caption">{FORMAT_CAPTION[draft.format]}</p>

      {/* The format's own numbers, GENERATED from the one Swift list — they belong wherever the
          format is chosen, and that is here now. */}
      {formatFields(draft.format).length > 0 && (
        <div className="builder-format-fields">
          {formatFields(draft.format).map((field) => (
            <div className="ht-field builder-format-field" key={field.key}>
              <label className="ht-field-label" htmlFor={`cs-${field.key}-${index}`}>{field.label}</label>
              <span className="builder-format-input">
                <input id={`cs-${field.key}-${index}`} className="ht-input builder-cell" inputMode="numeric"
                       value={draft.formatConfig[field.key] ?? ""}
                       onChange={(e) => {
                         const raw = e.target.value.trim();
                         const next = { ...draft.formatConfig };
                         if (raw === "") delete next[field.key];
                         else {
                           const n = Number(raw);
                           // A typo is not a number: leave the previous value rather than write NaN.
                           if (!Number.isFinite(n)) return;
                           next[field.key] = Math.max(0, Math.round(n));
                         }
                         setDraft({ ...draft, formatConfig: next });
                       }} />
                <span className="ht-caption">{field.unit}</span>
              </span>
            </div>
          ))}
        </div>
      )}

      <div className="ht-field">
        <label className="ht-field-label" htmlFor={`cs-type-${index}`}>This block is</label>
        <select id={`cs-type-${index}`} className="ht-input" value={draft.sectionType}
                onChange={(e) => setDraft({ ...draft, sectionType: e.target.value as SectionType })}>
          <option value="workout">Main work</option>
          <option value="warm_up">Warm-up</option>
          <option value="cool_down">Cool-down</option>
          <option value="recovery">Recovery</option>
        </select>
      </div>

      <div className="builder-customise-actions">
        <button type="button" className="ht-button ht-button-quiet" onClick={onCancel}>Cancel</button>
        {/* ⚠️ UPDATING AN IMPLICIT SECTION MAKES IT REAL — §6.5's one deliberate TRUE -> FALSE. A
            coach who names it has made a section, and it must stop being swept when it empties. */}
        {/* 🔴 THE BORDERED TIER, NOT THE ACCENT — §13 allows exactly ONE `.ht-button-primary` per
            screen and this builder had two. The SAVE bar keeps it: saving the workout is the
            screen's one action, and a section's Update is a step on the way to it. Two accents on
            one screen is not a stronger call to action, it is the absence of one. */}
        <button type="button" className="ht-button"
                onClick={() => onUpdate({ ...draft, isImplicit: false })}>Update</button>
      </div>
    </section>
  );
}

/** One quiet glyph per format, for the tiles. Not icons from a set we do not have — characters. */
const FORMAT_GLYPH: Record<SectionFormat, string> = {
  straight_sets: "\u2261", superset: "\u26AD", circuit: "\u21BB", emom: "\u23F1",
  amrap: "\u221E", timed_interval: "\u23F2", for_time: "\u2193",
};

/* W23(e) — THE EXERCISE CARD'S `⋯`, AND IT HOLDS EXACTLY ONE KIND OF ITEM.
 *
 * `EVERFIT-MAP` §18-ter depth 3: theirs is fourteen items, and this copies ONE of them — the
 * computed `Move to <section>` tail. The other thirteen are either already on this card as
 * visible controls (`Delete` is the `Remove` button beside this menu; `Customize fields` is the
 * exercise editor the "records nothing" line links to), or are things we do not have
 * (`Add alternate exercises`, `Use %`), and §7's standing bound is that an absent concept stays
 * ABSENT rather than becoming a disabled row in a menu.
 *
 * ⚠️ **A MENU OF ONE KIND OF THING IS STILL A MENU, AND IT IS THE RIGHT SHAPE** rather than N
 * buttons on the card: a nine-section workout would otherwise put eight `Move to …` controls in
 * the header of every row.
 *
 * ⚠️ REAL BUTTONS IN A REAL LIST, like the section menu above it — their own work surface was
 * measured at ZERO focusable elements (§12), and a menu of `div`s would repeat exactly that.
 */
function MoveToMenu({ label, targets, paired, index, count, looseTo, onMove, onRemove, onMoveTo, onLoosen, onAddBelow, onDuplicate, viewHref }: {
  label: string; targets: { index: number; label: string }[]; paired: boolean;
  /** W95 — Everfit's `Duplicate` and `View exercise` (his 2026-09-29 stills): a copy below the row's run; the library row
   *  in a NEW TAB (the builder saves only on Save — leaving the window would drop the edits). */
  onDuplicate: () => void; viewHref: string;
  /** W72 step 3 — the keyboard's "drop a new exercise here" (his note 3's fallback). */
  onAddBelow: () => void;
  index: number; count: number;
  /** W28(e) — the boundary "out of the section" lands at, or `null` when it is not offered. */
  looseTo: number | null;
  onMove: (delta: -1 | 1) => void;
  onRemove: () => void;
  onMoveTo: (section: number) => void;
  onLoosen: (at: number) => void;
}) {
  const [open, setOpen] = useState(false);
  return (
    <div className="builder-section-menu-wrap builder-row-menu-wrap">
      <button type="button" className="builder-section-kebab"
              aria-expanded={open} aria-haspopup="menu"
              onClick={() => setOpen((o) => !o)}>
        <span aria-hidden="true">{"⋯"}</span>
        <span className="ht-visually-hidden">Actions for {label}</span>
      </button>
      {open && (
        <ul className="builder-section-menu" role="menu" onMouseLeave={() => setOpen(false)}>
          {/* 🔴 W72 STEP 3 — THE NON-MOUSE PATH FOR ADDING WHERE IT GOES. His note: no per-section "+ Add exercise",
              drag from the library to the place. A drag is an accelerator, never the only door (his 2026-08-29 rule),
              so this names the place and hands the caret to the library's search; the next pick lands below. */}
          <li role="none">
            <button role="menuitem" onClick={() => { setOpen(false); window.open(viewHref, "_blank", "noopener"); }}>View exercise</button>
          </li>
          <li role="none">
            <button role="menuitem" onClick={() => { setOpen(false); onDuplicate(); }}>Duplicate</button>
          </li>
          <li role="none" className="is-divided">
            <button role="menuitem" onClick={() => { setOpen(false); onAddBelow(); }}>Add exercise below…</button>
          </li>
          {/* 🔴 W32(8) — MOVE UP / MOVE DOWN LIVE HERE NOW, AND THAT IS WHAT MAKES DELETING THE
              ARROWS LEGITIMATE RATHER THAN A LOSS. Theirs has no arrows on a card and reorders by
              DRAG; ours must keep a pointer-free route, because dragging is not essential here
              (WCAG 2.5.7) and a touch coach gets no HTML5 drag at all. The arrows went; the
              capability did not move to a worse place — it moved into the menu beside the
              cross-section moves, which is where a coach already looks to relocate a row.
              ⚠️ DISABLED AT THE ENDS RATHER THAN HIDDEN, so the menu's shape does not change
              under the coach between one row and the next. */}
          <li role="none">
            <button role="menuitem" disabled={!canMove(count, index, -1)}
                    onClick={() => { setOpen(false); onMove(-1); }}>Move up</button>
          </li>
          <li role="none">
            {/* ⚠️ `canMove` IS ASKED, NOT RE-DERIVED. It is the tested rule in `builder.ts` and
                the deleted `MoveControls` asked it too; writing `index >= count - 1` here instead
                would be a second definition of "may this row move", two lines from a first — the
                producer-and-judge shape this repo's codex is named after. */}
            <button role="menuitem" disabled={!canMove(count, index, 1)}
                    onClick={() => { setOpen(false); onMove(1); }}>Move down</button>
          </li>
          {/* ⚠️ THE PAIR TRAVELS TOGETHER AND THE MENU SAYS SO **BEFORE** THE MOVE, not after.
              `moveExerciseTo` is handed the whole run, so a coach moving the upper half of a
              superset takes the lower half with it — correct, and surprising unless stated. */}
          {paired && (
            <li role="none">
              <p className="ht-caption builder-row-menu-note">Moves the linked pair together.</p>
            </li>
          )}
          {targets.map((t) => (
            <li role="none" key={t.index}>
              <button role="menuitem" onClick={() => { setOpen(false); onMoveTo(t.index); }}>
                {/* The arrow is theirs and is hidden from the reader — "Move to" already says
                    the direction, and an arrow read aloud after it is the same fact twice. */}
                Move to {t.label}<span aria-hidden="true">{" \u2192"}</span>
              </button>
            </li>
          ))}
          {/* 🔴 W28(e) — OUT OF EVERY SECTION, WHICH UNTIL NOW ONLY A DRAG COULD DO. §6.5 is his
              own feature (*"why not have that freedom?"*) and MEASURED 2026-09-21 every caller of
              `dropLooseAt` for an existing row was a drag handler on an `aria-hidden` line — so
              the one thing this builder has that theirs has not got was the one thing a keyboard
              or a touch coach could not reach. Same argument as W32(8)'s arrows, one gesture on.
              ⚠️ LAST OF THE DESTINATIONS, because it IS one: the family reads `Move to …` and
              then the way out of the family. `Remove` stays below it and apart.
              ⚠️ NO ORDINAL AND NO SECTION NAME. `describeAddTarget`'s ordinal exists because a
              DESTINATION among many is ambiguous; a SOURCE cannot be — a row is in exactly one
              section, drawn around it — so naming it here would be copying a rule past the
              reason it was written for, and printing what the card already sits inside. */}
          {looseTo !== null && (
            <li role="none">
              <button role="menuitem" onClick={() => { setOpen(false); onLoosen(looseTo); }}>
                Move out of the section<span aria-hidden="true">{" \u2192"}</span>
              </button>
            </li>
          )}
          {/* ⚠️ REMOVE LAST AND SET APART. It used to be a standing button in the row's header,
              one pointer-slip from the arrows it sat beside; in a menu it costs a deliberate
              opening first. Same verb, same act, a gesture further from an accident. */}
          <li role="none">
            <button role="menuitem" className="builder-row-menu-destructive"
                    onClick={() => { setOpen(false); onRemove(); }}>Remove</button>
          </li>
        </ul>
      )}
    </div>
  );
}

/* THE LINK MARK — the superset toggle, between the two cards it joins (§6 adoption 4).
 *
 * ⚠️ A REAL BUTTON WITH `aria-pressed`, NOT A DECORATIVE GLYPH WITH A CLICK ON IT. It replaces a
 * checkbox that read "Superset with the next exercise", and a screen reader must still be told
 * exactly that — so the accessible name NAMES BOTH EXERCISES rather than saying "link", which
 * would be meaningless in a list of eleven of them.
 *
 * ⚠️ The unequal-pair rule (DECIDED 2026-08-24) is NOT enforced here and that is deliberate: it
 * belongs to the model (`levelRunDown`), and a toggle that silently refused to join two rows
 * because their set counts differ would be a rule the coach cannot see, taught by a control that
 * does nothing. */
/* THE ROW'S GRIP — one component for a card's own grip and a superset's shared one (W88), so the pick-up rule below is
   written once. 🔴 W80: the pick-up is said ONE TICK after `dragstart`, never inside it (the engines refuse a drag whose
   source moved out from under the pointer, and announcing the pick-up draws drop slots above the row). */
function RowGrip({ className, label }: { className?: string; label: string }) {
  /* 🔴 W96 — THE GRIP IS THE RUN'S ACTIVATOR (`SortableRun` hands it down): a mouse picks the run up here after a 4 px move
     (a bare click does nothing), the keyboard sensor answers Space / Enter on it, and it takes no touch scrolling
     (`touch-action: none`) — a finger holds 150 ms anywhere on the card instead. What follows the pointer is the run's
     own look (`DragOverlay`), so the grip no longer paints a drag image of its own. */
  const grip = useGrip();
  return (
    <span className={className === undefined ? "wb-grip" : `wb-grip ${className}`} title="Drag to move"
          aria-label={`Move ${label}`} ref={grip?.ref} {...grip?.attributes} {...grip?.listeners}>
      <Icon name="grip" size={14} stroke={2.4} />
    </span>
  );
}

function JoinMark({ joined, upper, lower, onToggle, grip, showToggle }: {
  joined: boolean; upper: string; lower: string; onToggle: () => void;
  /** False while a row is in flight: the gap is a drop slot then (the seam stays only to keep a run's grip mounted). */
  showToggle: boolean;
  /** W88 — an EVEN superset's one grip, drawn on this seam (the seam between its two middle cards); null elsewhere. */
  grip: ReactNode | null;
}) {
  return (
    <li className="builder-join">
      {grip}
      {/* §6.6 item 6: hovering theirs reads "Link as Superset" / "Unlink Superset" — the verb, because a chain glyph
          alone does not say what clicking it will do. Ours says it as the control's OWN label beside the glyph (W72
          step 7), never a tooltip.
          ⚠️ THE SAME SENTENCE THE SCREEN READER ALREADY GOT. It was in `ht-visually-hidden` and
          nowhere else, so a keyboard user was told what the button does and a MOUSE user was not
          — the inverse of the usual omission, and just as much a gap. One string, both audiences. */}
      {/* 🔴 A CHAIN LINK IN TWO STATES, AND HIS TWO SCREENSHOTS ARE THE SPEC (2026-09-21):
          *"you see the superset icons and hover messages in everfit? make ours like so."*
          Unlinked is a chain BROKEN in the middle; linked is the two halves interlocked. They were
          `\u26AD` and `\u26AF` — MARRIAGE and DIVORCE, typographic symbols that carry the right
          idea and the wrong picture, and whose shape is whatever the installed font decides. Drawn
          here so both states are one stroke weight and one size on every machine.
          ⚠️ THE VISIBLE TOOLTIP IS SHORT AND THE ACCESSIBLE NAME IS NOT, WHICH IS DELIBERATE.
          Theirs reads "Link as Superset" / "Unlink Superset" — the VERB, which is all a coach
          looking at the two cards needs, and his standing rule is the shortest form that keeps the
          meaning. But a screen-reader user tabbing through twenty of these hears only the button,
          never the pair, so `ht-visually-hidden` keeps the sentence that NAMES the two exercises.
          One control, two audiences, two lengths — the previous version gave both audiences the
          long one and the mouse got it through a native `title`, which is unstyled and slow. */}
      {showToggle && (
      <button type="button"
              className={joined ? "builder-join-button is-joined" : "builder-join-button"}
              aria-pressed={joined}
              onClick={onToggle}>
        <svg className="builder-join-glyph" viewBox="0 0 24 12" aria-hidden="true"
             focusable="false" fill="none" stroke="currentColor" strokeWidth="1.6"
             strokeLinecap="round">
          {joined
            /* INTERLOCKED: two links that overlap across the middle. */
            ? <>
                <path d="M10.5 6 h3" />
                <path d="M9.5 2.2 H6.2 a3.8 3.8 0 0 0 0 7.6 H9.5" />
                <path d="M14.5 2.2 h3.3 a3.8 3.8 0 0 1 0 7.6 H14.5" />
              </>
            /* BROKEN: the same two links, pulled apart, each open end facing the gap. */
            : <>
                <path d="M9 2.2 H6.2 a3.8 3.8 0 0 0 0 7.6 H9" />
                <path d="M15 2.2 h2.8 a3.8 3.8 0 0 1 0 7.6 H15" />
              </>}
        </svg>
        <span className="ht-visually-hidden">
          {joined ? `Separate ${upper} from ${lower}` : `Superset ${upper} with ${lower}`}
        </span>
        {/* W72 step 7 — THE CONTROL'S OWN LABEL, NOT A TOOLTIP (Proposal 7 rule 10: dark tips with tails are theirs).
            It shows beside the glyph on hover or focus, in ink, with no box; `aria-hidden` because the sentence above
            already names the pair for a screen reader. */}
        <span className="builder-join-label" aria-hidden="true">{joined ? "Unlink superset" : "Link as superset"}</span>
      </button>
      )}
    </li>
  );
}

/* ── The exercise row — one line by default, the table on request ─────────────────────────── */

function ExerciseRow({ exercise, media, domId, format, joinedAbove, joinedRun, gripShown, index, count, moveTo, looseTo, pairedRun, onMoveTo, onLoosen, onMove, onChange, onRemove, ruleTag, ruleLine, steps, onAddBelow, onDuplicate, viewHref }: {
  /** W95 — the ⋯'s Duplicate and View exercise (see `MoveToMenu`). */
  onDuplicate: () => void; viewHref: string;
  /** W72 step 3 — `Rule · rep ladder`, or null (no tag; the workout's line counts the rows without one). */
  ruleTag: { text: string; lacks: string | null } | null;
  /** F15 step 3 (workout-based) — drawn in place of `ruleTag` when given. */
  ruleLine?: ReactNode;
  /** F15 step 4.1a — the rule tabs: drawn in place of `ruleLine`, and the card shows and edits the chosen step's half. */
  steps?: RowStepsInput | null;
  onAddBelow: () => void;
  /** W23(e) — every section this row could go to, the one it is in excluded. EMPTY means there
   *  is nowhere else, and the menu is then not drawn at all. */
  moveTo: { index: number; label: string }[];
  /** W28(e) — where "out of the section" puts this row, or `null` when it is already loose. */
  looseTo: number | null;
  onLoosen: (at: number) => void;
  /** True when this card heads, or belongs to, a linked run — so the menu can say that the pair
   *  travels together rather than letting a coach discover it afterwards. */
  pairedRun: boolean;
  onMoveTo: (section: number) => void;
  /* ⚠️ THE PICTURE IS NOT PART OF THE DRAFT, AND THAT IS THE WHOLE POINT. `ExerciseDraft` is what
     the coach TYPES; a demo video is a fact about the LIBRARY row, exactly as the exercise
     editor keeps `videoSource` out of its own draft. Putting it in the draft would thread two
     read-only fields through `planWrites` and the round trip §6 rules on — a write path widened
     for a thumbnail. So it is looked up by `libraryExerciseId` and passed in. */
  media: RowMedia;
  /** "no rule", or null when this row's exercise HAS one — or when the library has not loaded,
   *  which is a different thing from being fine and is why this is null rather than false. */
  index: number; count: number; onMove: (delta: -1 | 1) => void;
  exercise: ExerciseDraft; format: SectionFormat;
  /** Drawn joined to the card above. ⚠️ `canJoin` is GONE with the checkbox it gated: whether a
   *  pair CAN be made is a question about two neighbours, which only the section knows, and it is
   *  asked where the mark is drawn. */
  /** The outline scrolls to this. Passed rather than derived here, because the SECTION owns both
   *  indices and the outline lists the same pair — one source for the id, not two spellings. */
  domId: string;
  joinedAbove: boolean; joinedRun: boolean;
  /** W88 — whether THIS card draws the grip: a loose card, or the middle card of an odd superset. No default. */
  gripShown: boolean;
  onChange: (e: ExerciseDraft) => void; onRemove: () => void;
}) {
  /* 🔴 F15 4.1a — THE CARD IS THE EDITOR: on a step's Target or Action the card draws THAT half (`card`) and its edits go to
     the step (`change`); on Now, or with the tabs hidden, it is the row as written, exactly as before. */
  const lens = useRowSteps(steps ?? null, exercise);
  const card = lens?.view ?? exercise;
  const change = lens?.onChange ?? onChange;
  /* A half the card SHOWS and cannot edit (a change of exercise, a pause, a resume, a preset): its body is `inert` — drawn,
     never focused or clicked. An attribute, never a wrapper: the card's grid places its children by `>` (direction.css). */
  const frozen = (lens?.frozen === true ? { inert: "" } : {}) as Record<string, string>;
  const columns = columnsFor(card.trackingFields, format);
  /* 🔴 §6 ADOPTION 2 — THE TABLE IS THE DEFAULT, NOT A DISCLOSURE. The collapsed
     "Sets / Reps / Rest + Vary by set" line is GONE, with `expanded`, `setUniform` and `setCount`.
     The reason is the model, not the look: `coach_workout_sets` has always held ONE ROW PER SET,
     so the summary was the screen describing the data rather than showing it, and every edit
     through it rewrote every set (`expandUniform`) to keep the fiction that they were one thing.
     ⚠️ NOTHING IS LOST BY DELETING IT: the 94.7%-uniform finding still holds and a uniform
     exercise simply draws N identical rows, which is what it IS. What IS lost is the levelling
     edit — and that was the risk, not the feature. */
  const recordsNothing = card.trackingFields.length === 0;
  /* 🔴 W95 — EACH SIDE, TEMPO AND THE NOTE ARE FIXED (his words, 2026-09-29: "need to be there in each exercise added
     anyway … not needed to have a done. they'll be there anyways, fixed"). This REVERSES Fix A's level two (a ⋯ item,
     edit tags and a `Done`), which Fable made to keep a 20-row workout short; his word is the later one. They sit in the
     column under the name, which was empty beside the set table (MEASURED), so the card barely grows. */

  return (
    /* 🔴 HIS WORD, TWICE, 2026-09-21: *"you see how containers of 2 exercises connect when they're
       superset?"* and then *"why not still fix the superset as i said? the 2 exercises containers
       that we make superset should join."*
       ⚠️ `joinedAbove` ALREADY EXISTED AND ONLY NUDGED THE MARGIN — the two cards still read as two
       objects with a glyph between them. A LINKED RUN IS ONE OBJECT: the upper card loses its
       lower corners, the lower card loses its upper ones, and the seam between them is a hairline
       rather than a gap. `joinedRun` says "this card is part of a run" and `joinedAbove` says
       "…and it is not the first", which is exactly the pair of facts the two halves of the shape
       need — so nothing new is derived and the two cannot disagree. */
    <li id={domId}
        className={[
          "builder-exercise",
          joinedRun ? "is-run" : "",
          joinedAbove ? "builder-exercise-joined is-run-lower" : "",
          lens?.paused === true ? "is-step-paused" : "",
        ].filter(Boolean).join(" ")}
>
      {/* 🔴 A SUPERSET SAYS SO, ONCE, AT ITS TOP — William's first look at the builder (2026-09-28, relayed by him):
          *"it's also hard to see if its a superset or not, it's not super clear."* MEASURED on a still: a linked pair
          differed from two loose cards only by a smaller gap and the chain's shape, both drawn in the same accent. The
          2026-09-21 removal of the word was about a word BESIDE EVERY NAME repeating a drawing; this is one label per
          RUN, on the run's first card, with the accent rail down the run's edge (CSS `.is-run`) — the run is now a
          marked object, and the loose chain between two unlinked cards is quiet grey until hovered. */}
      {joinedRun && !joinedAbove && <p className="wb-run-label">Superset</p>}
      {/* 🔴 THE `⌐` MARK IS GONE — his word, 2026-09-21: *"why when superset the bottom exercise
          has this red thing left of its video?"* It was a bracket drawn beside the lower card to
          say "this one belongs to the one above", from the days when the two cards were two
          separate boxes and nothing else said so. The cards are ONE box now, so the mark is a
          glyph repeating a drawing — the same objection he made to the ` superset` word beside
          the name an hour earlier, and to the section counts on 2026-09-19. */}

      <div className="builder-exercise-head">
        {/* 🔴 W72 STEP 3 — THE GRIP. The arrangement panel is gone, so the ROW is what drags: picked up here, dropped on
            any slot between rows or sections. The ⋯ keeps Move up / down / to for every coach without a pointer. */}
        {/* 🔴 W80 — THE PICK-UP IS SAID ONE TICK AFTER `dragstart`, NEVER INSIDE IT. His report, 2026-09-25 ~19:00:
            dragging a row in the main area did nothing. MEASURED in Chrome (a real drag in the pane): `dragstart` on the
            grip, then `dragend` at once — no `dragenter`, no `dragover`. The cause is the engines' own rule, in both
            Chromium's and WebKit's `DragController::StartDrag`: after `dragstart` they hit-test the drag's origin and
            refuse to start a drag whose source *"isn't under the drag origin anymore… moved out from under the
            cursor"*. `onGripStart` flips the page into dragging, which draws drop slots ABOVE this row and takes the
            link marks away — so the 12 × 14 px grip was pushed out from under the pointer inside `dragstart` itself.
            The library and the Arrange panel never move their own source, which is why they drag and this did not.
            So the data is set here (it must be), and the page learns of the pick-up after the browser has started the
            drag; a drag that ends before that tick never says it started. */}
        {/* W88 — A SUPERSET HAS ONE GRIP (his word, 2026-09-28): only a loose card, or the MIDDLE card of an odd run, draws
            its own; an even run's grip sits on the seam between its two middle cards (`JoinMark`). Any card's grip already
            lifted the whole run (`runAt`), so this is one handle for one object, never a behaviour change. */}
        {gripShown && <RowGrip label={exercise.name} />}
        {/* §6 adoption 1: every card in the centre carries its video container, the same
            `VideoThumb` the shelf and the picker draw. A coach recognises a movement by sight,
            and this is the surface where they are deciding what the session IS. */}
        {/* 🔴 PENDING DRAWS PLAIN SURFACE AND SAYS NOTHING (R18). The no-video glyph is an
            assertion, and while the library is in flight the screen has not got the fact to
            assert. The tile keeps its size so nothing moves when the answer arrives. */}
        {media.state === "pending"
          ? <span className="ht-thumb builder-thumb-pending" aria-hidden="true" />
          : <VideoThumb demoVideoUrl={media.state === "found" ? media.demoVideoUrl : null}
                        thumbnailUrl={media.state === "found" ? media.thumbnailUrl : null} />}
        {/* 🔴 THE NAME IS THE CARD'S TITLE, NOT A CONTROL — his word, 2026-09-29: *"NO Folded exercises at all!"* W88's
            one-line row (a button opening the table) and W93's name-tap fold are both gone; every card is open. */}
        <span className="builder-exercise-name ht-body-medium">
          {lens?.title ?? card.name}
          {/* 🔴 TWO TAGS REMOVED ON HIS WORD, 2026-09-21: *"no superset text please next to the
              exercise titles or anywhere. and what's this no rule text in all titles of
              exercises??? remove!"*
              · THE `superset` TAG SAID WHAT THE PICTURE ALREADY SAYS. The two cards are joined by
                the link mark between them and, since W43, by a shared container — a word repeating
                a drawing is the same objection he made to the section counts on 2026-09-19.
              · THE `NO RULE` BADGE WAS ON A MAJORITY OF ROWS, WHICH IS WHY IT READ AS NOISE RATHER
                THAN AS A WARNING: this fixture alone says "15 of 20 rows carry no progression
                rule". A badge on three rows in twenty is a flag; a badge on fifteen is wallpaper,
                and it sat inside the one string a coach reads to find the exercise.
              ⚠️ THE FACT IS NOT LOST, ONLY THE BADGE. The workout's header still carries
              `missingRuleSentence` — one line, once, counting them — which is where a count
              belongs. `rowRuleBadge` keeps its callers there; nothing about the data changed. */}
        </span>
        {/* 🔴 W23(e) — `Move to <section>`, ONE ITEM PER SECTION, AND IT IS THE ONLY WAY TO MOVE
            A ROW BETWEEN SECTIONS WITHOUT A POINTER. `EVERFIT-MAP` §18-ter depth 3: their card's
            `⋯` ends with one `Move to …` per section in the workout. ⚠️ W32(8) folded the
            WITHIN-section move into this same menu — it used to be a pair of hover arrows
            (`MoveControls`, now deleted) — so one control carries both kinds of move, which is
            also the only pointer-free route to either. A cross-section move has only ever been a
            drag on both products; theirs has no drag handle on an exercise card at all
            (§18-ter depth 4) and ZERO focusable elements on that surface (§12). */}
        {/* ⚠️ ALWAYS DRAWN NOW. It used to appear only when there was somewhere to move TO, so a
            one-section workout got no `⋯` at all — which was right when the menu held nothing but
            cross-section moves and is wrong now that it holds this row's move and its removal. */}
        <MoveToMenu label={exercise.name} targets={moveTo} onMoveTo={onMoveTo} paired={pairedRun}
                    index={index} count={count} onMove={onMove} onRemove={onRemove}
                    looseTo={looseTo} onLoosen={onLoosen} onAddBelow={onAddBelow}
                    onDuplicate={onDuplicate} viewHref={viewHref} />
      </div>
      <div className="builder-qualifiers wb-tags">
        {/* 🔴 W72 STEP 3 — THE ROW'S TAGS ARE ITS OWN EDITORS (Proposal 3): the rule by name, Each side as a toggle,
            Tempo as its four-slot field, the warm-ups as a count. No `No rule` tag — his 2026-09-21 word removed it
            from every title (15 of 20 rows made it wallpaper); the workout's own line counts those. */}
        {/* W73 — the rule as the sentence its numbers make, a CAPTION LINE under the name (Fable, judging step 5: a
            tag box wrapping five lines breaks rule 7); what the config never stated follows in the accent. */}
        {lens != null ? lens.bar : ruleLine !== undefined ? ruleLine : ruleTag !== null && (
          <span className="wb-rule-line">
            {ruleTag.text}
            {ruleTag.lacks !== null && <span className="wb-rule-lacks"> — {ruleTag.lacks}</span>}
          </span>
        )}
        {/* His 11-01 word: no `N warm-up sets` chip — the table's W rows already say it. */}
      </div>

      {lens?.table ?? <SetTable exercise={card} columns={columns} onChange={change} id={`${domId}-sets`}
                                target={lens?.part === "target"} frozen={lens?.frozen === true} />}

      {/* 🔴 AN EMPTY `tracking_fields` IS A ROW THE PHONE CANNOT LOG, AND THE BUILDER SAYS SO
          RATHER THAN PAPERING OVER IT (§6 adoption 2, the 09-18 finding). `columnsFor` already
          falls back to Reps alone — it has never invented a Weight column and must not start —
          but a fallback nobody can see is a DEFAULT, so the card names it and points at the one
          screen that can fix it. */}
      {recordsNothing && (
        <p className="ht-caption builder-records-nothing">
          This exercise records nothing yet —{" "}
          <a className="ht-link" href={href({ name: "exercise", exerciseId: card.libraryExerciseId })}>
            set what a set records
          </a>.
        </p>
      )}

      {/* §6 adoption 2 — `Each side` and `Tempo` sit UNDER THE TABLE, on its face. They were behind
          `More` when the table itself was behind "Vary by set"; with the table now the default,
          the two fields that qualify every row in it belong beside it. ⚠️ The NOTE and the
          superset box stay disclosed: the note is prose, not a set property, and `More` keeps its
          job for those. `each_side` is on 13% of real rows and tempo on 6% — real, and still not
          what a coach touches on nine rows in ten, so they are QUIET rather than prominent. */}
      {/* ONE FOOTER ROW — qualifiers left, "Add set" right, on a line of their own. They were two
          blocks stacked, which is what made the card read as a form rather than a row of work. */}
      {lens?.table == null && <div className="builder-exercise-foot" {...frozen}>
      {/* 🔴 HIS ASK, 2026-09-19, WITH FOUR SCREENSHOTS OF THEIRS: ONE SPLIT CONTROL replaces the
          `Add set` button AND the `+N ▾` select that sat beside it. `Add Set` adds one; the `+`
          opens `+ 2 Sets … + 8 Sets`. The ceiling is his own sentence — *"everfit has up to 8
          sets. they're more than enough."*
          ⚠️ THE `<select>` GOES, AND IT WAS THE SAME IDEA IN THE WRONG GRAMMAR: a combo box whose
          chosen value is thrown away on the next line announces a current value it does not have.
          ⚠️ A NEW SET COPIES THE LAST ONE — unchanged, and it is `Add set`'s own rule applied N
          times, NOT `expandUniform`, which REWRITES every existing row to match a template and
          would silently flatten a ramp the coach had already typed (2 warm-ups at 40 kg then 3
          working at 60 would become five identical rows). */}
      {/* FIX A: ONE `+ set` at the grid's foot — a new set copies the last one, unchanged (never `expandUniform`, which would
          flatten a ramp). The split `Add Set | +` pair stays on the client page's set window, where it was asked for. */}
      {/* 🔴 W95 (his word, with Everfit's card): Each side and Tempo, FIXED, on the left of the foot; `Add set | +` on the
          right — `Add set` adds ONE, the `+` adds 2…8 at once, each a copy of the last set (never `expandUniform`). */}
      {/* F15 4.1a: a TARGET is the working sets' numbers — each side and tempo are not targets: they stay drawn (W95: on every
          card), the row as the tab before leaves it, and inert. */}
      <div className="wb-qualifiers" {...(lens?.part === "target" ? { inert: "" } : {})}>
          <label className="builder-check">
            <input type="checkbox" checked={card.eachSide}
                   onChange={(e) => change({ ...card, eachSide: e.target.checked })} />
            {" "}Each side
          </label>
          {/* No clock switch (his word 2026-10-15: "don't make this a choice for anybody"): a timed set's TYPE decides
              its clock on the phone — Failure counts up, every other type counts down. The 10-07 Balance box is gone;
              the row still carries `holdUntilLost` through load and save, unread by the player. */}
          <label className="builder-tempo">
            <span className="ht-caption">Tempo</span>
            {/* 🔴 W46 — HIS TEMPO RULE, AND THE PHONE ALREADY HAD IT. His words: *"when we click
                tempo we should see: X-X-X-X and cursor be in the first one. we type, then the - is
                automatically there, and we are in the second digit and so on. and from letters only
                the X is allowed to use. when we typed all the 4 digits then we should not be able to
                type more. only to delete 1 by one."*
                ⚠️ MEASURED BEFORE THIS: the field was a bare `<input>` that accepted ABSOLUTELY
                ANYTHING — any length, any character, no mask, no auto-dash, no slots. The phone's
                `TempoKeypadSheet` meanwhile enforced every clause of that sentence already, so the
                rule was EXTRACTED (`TempoEntry`/`tempoEntry.ts`) rather than written twice: one
                `tempo` column with two producers and nothing comparing them is vocabulary mismatch
                #25 by construction. A generated table drives both.
                ⚠️ TYPING IS HANDLED ON `keydown`, NOT `change`, because the dash is DRAWN and never
                typed: an `onChange` mask has to work out what the browser did to the caret after the
                fact, which is the class of bug this whole item is about. Here a key is either
                accepted into the next slot or refused outright. */}
            <input className="ht-input builder-cell" value={card.tempo ?? ""}
                   placeholder={"X-X-X-X"} aria-label="Tempo, four slots — digits or X"
                   /* ⚠️ NO `inputMode="numeric"`, AND THAT IS THE PHONE'S OWN LESSON. `X` is a legal
                      value here ("explosive"), and a numeric input mode shows a keypad that HAS no
                      X — `TempoKeypadSheet` draws its own X key for exactly that reason, because
                      iOS gives no way to add one to the system number pad. Asking for the numeric
                      keyboard would make a rule the keyboard cannot obey, on the one surface where
                      the coach cannot work around it. */
                   onKeyDown={(e) => {
                     if (e.key === "Backspace") {
                       e.preventDefault();
                       const next = tempoDeleting(card.tempo ?? "");
                       change({ ...card, tempo: next === "" ? null : next });
                       return;
                     }
                     if (e.key.length !== 1 || e.metaKey || e.ctrlKey || e.altKey) return;
                     e.preventDefault();
                     const next = tempoTyping(card.tempo ?? "", e.key);
                     if (next !== null) change({ ...card, tempo: next });
                   }}
                   /* The field is driven by keydown, so `onChange` only ever fires for a paste or an
                      autofill. Anything the rule refuses is dropped rather than half-applied. */
                   onChange={(e) => {
                     const v = e.target.value;
                     if (tempoAccepts(v)) change({ ...card, tempo: v || null });
                   }}
                   /* HIS PADDING RULE, on the way OUT: `3-1` stored means `3-1-0-0`, which is what
                      the phone has always saved and what the engine's reader expects. */
                   onBlur={() => {
                     const full = tempoStored(card.tempo ?? "");
                     if (full !== (card.tempo ?? null)) change({ ...card, tempo: full });
                   }} />
          </label>
      </div>
      <AddSetSplit className="wb-addset" onAdd={(n) => {
        const last = card.sets[card.sets.length - 1] ?? emptySet();
        change({ ...card, sets: [...card.sets, ...Array.from({ length: n }, () => ({ ...last }))] });
      }} />
      </div>}

      {/* 🔴 HIS WORD, 2026-09-21, WITH THEIR CARD BESIDE OURS: *"fix this 'Add note for this
          exercise' part with the hint instead of our 'more' part. just as everfit looks, not
          expandable, just text field."*
          ⚠️ MEASURED BEFORE REMOVING THE DISCLOSURE, because a `<details>` usually hides more than
          one thing and deleting one is how a control goes missing: this one held the NOTE AND
          NOTHING ELSE. Its only other occupant, the "superset with the next exercise" checkbox,
          left in §6 adoption 4 when the link mark between the cards became the toggle. So there is
          nothing behind the summary to lose.
          ⚠️ AND THE LABEL BECOMES THE PLACEHOLDER, WHICH IS A TRADE RATHER THAN A FREE WIN: a
          placeholder disappears as soon as somebody types, so a coach returning to a written note
          no longer sees the words "note for this exercise". It is the right trade HERE — the field
          is the last thing on the card, it is prose, and a filled note says what it is by being
          prose — and it is stated so nobody has to re-derive it. `aria-label` carries the name for
          anybody who cannot see the placeholder at all. */}
      {/* W95: the note is ALWAYS the field on every exercise — empty, with its hint (his word, 2026-09-29). */}
      <textarea className="ht-input builder-note" rows={1} {...frozen}
                placeholder={lens?.notePlaceholder ?? "Add note for this exercise"}
                aria-label={`Note for ${exercise.name}`}
                value={card.note ?? ""}
                onChange={(e) => change({ ...card, note: e.target.value || null })} />
    </li>
  );
}

/* ── The per-set table — Everfit's default, and our disclosure ────────────────────────────── */

/* ⚠️ "Add set" LEFT THIS COMPONENT for the card's footer row, where it sits opposite the
   qualifiers on one line. `onCollapse` went with the collapsed line in step 2 — it has been
   `null` at the only call site since, so the prop is gone rather than left as a parameter nothing
   can pass. */
function SetTable({ exercise, columns, onChange, id, target = false, frozen = false }: {
  exercise: ExerciseDraft; columns: TrackingField[]; id: string;
  onChange: (e: ExerciseDraft) => void;
  /** F15 4.1a — a step's TARGET: its working sets' numbers only — no rest, no set type (a target set is a working set). */
  target?: boolean;
  /** F15 4.1a — shown, not editable (`inert`): a step's half the table cannot state. */
  frozen?: boolean;
}) {
  const setSet = (i: number, patch: Partial<SetDraft>) =>
    onChange({ ...exercise, sets: exercise.sets.map((s, j) => (j === i ? { ...s, ...patch } : s)) });

  return (
    <div className="builder-table-wrap" id={id} {...(frozen ? { inert: "" } : {})}>
      <table className="builder-table">
        <thead>
          <tr>
            {/* 🔴 THE REMOVE SITS AT THE LEFT OF THE SET ROW — HIS OBSERVATION, 2026-09-18 ~23:50:
                *"in the left side of each set if you see the photos I sent you"*. It was on the
                right here, which is the iOS habit (a trailing destructive edge), and on a set
                TABLE his reading is better: the set number and the × are the row's identity and
                its removal, and putting them together means the eye crosses the numbers once
                rather than twice.
                🔴 AND IT IS HOVER-ONLY AFTER ALL — HIS WORD, 2026-09-19: *"the X left of each
                set need to show ONLY when we hover on a set's row. You would find out eventually
                digging Everfit but I just say it now"*. This comment previously argued the
                opposite (a × dimmed to 0.25 at rest) on a touch-reachability ground; that ground
                is answered by the `@media (hover: none)` rule in `direction.css` rather than by a
                ghost glyph on every row. `:focus-within` still brings it up for a keyboard, and
                the cell keeps its width so nothing reflows under the pointer. */}
            <th scope="col"><span className="ht-visually-hidden">Remove</span></th>
            <th scope="col">Set</th>
            {columns.map((c) => <th key={c} scope="col">{COLUMN_LABEL[c]}</th>)}
            {!target && <th scope="col">Rest</th>}
          </tr>
        </thead>
        <tbody>
          {exercise.sets.map((s, i) => (
            <tr key={i}>
              <td className="builder-set-remove">
                <button className="ht-button ht-button-quiet" aria-label={`Remove set ${i + 1}`}
                        onClick={() => onChange({ ...exercise, sets: exercise.sets.filter((_, j) => j !== i) })}>
                  ×
                </button>
              </td>
              {/* 🔴 THE TYPE LIVES ON THE SET-NUMBER CELL — §8-ter, read live 2026-09-19. It was a
                  trailing `Type` COLUMN, which cost every table a column for ever AND said the
                  same fact twice: the row header already prints `W` / `1` / `F`, and the select
                  beside it printed the word for the same value. Theirs has no such column; the
                  number IS the control. */}
              <th scope="row" className="builder-set-type-cell">
                {target ? <span className="builder-set-type-face">{i + 1}</span>
                  : <SetTypeControl sets={exercise.sets} index={i} onPick={(type) => setSet(i, { type })} />}
              </th>
              {columns.map((c) => (
                <td key={c}>
                  <MeasureField column={c} set={s} bare onChange={(patch) => setSet(i, patch)} />
                </td>
              ))}
              {!target && <td><RestField bare seconds={s.restSeconds} onChange={(v) => setSet(i, { restSeconds: v })} /></td>}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* 🔴 THE SET-NUMBER CELL IS THE TYPE CONTROL — their shape (`docs/EVERFIT-MAP.md` §8-ter, read
 * live 2026-09-19: clicking the number opens `R Regular · W Warm up · D Drop set · F Failure`),
 * with our semantics underneath.
 *
 * ⚠️ WHY A MENU BUTTON AND NOT THE `<select>` THAT WAS HERE. A `<select>` shows its OPTION's text
 * when closed, so putting one on this cell would print `Warm-up` where the table needs `W` — and
 * printing `W` as the option text would take the WORDS out of the menu, which is where they earn
 * their place. A menu button separates the two: the letter on the face, the words in the list.
 *
 * ⚠️ AND IT IS A REAL MENU, WHICH IS THE HALF THEIRS HAS NOT GOT. §12 and §8-ter measured their
 * builder at zero focusable elements and their set-number cell as a NAMELESS `button`; this one
 * is a `button` with `aria-haspopup`, `aria-expanded`, `role="menuitemradio"` per option and an
 * accessible name that says what the letter MEANS (`Set 2 · Working`) — a screen reader hearing
 * "W" alone learns nothing.
 *
 * ⚠️ THE ORDER RULE IS NOT RE-STATED HERE. `allowedSetTypes` is the one definition (a warm-up may
 * not follow a working set, and a working set may not be inserted before a later warm-up); this
 * disables those items and prints `SET_ORDER_RULE` as the reason, rather than hiding them — an
 * item that vanishes teaches nothing about why. */
function SetTypeControl({ sets, index, onPick }: {
  sets: SetDraft[]; index: number; onPick: (t: SetType) => void;
}) {
  const [open, setOpen] = useState(false);
  const s = sets[index];
  if (s === undefined) return null;
  const allowed = allowedSetTypes(sets, index);
  return (
    <span className="builder-set-type">
      <button type="button" className="builder-set-type-face"
              aria-haspopup="menu" aria-expanded={open}
              onClick={() => setOpen((o) => !o)}>
        <span aria-hidden="true">{setLabel(sets, index)}</span>
        <span className="ht-visually-hidden">
          {`Set ${index + 1} · ${SET_TYPE_LABEL[s.type]} — change the set type`}
        </span>
      </button>
      {open && (
        <ul className="builder-set-type-menu" role="menu" onMouseLeave={() => setOpen(false)}>
          {(Object.keys(SET_TYPE_LABEL) as SetType[]).map((t) => {
            const can = allowed.includes(t);
            return (
              <li key={t} role="none">
                <button role="menuitemradio" aria-checked={s.type === t} disabled={!can}
                        title={can ? undefined : SET_ORDER_RULE}
                        onClick={() => { setOpen(false); onPick(t); }}>
                  <span aria-hidden="true" className={`builder-set-type-letter is-${t}`}>
                    {SET_TYPE_LETTER[t]}
                  </span>
                  {SET_TYPE_LABEL[t]}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </span>
  );
}

const COLUMN_LABEL: Record<TrackingField, string> = {
  reps: "Reps", weight: "KG", time: "Time", rounds: "Rounds",
  distance_m: "M", distance_km: "KM",   // W136 B2 (design §5): the header IS the unit, as KG is
};

/* ── Fields ───────────────────────────────────────────────────────────────────────────────── */

/* ⚠️ `NumberField` WENT WITH THE COLLAPSED LINE (§6 adoption 2) — it drew that line's "Sets" box
   and nothing else ever used it. ITS ONE RULE IS KEPT HERE RATHER THAN DELETED WITH IT, because
   the rule outlived the component: A TYPO IS NOT A NUMBER. It returned early on a non-finite
   parse so the previous value stood, instead of writing NaN — which reaches the database as null
   and reads as "not prescribed". `MeasureField` and `RestField` below each carry their own
   version of that guard; if a numeric input is ever added to this file again, it needs one too. */

/** The one input a measure column owns. One switch, so a column can never be read by one code
 *  path and written by another — the phone's `value(for:)`/`set(_:for:)` pairing. */
function MeasureField({ column, set, bare, onChange }: {
  column: TrackingField; set: SetDraft; bare?: boolean; onChange: (patch: Partial<SetDraft>) => void;
}) {
  const label = COLUMN_LABEL[column];
  const common = "ht-input builder-cell";
  if (column === "time") {
    /* 🔴 W136 B2 S2b — A SET'S TIME IS TYPED BY THE RULE (`setTime`, up to 23:59:59 — his word 10-05). MEASURED before: this was a
       free-text `parseSeconds` with no cap, which read `1:05:00` as 65 SECONDS. Shown `m:ss` / `h:mm:ss` (`timeText`). */
    return (
      <Wrap bare={bare} label={label}>
        <TypedCell kind="setTime" value={set.timeSeconds} label={bare ? label : undefined} className={common}
                   onCommit={(n) => onChange({ timeSeconds: n })} />
      </Wrap>
    );
  }
  const unit = distanceUnitOf(column);
  if (unit !== null) {
    return (
      <Wrap bare={bare} label={label}>
        <DistanceCell unit={unit} metres={set.distanceM} label={bare ? label : undefined} className={common}
                      onCommit={(m) => onChange({ distanceM: m })} />
      </Wrap>
    );
  }
  const value = column === "reps" ? set.reps
    : column === "weight" ? set.weightKg
    : column === "rounds" ? set.rounds : null;
  /* ⚠️ `rounds` FOLLOWS `reps`, AND THAT IS A DECISION RATHER THAN AN OVERSIGHT. His rules name
     reps, kg and time; rounds is a whole-number count like reps and nothing about it wants a
     decimal, so it takes the same rule instead of keeping the old anything-goes handler beside
     three constrained ones. */
  const kind: CellKind = column === "weight" ? "weight" : "reps";
  return (
    <Wrap bare={bare} label={label}>
      <TypedCell kind={kind} value={value} label={bare ? label : undefined} className={common}
                 onCommit={(n) => {
                   if (column === "reps") onChange({ reps: n });
                   else if (column === "weight") onChange({ weightKg: n });
                   else if (column === "rounds") onChange({ rounds: n });
                 }} />
    </Wrap>
  );
}

/* 🔴 W46 — THE CELL HOLDS ITS OWN TEXT WHILE IT IS BEING TYPED. His rules are in
 * `lib/typedCell.ts`; this is the half that makes them reachable from a keyboard.
 *
 * MEASURED before it existed, driven one character at a time: **`2.5` typed into a KG cell ended
 * up as `25`**, and so did `2,5`. A coach prescribing two and a half kilos silently got
 * twenty-five. The cell was CONTROLLED straight from the model, so the instant `2.` was typed
 * `Number("2.")` was `2`, the model held `2` and the re-render wrote `"2"` back — the separator
 * could never survive the keystroke after it.
 *
 * ⚠️ THE PROP IS NOT ALLOWED TO OVERWRITE A FIELD SOMEBODY IS TYPING IN. That is the whole trick,
 * and re-syncing unconditionally would reproduce the original bug exactly: `2.` commits nothing,
 * the model still holds `2`, and an unguarded effect would push `"2"` back into the box and eat
 * the dot again. It syncs only while the field is NOT focused, which is what lets a half-typed
 * value exist at all.
 *
 * ⚠️ A REFUSED KEYSTROKE RESTORES THE CARET. Reverting the text alone sends it to the end, so
 * typing into the middle of `100` and being refused would move the cursor — his own rule is about
 * typing AT the cursor, and a fix that moved it would answer the limit and break the aim.
 */
function TypedCell({ kind, value, label, className, onCommit }: {
  kind: CellKind; value: number | null; label?: string; className: string;
  onCommit: (n: number | null) => void;
}) {
  const ref = useRef<HTMLInputElement>(null);
  // A set's time is SHOWN `m:ss` / `h:mm:ss` (W136 B2 S2b); every other kind as its bare number.
  const shown = (v: number | null) => (v === null ? "" : kind === "setTime" ? timeText(v) : String(v));
  const [text, setText] = useState(shown(value));
  useEffect(() => {
    if (document.activeElement === ref.current) return;
    setText(shown(value));
  }, [value]);
  return (
    <input ref={ref} className={className} inputMode="decimal" aria-label={label}
           value={text}
           onChange={(e) => {
             const next = e.target.value;
             if (accepts(kind, next)) { setText(next); onCommit(valueOf(kind, next)); return; }
             const at = e.target.selectionStart ?? next.length;
             const back = Math.max(0, at - (next.length - text.length));
             e.target.value = text;
             e.target.setSelectionRange(back, back);
           }}
           onBlur={() => setText(shown(value))} />
  );
}

/* 🔴 W136 B2 — A DISTANCE CELL, IN THE COACH'S UNIT (`lib/distanceCell.ts` holds the rules and the why). The model holds
 * METRES; the cell types and reads metres or kilometres by the COLUMN. TypedCell's shape: the text is the cell's own while
 * it is focused, a keystroke the rule refuses is reverted with the caret kept.
 * ⚠️ THE ONE COMMIT IS IN `onChange` (`distanceChange`); `onBlur` only redraws from the model. 21,097.5 m on a KM row shows
 * `21.1`, and a coach who clicks in and out must leave 21,097.5 stored — never 21,100. */
function DistanceCell({ unit, metres, label, className, onCommit }: {
  unit: DistanceUnit; metres: number | null; label?: string; className: string; onCommit: (m: number | null) => void;
}) {
  const ref = useRef<HTMLInputElement>(null);
  const [text, setText] = useState(distanceText(metres, unit));
  useEffect(() => {
    if (document.activeElement === ref.current) return;
    setText(distanceText(metres, unit));
  }, [metres, unit]);
  return (
    <input ref={ref} className={className} inputMode="decimal" aria-label={label}
           value={text} placeholder={unit === "km" ? "5" : "400"}
           onChange={(e) => {
             const next = e.target.value;
             const step = distanceChange(next, unit);
             if (step !== null) { setText(step.text); onCommit(step.commit); return; }
             const at = e.target.selectionStart ?? next.length;
             const back = Math.max(0, at - (next.length - text.length));
             e.target.value = text;
             e.target.setSelectionRange(back, back);
           }}
           onBlur={() => setText(distanceText(metres, unit))} />
  );
}

/** Rest accepts `90`, `1:30` and `1.30` — the phone's own parser, so a coach does not have to
 *  remember which form this field wanted. */
function RestField({ seconds, bare, onChange }: {
  seconds: number | null; bare?: boolean; onChange: (s: number | null) => void;
}) {
  const [text, setText] = useState(seconds === null ? "" : clock(seconds));
  useEffect(() => { setText(seconds === null ? "" : clock(seconds)); }, [seconds]);
  return (
    <Wrap bare={bare} label="Rest">
      <input className="ht-input builder-cell" aria-label={bare ? "Rest" : undefined}
             value={text} placeholder="1:30"
             onChange={(e) => setText(e.target.value)}
             onBlur={() => onChange(parseSeconds(text))} />
    </Wrap>
  );
}

function Wrap({ bare, label, children }: { bare?: boolean; label: string; children: React.ReactNode }) {
  if (bare === true) return <>{children}</>;
  return (
    <label className="builder-field">
      <span className="ht-caption">{label}</span>
      {children}
    </label>
  );
}

/* ── The window's rail: the library you are composing from ────────────────────────────────
 *
 * ⚠️ `BuilderRail` IS GONE, AND ITS REASON WENT WITH IT RATHER THAN BEING FORGOTTEN. It existed
 * because `app.css` applies `position: sticky` with a single `top` to `.workspace-list > *`, so
 * the builder's column 2 had to hand the shell exactly ONE element or the nav and the library
 * would stick to the same line and overlap. A window has no `.workspace-list`, so this screen is
 * no longer a client of that rule — the rule itself still binds every screen that IS one, which
 * is why `b4-9`'s ruler now asserts the CSS still exists AND that this screen's exemption is
 * STRUCTURAL rather than remembered.
 */


/**
 * The exercise library, in the window's rail, while the workout is beside it.
 *
 * 🔴 THE CHANGE THIS SESSION IS ABOUT. The picker used to be a disclosure INSIDE each section, so
 * the library was invisible while composing and a coach could not see what they owned until they
 * had already chosen where to put it. The owner's comparison was blunt — *"we got left our
 * exercises and sections … ours look infantile as it is now"* — and the shell's own contract says
 * column 2 is the list you are choosing from.
 *
 * ⚠️ WHICH SECTION A CLICK ADDS TO IS STATED, NEVER INFERRED. Everfit answers it by where you
 * drop; `EVERFIT-MAP.md` §12 measured their work surface at ZERO focusable elements, so a drop
 * target cannot be the only path here. The destination is printed above the search box on every
 * render and carries its ORDINAL, so two sections called `Main` are still distinguishable — and
 * it is changed by clicking a section, or by moving focus into one (§6.1 deleted the button).
 *
 * ⚠️ AND WHEN THERE IS NOWHERE TO PUT ONE, IT SAYS SO INSTEAD OF OFFERING A LIST OF DEAD BUTTONS.
 * A workout whose sections have all been removed can still be typed into; a picker that looked
 * live and silently dropped every click would be §29.8's flyout header in another costume.
 */
/** 🔴 W96 — A WORKOUT WITH NO PART is one drop target: a library row released here opens the implicit part that holds it
 *  (`addExerciseLoose`). It is the one container that is not a part. */
function EmptyDrop({ armed, children }: { armed: boolean; children: ReactNode }) {
  const { setNodeRef } = useDroppable({ id: "m:part:empty", data: { kind: "part", surface: "main", section: 0 } satisfies DropData });
  return <div ref={setNodeRef} className={`builder-empty-drop${armed ? " is-armed" : ""}`}>{children}</div>;
}

function LibraryColumn({ library, libraryLoaded, destination, canAdd, hasSections, lastAdded,
                        onPick, searchRef }: {
  library: LibraryExercise[];
  /* ⚠️ SEPARATE FROM THE ARRAY, BECAUSE THE ARRAY CANNOT SAY IT. `[]` is both "loaded and
     empty" and "not loaded yet", and the picker told a coach their library was EMPTY for as long
     as the read took. The rail takes the rows AND whether they have arrived. */
  libraryLoaded: boolean;
  destination: string;
  canAdd: boolean;
  hasSections: boolean;
  lastAdded: { name: string; place: string } | null;
  /** §6 rule (b): every add goes through this. `sectionIndex`/`position` null = the claimed
   *  section, at its end — which is what a click and Enter mean. A drop states both. */
  onPick: (id: string, sectionIndex?: number | null, position?: number | null) => void;
  /** The library id being dragged, so the sections can show their drop slots. */
  searchRef: React.RefObject<HTMLInputElement>;
}) {
  const [q, setQ] = useState("");
  // The rule lives in `builder.ts` with tests: every typed word must appear, in any order, so
  // `dips ring` finds `Ring Dips` and `pull up` finds `Pull-Ups`. A `.filter()` here would be the
  // untested `.map()` session 2 paid for, one verb over.
  /* §6.1 — the picker gets the library page's List · Grid switch. ⚠️ ITS OWN REMEMBERED KEY,
     deliberately: the library page and this rail are two places a coach makes two different
     choices (browsing a shelf vs composing a session beside it), and sharing one key would make
     a change in one silently move the other. Same fallback as the page's: a blocked or empty
     store reads as `list`. */
  /* ⚠️ W72 STEP 3 (Proposal 3: "no thumbnail grid"): the library is a LIST — thumbnail, name, `+` — beside the work,
     so the List · Grid switch and its remembered key are gone. A card grid is the shelf's way to browse; this pane is
     for adding, and a row carries the picture at the size a coach recognises it by. */
  /* 🔴 W32(3) — THE FILTER BEHIND THE ICON IN THE SEARCH FIELD. His word via Fable: *"under the
     search of exercises with the filter icon button"*, and theirs opens equipment/category.
     ⚠️ NOT REMEMBERED ACROSS VISITS, the same call the tab above took: a rail that opens already
     filtered to `Barbell` because of something the coach did last week reads as a library that
     has lost rows. The VIEW switch below is remembered because it is a preference about how one
     list is drawn; this is a question about which rows, and the answer expires with the visit. */
  const [facets, setFacets] = useState<{ equipment: string | null; category: string | null }>(
    { equipment: null, category: null },
  );
  const [filterOpen, setFilterOpen] = useState(false);
  const filterWrapRef = useRef<HTMLDivElement | null>(null);
  /* 🔴 THE TWO DISMISSALS THE COMMENT BELOW PROMISES, BUILT RATHER THAN DESCRIBED. A claim that
     lives only in a comment lapses with nothing turning red — this repo's docs-and-handoffs
     lesson — so Escape and click-outside are here, and the ruler asserts this effect exists.
     ⚠️ `mousedown`, NOT `click`: a chip inside the panel re-renders on press, and by the time a
     `click` bubbles the target can already be detached from the DOM, so `contains()` says false
     and the panel closes under the coach's own finger. */
  useEffect(() => {
    if (!filterOpen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setFilterOpen(false); };
    const onDown = (e: MouseEvent) => {
      const wrap = filterWrapRef.current;
      if (wrap !== null && !wrap.contains(e.target as Node)) setFilterOpen(false);
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onDown);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onDown);
    };
  }, [filterOpen]);
  const facetsOn = (facets.equipment !== null ? 1 : 0) + (facets.category !== null ? 1 : 0);

  /* ⚠️ THE EQUIPMENT CHIPS ARE DERIVED FROM THIS COACH'S OWN LIBRARY, never from the canonical
     vocabulary — the exercises page does the same, and for the same reason: offering `Sled` to a
     coach who owns no sled is a chip that can only ever return nothing. CATEGORY is the opposite
     case and is offered whole (`CATEGORY_ORDER`), because its five words are a closed set the
     coach is choosing WITHIN rather than a description of what they happen to own. */
  const availableEquipment = useMemo(
    () => [...new Set(library.flatMap((e) => e.equipment ?? []))].sort((a, b) => a.localeCompare(b)),
    [library],
  );

  /* 🔴 W72 STEP 5 — THE PANE AND THE LIBRARY PAGE ASK ONE SEARCH (`shelfMatches`), word-AND as the rail's old
     `filterLibrary` was (`dips ring` still finds `Ring Dips`, which the placeholder promises), now over the name WITH
     its long forms (the engine's generated abbreviations), the muscles and the equipment — a superset of what it
     matched, so nothing that was found stops being found. The chips still share `matchesFacets`. A HIDDEN row is
     skipped here and only here: the `library` it filters also resolves the rows already in the workout. */
  const [paneView, setPaneView] = useShelfView("builderPane");
  const shown = useMemo(
    () => library.filter((e) => e.hiddenAt === null && shelfMatches(e, q) && matchesFacets(e, facets)),
    [library, q, facets],
  );

  return (
    <section className="builder-library" id="builder-library" aria-labelledby="lib-h">
      {/* 🔴 W88 — ONE LIST, SO NO TABS AND NO HEADING OVER IT (his yes, 2026-09-28): the pane opens on where the next exercise
          lands (`Adding to: Warm-up`). The heading stays for a screen reader — it names this region. */}
      <h2 className="ht-visually-hidden" id="lib-h">Exercises</h2>

      {/* ⚠️ TWO DIFFERENT NULLS, AND THEY NEED DIFFERENT SENTENCES. `addTargetIndex` returns
          `null` both when there is no section at all and when there are several and the coach has
          not said which — one is "make a section", the other is "point at one". Collapsing them
          into a single phrase would leave a coach with eleven sections reading an instruction that
          does not apply to them.
          Not `aria-live`: it changes only in response to a button the coach just pressed. */}
      <p className="ht-caption builder-library-target">
        {/* ⚠️ A THIRD STATE SINCE W32(a): a workout with NO section at all. It is not "Add a
            section first" any more — an exercise added now lands loose and makes its own home —
            and it is not "Adding to Section 1", because there is no Section 1 to name. Saying
            either would be a sentence about a workout the coach is not looking at. */}
        {!hasSections
          ? <>Adding as the <strong>first exercise</strong></>
          : canAdd
          ? <>Adding to: <strong>{destination}</strong></>
          /* 🔴 THE THIRD ARM IS GONE, AND IT WAS BOTH DEAD AND FALSE. It read "Add a section
             first" for a workout with no sections — a branch `!hasSections` above now catches,
             so it was unreachable, AND an instruction that stopped being true when W32(a) made an
             empty workout accept a dragged exercise directly. Its own comment had already said it
             was "nearly unreachable… kept honest rather than deleted"; the honest thing once it
             is both unreachable and wrong is to delete it. What is left is the one real case: the
             coach has several sections and has not said which. */
          : "Click a section to add there"}
      </p>

      {/* ⚠️ `role="status"` RATHER THAN A TOAST. The section it landed in is often scrolled out of
          view, so this is the only evidence the click did anything — and it is announced to a
          screen reader for the same reason it is printed for everyone else. */}
      {/* JOB 3 STEP 5 PAGE 3 — the place is DRAWN only when it differs from the `Adding to:` line just above (MEASURED on
          the first exercise: "Adding to: the 1st section" over "Added Dip → the 1st section", one neighbouring line said
          twice); a screen reader still hears the whole sentence, since only this line is announced. */}
      {lastAdded !== null && (
        <p className="ht-caption builder-library-added" role="status">
          Added {lastAdded.name}
          {hasSections && canAdd && lastAdded.place === destination
            ? <span className="ht-visually-hidden"> → {lastAdded.place}</span>
            : <> → {lastAdded.place}</>}
        </p>
      )}
      {/* 🔴 W32(3), HIS EYE ON THEIR SCREEN: BOTH AFFORDANCES SIT **INSIDE** THE FIELD — a
          magnifier at the left and the filter button at the right. The visible label goes with
          them (theirs has none); it stays as the input's accessible name, because a field whose
          only label is an icon is a field a screen reader announces as nothing.
          ⚠️ THE MAGNIFIER IS `aria-hidden` AND THE BUTTON IS NOT. One is decoration on a control
          that already says what it is; the other is a control, so it carries a real name and the
          `aria-expanded` that says the popover is open. */}
      <div className="builder-search" ref={filterWrapRef}>
        <label className="ht-visually-hidden" htmlFor="pick">Search your library</label>
        <Icon name="search" className="builder-search-glass" size={16} />
        {/* JOB 3 · WILLIAM (10-15): the hint was `dips ring` — the matcher's party trick (word order free), read at rest as a
            query somebody had typed. It says what the field is for; the word-AND search behind it is unchanged. */}
        <input id="pick" ref={searchRef} className="ht-input builder-search-input" value={q}
               placeholder="Search exercises" onChange={(e) => setQ(e.target.value)} />
        <button type="button"
                className={`builder-search-filter${facetsOn > 0 ? " is-on" : ""}`}
                aria-expanded={filterOpen} aria-controls="builder-filter-pop"
                /* ⚠️ THE COUNT IS IN THE NAME, NOT ONLY IN THE COLOUR. A tinted icon is the
                   whole signal that rows are being withheld, and it is invisible to a screen
                   reader and to anyone who does not see the tint — which is how a coach ends up
                   certain their library has lost exercises. */
                aria-label={facetsOn === 0 ? "Filter" : `Filter (${facetsOn} on)`}
                onClick={() => setFilterOpen((v) => !v)}>
          <Icon name="filter" size={16} />
        </button>

        {filterOpen && (
          /* ⚠️ A POPOVER, SO IT CLOSES ON Escape AND ON A CLICK OUTSIDE — a panel that can only
             be dismissed by finding the same small button again is the thing a coach fights. */
          /* 🔴 A BARE BLOCK COMMENT HERE, NEVER THE BRACED JSX FORM — THIS IS A JS EXPRESSION
             POSITION, NOT JSX. `{filterOpen && (` opens an expression, so a braced comment there is
             read as an empty OBJECT LITERAL and the `<div>` after it is a syntax error; the whole
             function was invalid from here down. `tsc` said so in nine lines and NOTHING ELSE
             COULD: every test that names this file imports it with `?raw` and reads it as TEXT, and
             so do the three Swift gates — 2,575 web tests and 210 gate assertions stood green over a
             route that did not compile. The comment three lines above already had the right form;
             copying THAT was the entire fix. (2026-09-20; the class ruler is `routesParse.test.ts`.)
             ⚠️ `role="group"`, NOT `role="dialog"` — I wrote `dialog` and the builder's own window
              ruler caught it, correctly and for a reason better than the one it was watching for.
              That ruler forbids a hand-rolled `role="dialog"` because the BUILDER must be a native
              `<dialog>` rather than an overlay imitating one; this popover is not that window, but
              the role was wrong on its own terms: a dialog promises modality — a focus trap, an
              inert page behind, Escape handled as a cancelable event — and this panel has none of
              it and should not. It closes on an outside click, which is the opposite of modal.
              A labelled GROUP is what it is: some controls, named, that the button above it
              expands. */
          <div className="builder-filter-pop" id="builder-filter-pop" role="group"
               aria-label="Filter your library">
            {/* 🔴 CATEGORY FIRST, EQUIPMENT SECOND — the order the exercises page's own panel
                already draws them in, and RENDERING IT THE OTHER WAY IS WHAT SHOWED WHY. This
                coach's library holds 31 equipment values, so with Equipment on top the Category
                group sat below the panel's fold: a coach opening the filter saw equipment and
                would reasonably conclude there is no category filter at all. Three short chips
                first, the long list under them. Invisible in source, obvious in the still. */}
            <ChipGroup heading="Category"
                       options={CATEGORY_ORDER.map((c) => ({ value: c, label: CATEGORY_LABEL[c] }))}
                       current={facets.category}
                       onPick={(v) => setFacets((f) => ({ ...f, category: v }))} />
            {availableEquipment.length > 1 && (
              <ChipGroup heading="Equipment"
                         options={availableEquipment.map((k) => ({ value: k, label: vocabularyLabel(k) }))}
                         current={facets.equipment}
                         onPick={(v) => setFacets((f) => ({ ...f, equipment: v }))} />
            )}
            {/* ⚠️ ONLY WHEN SOMETHING IS ON. A permanently visible `Clear` on an untouched panel
                is a button that does nothing, and a coach learns to distrust it. */}
            {facetsOn > 0 && (
              <button type="button" className="ht-button ht-button-quiet builder-filter-clear"
                      onClick={() => setFacets({ equipment: null, category: null })}>
                Clear
              </button>
            )}
          </div>
        )}
      </div>

      {/* 🔴 W32(4): THEIR `MOST RECENT (N)` LINE, WITH THE VIEW TOGGLE AT ITS RIGHT.
          ⚠️ N COUNTS WHAT THE LIST ACTUALLY HOLDS, not the library. With a query or a chip on,
          a heading reading the full 142 over a list of six is a screen contradicting itself one
          line later. ⚠️ AND THIS SENTENCE USED TO END *"the `Showing 40 of N` note at the foot
          already says where the CAP bit"* — HIS WORD DELETED THAT NOTE IN `383fff66` AND THE
          COMMENT KEPT CITING IT (corrected 2026-09-20). `LIBRARY_PAGE` still caps the list, so N
          over a shorter list is now the ONLY place the cut is visible at all, and it says it by
          arithmetic rather than in words — filed as the sheet's row 22, his to keep or undo.
          Real radios still, as the text switch they replace used: the browser gives arrow-key
          movement and "1 of 2" for nothing, and an icon-only control needs a real name more than
          a labelled one did, not less. */}
      <div className="builder-picker-bar">
        <p className="ht-overline builder-picker-count">Most recent ({shown.length})</p>
        {/* W72 step 5 — his List · Grid switch, on this line at its right. The pane remembers its own view. */}
        <ShelfViewSwitch view={paneView} onChange={setPaneView} name="builder-pane-view" />
      </div>

      {/* ⚠️ THE SAME COLLISION ONE LEVEL UP, AND IT WAS LIVE: with `library` at `[]` before the
          fetch resolved, this told the coach their library was EMPTY for as long as the read took
          — about a row count they own. It waits for the answer now. */}
      {libraryLoaded && library.length === 0 && <p className="ht-caption">Your library is empty.</p>}

      {/* ⚠️ THE SENTENCE NAMES WHATEVER IS ACTUALLY WITHHOLDING THE ROWS. It used to name the
          QUERY alone, so a coach who had pressed a chip and typed nothing read `Nothing matches
          “”` — a screen blaming an empty search box for a filter. */}
      {library.length > 0 && shown.length === 0 && (
        <p className="ht-caption">
          {q.trim() !== "" && facetsOn > 0 ? <>Nothing matches “{q}” with that filter.</>
            : q.trim() !== "" ? <>Nothing matches “{q}”.</>
              : "Nothing matches that filter."}
        </p>
      )}

      {shown.length > 0 && (
        /* 🔴 W72 STEP 5 — THE SHARED SHELF (`components/ExerciseShelf.tsx`), the SAME component the Library's
           Exercises page draws, so the pane and the page cannot drift. List = the picker row (picture, name, the
           grip on its right edge); grid = two square containers per row, the name under the picture, the grip on
           the name line's right edge (his 2026-09-25 ~07:05 note). Drag and click are identical in both views. */
        <ExerciseShelf variant="pane" view={paneView} items={shown.slice(0, LIBRARY_PAGE)}
                       canAdd={canAdd} onPick={onPick} />
      )}

      {/* ⚠️ A TRUNCATION NOBODY IS TOLD ABOUT IS THE SHAPE THIS REPO KEEPS PAYING FOR. The true
          total is named beside the cap, so a coach who cannot find something knows whether to
          keep typing or to stop looking. */}
      {/* 🔴 HIS WORD: *"no need for this text on the bottom of the list view of 'showing 40 of
          142 and so on' so remove that completely."* It said `Showing 40 of {n}. Keep typing.`
          ⚠️ THE CAP ITSELF STAYS — `LIBRARY_PAGE` still bounds the list, because his library is
          142 rows and the catalogue behind it 472, and an uncapped list is a scroll rather than a
          choice. What goes is the SENTENCE. That is a real trade and he has made it: this repo's
          own rule is that a truncation nobody is told about is the shape it has paid for, and the
          search field one thumb-length above is the answer to "where are the rest" for a coach who
          wonders. Removing the line does not remove the cap, and the cap is not a secret — it is
          the same forty rows their own picker shows. */}
    </section>
  );
}


/** A freshly picked exercise, at the dose the catalogue says a real row starts from. */
function seededRow(lib: LibraryExercise): ExerciseDraft {
  return {
    libraryExerciseId: lib.id, name: lib.name, trackingFields: lib.trackingFields,
    /* 🔴 THE LIBRARY'S NOTE SEEDS THE ROW — W15. It can never overwrite a coach's typed note,
       and the reason is structural rather than a guard: this function builds a row that does not
       exist yet, so there is nothing to overwrite. An EXISTING row loads its own `notes` from the
       prescription (`note: e.notes` in `toDraft`), which is a different column on a different
       table and is never touched by this.
       ⚠️ A LATER EDIT TO THE LIBRARY NOTE DOES NOT REACH WORKOUTS ALREADY BUILT, and that is
       copy-on-assign's accepted cost in his own sentence — the same rule that stops a template
       typo reaching assigned days. It is a SEED, not a link. */
    eachSide: false, holdUntilLost: false, tempo: null, note: lib.note, supersetWithNext: false,
    // Three sets of nothing, with the rest field the only one carrying a value: rest is on 601 of
    // 699 real sets, the most-filled field in the whole vocabulary.
    sets: expandUniform({ ...emptySet(), restSeconds: 90 }, 3),
  };
}

/* ── Reading an existing workout back into a draft ────────────────────────────────────────── */

/**
 * ⚠️ THE ROWS ARE SORTED BY `position` HERE, BECAUSE THE QUERY DOES NOT.
 * A PostgREST nested embed comes back in no guaranteed order, and session 3 already paid for
 * this: two mutations survived there because every fixture happened to arrive already ordered.
 * An unsorted read would render a coach's workout with its sections shuffled — and then SAVE
 * that order back, turning a display bug into a data change.
 */
function toDraft(row: WorkoutQueryRow): WorkoutDraft {
  const sections = [...(row.coach_workout_sections ?? [])].sort((a, b) => a.position - b.position);
  return {
    id: row.id,
    name: row.name,
    description: row.description,
    sections: sections.map((s) => ({
      name: s.name,
      format: s.format as SectionFormat,
      sectionType: s.section_type as SectionType,
      // ⚠️ `?? false` for a row written before the column existed — it cannot be null in the
      // database, but a tolerant read costs nothing and an older cached shape would otherwise
      // produce `undefined` on a field the compiler was told is a boolean.
      isImplicit: (s as { is_implicit?: boolean | null }).is_implicit ?? false,
      description: s.description,
      // Every spelling a stored row may carry, resolved to the canonical keys on the way in.
      formatConfig: readFormatConfig(s.format as SectionFormat, s.format_config),
      exercises: [...(s.coach_workout_exercises ?? [])]
        .sort((a, b) => a.position - b.position)
        .map((e) => ({
          libraryExerciseId: e.exercise_id,
          // ⚠️ A POSTGREST EMBED COMES BACK AS AN OBJECT **OR** AN ARRAY depending on the
          // relationship it inferred, and `.name` on an array is `undefined` — which would render
          // every row in the builder as "Exercise" with nothing red. Session 2 hit exactly this
          // and the rule it left is to normalise the shape rather than trust the inference.
          name: embedded(e.coach_exercises)?.name ?? "Exercise",
          trackingFields: (embedded(e.coach_exercises)?.tracking_fields ?? []) as TrackingField[],
          eachSide: e.each_side,
          holdUntilLost: e.hold_until_lost === true,
          tempo: e.tempo,
          note: e.notes,
          supersetWithNext: e.superset_with_next,
          // §6's round trip, one half. Inline here it could never be executed by a test; it is
          // `setsFromRows` now, and the test drives the same function the screen does.
          sets: setsFromRows((e.coach_workout_sets ?? []).map((st) => ({
            position: st.position, set_type: st.set_type,
            /* `numeric` over the wire — see `builder.ts`'s note at the same seam. */
            reps: st.reps, weight_kg: wireNumber(st.weight_kg), time_seconds: st.time_seconds,
            rounds: st.rounds, rest_seconds: st.rest_seconds,
            /* W136 B2 — READ, so a save writes back what was loaded: before this the select above did not ask for it,
               every load read null, and the next save (`rowsFromSets` writes `distance_m: s.distanceM`) wiped it. */
            distance_m: wireNumber(st.distance_m),
          }))),
        })),
    })),
  };
}

type LibraryEmbed = { id: string; name: string; tracking_fields: string[] | null };

/** One embedded row, whichever shape PostgREST chose. */
function embedded(v: LibraryEmbed | LibraryEmbed[] | null | undefined): LibraryEmbed | null {
  if (v === null || v === undefined) return null;
  return Array.isArray(v) ? (v[0] ?? null) : v;
}

type WorkoutQueryRow = {
  id: string; name: string; description: string | null;
  coach_workout_sections?: {
    id: string; name: string | null; position: number; format: string;
    section_type: string; description: string | null; format_config?: unknown;
    coach_workout_exercises?: {
      id: string; exercise_id: string; position: number; each_side: boolean; hold_until_lost?: boolean;
      tempo: string | null; notes: string | null; superset_with_next: boolean;
      coach_exercises?: LibraryEmbed | LibraryEmbed[] | null;
      coach_workout_sets?: {
        id: string; position: number; reps: number | null; weight_kg: number | null;
        time_seconds: number | null; rounds: number | null; rest_seconds: number | null;
        set_type: string;
        /** `numeric` — a number or a string on the wire (W136 B2: read so a save cannot wipe it). */
        distance_m?: number | string | null;
      }[] | null;
    }[] | null;
  }[] | null;
};

/* ── The write ────────────────────────────────────────────────────────────────────────────── */

/**
 * Execute the plan, one step at a time.
 *
 * ⚠️ SEQUENTIAL, AND RLS IS WHY — measured, not assumed. The policies on all three child tables
 * are `EXISTS (… JOIN coach_workouts w … w.coach_id = auth.uid())`, and a row inserted earlier in
 * the SAME statement is not visible to that subquery: a chained-CTE insert of the whole tree was
 * refused **42501 on `coach_workout_sections`**, while the identical inserts as separate
 * statements were accepted. `Promise.all` over the tree is the obvious tidy-up and is wrong.
 *
 * ⚠️ EVERY ERROR REACHES THE COACH UNTOUCHED. `assign.ts` states the reason and it is this repo's
 * own: `delete-account`'s tolerant handler swallowed three column typos and returned `{ok:true}`
 * for work it never did. A message we did not write is the only evidence of a cause we did not
 * anticipate — so there is no friendly blanket sentence here, and the step that failed is named.
 *
 * ⚠️ IT IS NOT ATOMIC, AND SAYING SO IS PART OF SHIPPING IT. PostgREST has no transaction across
 * requests, so a failure midway leaves a workout whose children are partly written. The workout
 * ROW survives either way — which is what makes the state recoverable by pressing Save again,
 * since a re-save deletes the sections and rewrites them from the draft still on screen.
 */
async function executeSave(
  draft: WorkoutDraft, client?: ClientTarget,
): Promise<{ workoutId: string | null; assignmentId: string | null; error: string | null }> {
  const steps = planSave(draft);

  // ⚠️ `coach_id` HAS NO DEFAULT AND THE GENERATED TYPES REFUSED TO LET IT BE OMITTED — §3a's
  // mechanism working, and it caught a real one. `coach_workouts_owner_all`'s WITH CHECK is
  // `coach_id = auth.uid()`, so a row that named somebody else would be refused by RLS anyway;
  // what the type prevented is the quieter version, an insert that names NOBODY and fails at the
  // database with a not-null violation the coach cannot act on. It is read from the session
  // rather than passed in, so no caller can supply a different one.
  const { data: auth } = await supabase.auth.getUser();
  const coachId = auth.user?.id ?? null;
  // (Over a client's week the RPC takes the coach from the session ON THE SERVER — `auth.uid()` — so no id is read here.)
  if (coachId === null && draft.id === null && client === undefined) {
    return { workoutId: null, assignmentId: null, error: "You are signed out. Sign in again and your work is still on screen." };
  }
  let workoutId = draft.id;
  let assignmentId: string | null = null;
  const sectionIds = new Map<number, string>();
  const exerciseIds = new Map<string, string>();

  for (const step of steps) {
    if (step.kind === "insertWorkout" && client !== undefined) {
      /* 🔴 WILLIAM'S ITEM 4 — OVER A CLIENT'S WEEK THE ROW IS THEIR COPY AND ITS DAY, ONE TRANSACTION
         (`create_client_workout`, SECURITY INVOKER: the coach's own two policies decide; rehearsed per identity 10-15).
         Two inserts from here could leave a copy with no day — invisible, since the library lists `client_id is null`. */
      if (client.day === null) return { workoutId: null, assignmentId: null, error: "Which day this is for is not known yet." };
      const { data, error } = await supabase.rpc("create_client_workout", {
        p_client_id: client.clientId, p_scheduled_date: client.day, p_name: step.name,
        // The generated signature says `string`; every Postgres argument is nullable and NULL is "no description"
        // (`duplicateDeps`' note on the same generator artefact).
        p_description: step.description as string,
      });
      const made = data?.[0];
      if (error || made === undefined) {
        return { workoutId: null, assignmentId: null, error: `Could not create the workout: ${error?.message ?? "nothing came back."}` };
      }
      workoutId = made.new_workout_id;
      assignmentId = made.new_assignment_id;
    } else if (step.kind === "insertWorkout") {
      const { data, error } = await supabase
        .from("coach_workouts")
        .insert({ coach_id: coachId as string, name: step.name, description: step.description })
        .select("id").single();
      if (error) return { workoutId: null, assignmentId: null, error: `Could not create the workout: ${error.message}` };
      workoutId = data.id;
    } else if (step.kind === "updateWorkout") {
      const { error } = await supabase.from("coach_workouts")
        .update({ name: step.name, description: step.description })
        .eq("id", step.workoutId);
      if (error) return { workoutId, assignmentId, error: `Could not rename the workout: ${error.message}` };
    } else if (step.kind === "deleteSections") {
      // 🔴 THE CHILDREN ONLY. `coach_assignments.workout_id → coach_workouts` is ON DELETE
      // CASCADE, so deleting the WORKOUT to rebuild it would destroy every assignment of it,
      // including days already trained. The three child tables cascade among themselves, so one
      // delete of the sections removes the exercises and sets with them — and nothing outside
      // the tree references any of the three, measured over `pg_constraint`.
      const { error } = await supabase.from("coach_workout_sections")
        .delete().eq("workout_id", step.workoutId);
      if (error) return { workoutId, assignmentId, error: `Could not replace the sections: ${error.message}` };
    } else if (step.kind === "insertSection") {
      if (workoutId === null) return { workoutId, assignmentId, error: "The workout was not created." };
      const { data, error } = await supabase.from("coach_workout_sections").insert({
        workout_id: workoutId, name: step.section.name, position: step.position,
        format: step.section.format, section_type: step.section.sectionType,
        description: step.section.description,
        // §6.5 — whether the app made this section or a coach did. Written explicitly rather than
        // left to the column's default: a default is what a row gets when nobody decided, and
        // here somebody always has.
        is_implicit: step.section.isImplicit,
        // The CANONICAL keys — the spelling the engine writes and the phone's player reads.
        format_config: Object.keys(step.section.formatConfig).length > 0 ? step.section.formatConfig : null,
      }).select("id").single();
      if (error) return { workoutId, assignmentId, error: `Could not save the ${ordinalWord(step.position + 1)} section: ${error.message}` };
      sectionIds.set(step.position, data.id);
    } else if (step.kind === "insertExercise") {
      const sectionId = sectionIds.get(step.sectionPosition);
      if (sectionId === undefined) return { workoutId, assignmentId, error: "A section went missing mid-save." };
      const { data, error } = await supabase.from("coach_workout_exercises").insert({
        section_id: sectionId, exercise_id: step.exercise.libraryExerciseId,
        position: step.position, each_side: step.exercise.eachSide,
        hold_until_lost: step.exercise.holdUntilLost === true,
        tempo: step.exercise.tempo, notes: step.exercise.note,
        superset_with_next: step.exercise.supersetWithNext,
      }).select("id").single();
      if (error) return { workoutId, assignmentId, error: `Could not save ${step.exercise.name}: ${error.message}` };
      exerciseIds.set(`${step.sectionPosition}:${step.position}`, data.id);
    } else {
      const exerciseId = exerciseIds.get(`${step.sectionPosition}:${step.exercisePosition}`);
      if (exerciseId === undefined) return { workoutId, assignmentId, error: "An exercise went missing mid-save." };
      if (step.sets.length === 0) continue;
      // ⚠️ THE LEGACY PER-EXERCISE COLUMNS ARE DELIBERATELY NOT WRITTEN. `sets`, `reps_min`,
      // `reps_max`, `hold_seconds_*` and `rest_seconds` on `coach_workout_exercises` are read by
      // NO select in the app (V6-8), and measured live they are populated on ZERO of his 323
      // rows. Writing them would produce a workout that looks right in the database and renders
      // EMPTY in the builder, which is the trap V6-8 recorded in exactly these words.
      const { error } = await supabase.from("coach_workout_sets").insert(
        // The other half of §6's round trip. `rowsFromSets` sets `position` from the INDEX,
        // which is what makes rows → drafts → rows stable.
        rowsFromSets(step.sets).map((r) => ({ ...r, workout_exercise_id: exerciseId })));
      if (error) return { workoutId, assignmentId, error: `Could not save the sets: ${error.message}` };
    }
  }
  return { workoutId, assignmentId, error: null };
}
